import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Lock, Package, Truck, CheckCircle, XCircle, Clock, LogOut, Trash2, Eye, CreditCard, Banknote, UserCheck, BarChart3, Receipt, DollarSign, Printer, Send, MapPin, FileText, Loader2, MousePointerClick, Pencil, ChevronLeft, ChevronRight, AlertCircle, FileSpreadsheet, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminSidebar from '../components/AdminSidebar';
import { AiKeyCard } from '../components/AiKeyCard';
import AdminProducts from '../components/AdminProducts';
import AdminCustomers from '../components/AdminCustomers';
import AdminShipping from '../components/AdminShipping';
import AdminWhatsApp from '../components/AdminWhatsApp';
import AdminSettings from '../components/AdminSettings';
import AdminCoupons from '../components/AdminCoupons';
import AdminBanner from '../components/AdminBanner';
import AdminBackup from '../components/AdminBackup';
import AdminWaSender from '../components/AdminWaSender';
import WaBotApp from '../components/wabot/WaBotApp';
import AdminAssistant from '../components/AdminAssistant';
import SiteEditorChat from '../components/SiteEditorChat';
import FbTokenConfig from '../components/FbTokenConfig';
import EhouseConfig from '../components/EhouseConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = searchParams.get('section') || 'orders';
  const setActiveSection = (s) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('section', s);
      return next;
    }, { replace: false });
  };
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeFilter, setActiveFilter] = useState('all');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [shippingLoading, setShippingLoading] = useState(null);
  // UPS state removed May 2026 — kept only as a tombstone constant so any
  // residual reference (e.g. legacy order docs with `tracking_number`) won't
  // crash. We never write to this map anymore.
  const upsStatuses = {};
  const [hfdStatuses, setHfdStatuses] = useState({});
  // Pagination — orders list is heavy (HFD/UPS lookups, tons of buttons per
  // row). Render only the first N then offer "טען עוד" so the page is
  // always responsive after team actions.
  const [orderLimit, setOrderLimit] = useState(6);
  // Collapse state for expense card sub-rows (shipping / products
  // breakdown). Default closed — admin expands when they want detail.
  const [shipBreakdownOpen, setShipBreakdownOpen] = useState(false);
  const [productBreakdownOpen, setProductBreakdownOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; });
  const [dateTo, setDateTo] = useState(() => {
    const d = new Date();
    const last = new Date(d.getFullYear(), d.getMonth()+1, 0);
    return `${last.getFullYear()}-${String(last.getMonth()+1).padStart(2,'0')}-${String(last.getDate()).padStart(2,'0')}`;
  });
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const [exportPaymentMethod, setExportPaymentMethod] = useState('all');
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [editingField, setEditingField] = useState(null); // { orderId, field, value }
  const [clickStats, setClickStats] = useState({ total: 0, today: 0, last_7_days: [] });
  // 5 trackable share-links the admin can launch from the brown card.
  // Backend exposes /api/go/{1..5} which records the visit, then redirects
  // to the slot's target_url. Editing the URL clears the slot's history.
  const [clickSlots, setClickSlots] = useState([]); // [{id,target_url,active,stats}]
  const [activeSlotId, setActiveSlotId] = useState(1);
  const [slotEdits, setSlotEdits] = useState({}); // {id: editedUrlString}
  const [slotNameEdits, setSlotNameEdits] = useState({}); // {id: editedNameString}
  const [providerFilter, setProviderFilter] = useState('all');
  const [senderFilter, setSenderFilter] = useState('all');
  const [showCompleted, setShowCompleted] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  // CardCom reconciliation popup state — holds the full diff report from
  // POST /admin/orders/verify-cardcom-batch so we can show two tables:
  // dashboard orders missing from CardCom and CardCom tx missing from
  // dashboard (e.g. איציק כהן 555 ₪).
  const [cardcomReport, setCardcomReport] = useState(null);
  const [cardcomLoading, setCardcomLoading] = useState(false);
  const [fbStats, setFbStats] = useState(null);
  // Team stock overrides set by Bari from the TeamApp (May 2026). Each
  // entry: {courier_name, base_stock, base_set_at}. When present for a
  // courier we display `base_stock` directly on the dashboard card so
  // the value matches what the courier sees in his app — fixed until
  // Bari updates it again.
  const [stockOverrides, setStockOverrides] = useState([]);
  // Collapsible panels — all closed by default per user request.
  const [showClickTracking, setShowClickTracking] = useState(false);
  const refreshClickSlots = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/click-slots`);
      setClickSlots(r.data || []);
    } catch (e) { /* silent */ }
  }, []);
  // Live stats: while the tracking card is open, refresh counters every 10s
  // (they were only loaded once on page load — clicks never showed up).
  useEffect(() => {
    if (!showClickTracking) return;
    refreshClickSlots();
    const iv = setInterval(refreshClickSlots, 10000);
    return () => clearInterval(iv);
  }, [showClickTracking, refreshClickSlots]);
  const [showFbStats, setShowFbStats] = useState(false);
  const [showDailyCalendar, setShowDailyCalendar] = useState(false);
  const [fbCampaigns, setFbCampaigns] = useState([]);
  const [editingBudget, setEditingBudget] = useState(null); // { id, value, level }
  const [campaignLoading, setCampaignLoading] = useState(null);
  // WhatsApp templates pulled from /admin/wa-templates so edits made in
  // the "תבניות הודעה" panel actually flow through to every WhatsApp
  // message we open. Without this hook the UI saved templates to the DB
  // but the click-to-WhatsApp links kept using hard-coded text.
  // Re-fetched every 30 s so admin edits surface without a page reload.
  const [waTemplates, setWaTemplates] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/wa-templates`);
        if (!cancelled) setWaTemplates(Array.isArray(r.data) ? r.data : []);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  // Helper: render a template by id, substituting {placeholders}. Falls
  // back to a sane default if the template id isn't in the DB yet.
  const renderWaTemplate = (id, vars, fallback = '') => {
    const tpl = waTemplates.find(t => t.id === id);
    let text = tpl?.message || fallback;
    Object.entries(vars || {}).forEach(([k, v]) => {
      text = text.split(`{${k}}`).join(v ?? '');
    });
    return text;
  };
  // Cash-reports revenue total — sums `total_collected` across every cash
  // report uploaded by the admin (shipping-company cash remittances).
  // Polled every 60s so updates from the "מסמכים" panel surface here too.
  const [cashReportRevenue, setCashReportRevenue] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/cash-reports`);
        if (cancelled) return;
        const arr = Array.isArray(r.data) ? r.data : (r.data?.reports || []);
        setCashReportRevenue(arr.reduce((s, h) => s + Number(h.total_collected || 0), 0));
      } catch { /* keep last value */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  // FB Budget scheduling
  const [budgetSchedules, setBudgetSchedules] = useState([]); // active pending schedules
  const [scheduleModal, setScheduleModal] = useState(null); // { targetId, level, name, currentBudget }
  const [schedDate, setSchedDate] = useState('');
  const [schedTime, setSchedTime] = useState('');
  const [schedAmount, setSchedAmount] = useState('');
  // Financial date filter - defaults to today
  const todayStr = new Date().toISOString().split('T')[0];
  const [finDateFrom, setFinDateFrom] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; });
  const [finDateTo, setFinDateTo] = useState(todayStr);

  const exportCustomersToExcel = async () => {
    if (exportDateFrom && exportDateTo && exportDateFrom > exportDateTo) {
      toast.error('תאריך ההתחלה מאוחר מתאריך הסיום');
      return;
    }
    setExportLoading(true);
    try {
      const params = {};
      if (exportDateFrom) params.date_from = exportDateFrom;
      if (exportDateTo) params.date_to = exportDateTo;
      if (exportPaymentMethod !== 'all') params.payment_method = exportPaymentMethod;
      const response = await axios.get(`${API}/admin/customers/export`, {
        params,
        responseType: 'blob',
      });
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || 'customers.csv';
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      const count = response.headers['x-exported-customers'];
      toast.success(count ? `הקובץ ירד בהצלחה — ${count} לקוחות` : 'הקובץ ירד בהצלחה');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'הייצוא נכשל, נסה שוב');
    } finally {
      setExportLoading(false);
    }
  };

  const saveField = async (orderId, field, value) => {
    // Robust inline-edit save:
    // - functional setState to avoid stale closure on rapid typing
    // - explicit success toast so the user always sees "saved"
    // - retry once on transient network failure (user complained the edit
    //   silently fails sometimes after waking the computer up)
    // - optimistic local update keeps the UI responsive even on slow WiFi
    const tryOnce = () =>
      axios.patch(`${API}/admin/orders/${orderId}/field`, { [field]: value });
    try {
      try {
        await tryOnce();
      } catch (firstErr) {
        // Single transparent retry for transient network/CORS hiccups
        await new Promise(r => setTimeout(r, 400));
        await tryOnce();
      }
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, [field]: value } : o));
      setEditingField(null);
      toast.success('נשמר ✓', { duration: 1200 });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'שגיאה בשמירה - נסה שוב';
      toast.error(msg, { duration: 4000 });
      // Don't clear the field — keep the input open so the user can retry
    }
  };

  const hebrewMonths = [
    { value: '01', label: 'ינואר' }, { value: '02', label: 'פברואר' }, { value: '03', label: 'מרץ' },
    { value: '04', label: 'אפריל' }, { value: '05', label: 'מאי' }, { value: '06', label: 'יוני' },
    { value: '07', label: 'יולי' }, { value: '08', label: 'אוגוסט' }, { value: '09', label: 'ספטמבר' },
    { value: '10', label: 'אוקטובר' }, { value: '11', label: 'נובמבר' }, { value: '12', label: 'דצמבר' },
  ];

  const PRODUCT_IMAGES = {};
  const [productImagesMap, setProductImagesMap] = useState({});
  
  useEffect(() => {
    // Fetch product images from DB
    axios.get(`${API}/products`).then(r => {
      const map = {};
      for (const p of r.data) {
        const img = p.image_url || p.image || '';
        if (img) {
          map[p.id] = img;
          // Also map by name for fallback
          map[p.name] = img;
          map[p.name.toLowerCase()] = img;
        }
      }
      // Add gel variants manually
      map['warming-gel'] = 'https://customer-assets.emergentagent.com/job_intimate-care-pro-1/artifacts/uhp34fvx_4.png';
      map['buzzing-gel'] = '/api/static/buzzing-gel-white.png';
      setProductImagesMap(map);
    }).catch(() => {});
  }, []);

  const getProductImage = (item) => {
    if (!item) return null;
    // Try by product_id first
    if (item.product_id && productImagesMap[item.product_id]) return productImagesMap[item.product_id];
    // Try by exact name
    if (item.product_name && productImagesMap[item.product_name]) return productImagesMap[item.product_name];
    // Fuzzy match
    const n = (item.product_name || '').toLowerCase();
    if (n.includes('promescent') || n.includes('ספריי')) return 'https://www.promescent.com/cdn/shop/files/20Spray_1024x1024_da9bd637-8d47-4570-86e5-d4a698f59e90.webp?v=1738251213';
    if (n.includes('back') || n.includes('זיקפה') || n.includes('חיזוק')) return 'https://customer-assets.emergentagent.com/job_d56f6086-1f6c-43f9-a07d-5d56a502df97/artifacts/m1ozw06h_%2B%20%281%29.png';
    if (n.includes('buzzing') || n.includes('עוררות')) return '/api/static/buzzing-gel-white.png';
    if (n.includes('titan') || n.includes('טיטאן') || n.includes('ג\'ל')) return 'https://customer-assets.emergentagent.com/job_intimate-care-pro-1/artifacts/uhp34fvx_4.png';
    if (n.includes('ויטמל') || n.includes('פריון')) return 'https://customer-assets.emergentagent.com/job_intimate-care-pro-1/artifacts/o2gggziq_3.png';
    if (n.includes('חלבון')) return null;
    return null;
  };

  const applyMonthYear = (month, year) => {
    if (month && year) {
      const m = parseInt(month);
      const y = parseInt(year);
      const firstDay = `${y}-${String(m).padStart(2,'0')}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      const lastDayStr = `${y}-${String(m).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`;
      setDateFrom(firstDay);
      setDateTo(lastDayStr);
      // Sync the financial + FB Ads date window to the same month so
      // Facebook expenses (and net profit calculations) match exactly the
      // month the admin is looking at. Previously finDateFrom/To stayed
      // on the current calendar month, which caused FB to show a
      // different month's spend (e.g. June vs July).
      setFinDateFrom(firstDay);
      setFinDateTo(lastDayStr);
    }
  };

  // Constants for financial calculations
  const PRODUCT_COST = 68; // legacy fallback only
  // Product cost — flat 60₪ per UNIT (qty), per user spec (May 2026):
  // "על כל הזמנה יש הוצאה - עלות מוצר - 60 ש״ח - קבוע גורף על כל מוצר.
  //  אם הלקוח הזמין 3 מוצרים אז 180 ש״ח".
  // We sum quantities across all items in the order; legacy orders
  // without an `items` array are treated as a single unit (60₪).
  const PRODUCT_UNIT_COST = 60;
  const calcProductCost = (order) => {
    const items = Array.isArray(order?.items) ? order.items : [];
    if (items.length === 0) return PRODUCT_UNIT_COST; // legacy single-item orders
    const totalQty = items.reduce((q, it) => q + (parseInt(it.quantity, 10) || 1), 0);
    return PRODUCT_UNIT_COST * Math.max(1, totalQty);
  };
  // Simplified shipping-cost tiers (cost to the business, not the
  // customer-facing price).
  //   self-pickup ............ 0 ₪
  //   pickup point (HFD/UPS) . 20 ₪
  //   home delivery .......... 70 ₪  (any payment method)
  //   team courier (Yaniv/Liran) 40 ₪ (their delivery fee)
  //   private / sent by Bari .. 0 ₪  (no extra business cost)
  const SHIP_COST_CASH = 70;    // kept for legacy refs below
  const SHIP_COST_HOME = 70;    // simplified: all home delivery flat 70
  const SHIP_COST_PICKUP = 20;  // new unified pickup cost
  // Derive the shipping category for an order.
  // Priority: 1) explicit `o.shipping_category` override (admin edited),
  //           2) auto-derive from shipping_method + sent_by + payment_method.
  //  'delivery'      → עד הבית (70₪)
  //  'pickup'        → נקודת איסוף (20₪)
  //  'self_pickup'   → איסוף עצמי (0₪)
  //  'team_bari'     → נשלח ע״י ברי  (0₪ — ברי מוסר בחינם)
  //  'team_yaniv'    → נשלח ע״י יניב (40₪)
  //  'team_liran'    → נשלח ע״י לירן (40₪)
  const getShippingCategory = (o) => {
    if (o?.shipping_category) return o.shipping_category;
    // Auto-derive from payment_method when it already carries the courier.
    if (o?.payment_method === 'sent_bari' || o?.sent_by === 'ברי') return 'team_bari';
    if (o?.payment_method === 'sent_yaniv' || o?.sent_by === 'יניב') return 'team_yaniv';
    if (o?.payment_method === 'sent_liran' || o?.sent_by === 'לירן') return 'team_liran';
    if (o?.payment_method === 'sent_daniel' || o?.sent_by === 'דניאל') return 'team_daniel';
    const sm = o?.shipping_method || '';
    if (sm === 'self_pickup') return 'self_pickup';
    if (sm === 'pickup') return 'pickup';
    return 'delivery';
  };
  const SHIPPING_CATEGORY_LABELS = {
    delivery: 'עד הבית',
    pickup: 'נקודת איסוף',
    self_pickup: 'איסוף עצמי',
    team_bari: 'נשלח ע״י ברי',
    team_yaniv: 'נשלח ע״י יניב',
    team_liran: 'נשלח ע״י לירן',
    team_daniel: 'נשלח ע״י דניאל',
  };
  const SHIPPING_CATEGORY_COSTS = {
    delivery: 70,
    pickup: 20,
    self_pickup: 0,
    team_bari: 0,   // ברי delivers free
    team_yaniv: 40,
    team_liran: 40,
    team_daniel: 40,
  };
  // Mapping for auto-linking: when admin picks a "נשלח ע״י X" payment
  // method, flip the shipping_category to the matching team-X bucket.
  const PAYMENT_TO_CATEGORY = {
    sent_bari: 'team_bari',
    sent_yaniv: 'team_yaniv',
    sent_liran: 'team_liran',
    sent_daniel: 'team_daniel',
  };
  const calcShipCost = (o) => {
    // Returned orders aren't counted as shipping cost per user request
    // ("חזרו זה לא רווח - אבל פשוט תמחק - החישוב של זה"). They're a cost
    // to the business (60₪ reverse shipping) but tracked separately, not
    // in the monthly shipping-expense summary.
    if (o?.payment_method === 'returned') return 0;
    const cat = getShippingCategory(o);
    return SHIPPING_CATEGORY_COSTS[cat] ?? 70;
  };
  const VAT_RATE = 0.18;

  // Date-filtered orders (base for all calculations)
  const filteredByDate = useMemo(() => {
    const base = (dateFrom || dateTo)
      ? orders.filter(o => {
          const d = o.created_at ? new Date(o.created_at) : null;
          if (!d) return true;
          if (dateFrom && d < new Date(dateFrom)) return false;
          if (dateTo) { const end = new Date(dateTo); end.setHours(23,59,59,999); if (d > end) return false; }
          return true;
        })
      : orders;
    // Exclude cancelled orders from every revenue / count / cost tile per
    // user request ("הזמנות שבוטלו אל תכליל יותר בסה״כ כהכנסה"). The
    // cancelled-orders filter tile still reads the raw `orders` list, so
    // cancelled rows remain visible under that filter.
    return base.filter(o => o.status !== 'cancelled');
  }, [orders, dateFrom, dateTo]);

  // Category helpers - by payment method (all statuses for cards)
  const completedOrders = useMemo(() => filteredByDate.filter(o => o.status === 'completed'), [filteredByDate]);
  const pendingOrders = useMemo(() => filteredByDate.filter(o => o.status === 'processing'), [filteredByDate]);
  const byCreditCard = useMemo(() => filteredByDate.filter(o => o.payment_method === 'credit_card'), [filteredByDate]);
  const byBankTransfer = useMemo(() => filteredByDate.filter(o => o.payment_method === 'bank_transfer'), [filteredByDate]);
  const byBit = useMemo(() => filteredByDate.filter(o => o.payment_method === 'bit'), [filteredByDate]);
  const byCash = useMemo(() => filteredByDate.filter(o => o.payment_method === 'cash'), [filteredByDate]);
  const byReturned = useMemo(() => filteredByDate.filter(o => o.payment_method === 'returned'), [filteredByDate]);
  const byPrivate = useMemo(() => filteredByDate.filter(o => o.payment_method === 'private' || o.payment_method === 'sent_bari' || o.payment_method === 'sent_liran' || o.payment_method === 'sent_yaniv' || o.payment_method === 'sent_daniel'), [filteredByDate]);

  const sumRevenue = (list) => list.reduce((s, o) => s + (o.total_amount || 0), 0);

  // Calculate actual shipping cost per order based on what the customer chose
  const getOrderShippingCost = (order) => {
    // If the order has stored shipping_cost, use it as reference for the method
    // Determine by shipping_method if available, otherwise by payment_method
    if (order.shipping_method) {
      if (order.shipping_method === 'pickup') return SHIP_COST_PICKUP;
      if (order.shipping_method === 'delivery' || order.shipping_method === 'home') {
        if (order.payment_method === 'cash') return SHIP_COST_CASH;
        return SHIP_COST_HOME;
      }
      if (order.shipping_method === 'self_pickup') return 0;
    }
    // Fallback for old orders without shipping_method
    if (order.payment_method === 'cash') return SHIP_COST_CASH;
    return SHIP_COST_HOME; // default for credit/bit
  };

  // Filtered orders for table
  function orderBelongsToCourier(order, courierName) {
  if (!order || !courierName) return false;

  const courierMethodMap = {
  'ברי': ['private', 'sent_bari'],
  'לירן': ['sent_liran'],
  'יניב': ['sent_yaniv'],
  'דניאל': ['sent_daniel'],
  };

  const methods = courierMethodMap[courierName] || [];

  return (
  String(order.sent_by || '').trim() === courierName ||
  methods.includes(String(order.payment_method || '').trim())
  );
  }


  const displayedOrders = useMemo(() => {
    let filtered;
    switch (activeFilter) {
      case 'pending': filtered = pendingOrders; break;
      case 'completed': filtered = completedOrders; break;
      case 'credit_card': filtered = byCreditCard; break;
      case 'bank_transfer': filtered = byBankTransfer; break;
      case 'bit': filtered = byBit; break;
      case 'cash': filtered = byCash; break;
      case 'returned': filtered = byReturned; break;
      case 'private': filtered = byPrivate; break;
      // Per-courier breakdowns added May 2026 alongside the unified
      // "סינון לפי" dropdown. Each courier slug filters by its specific
      // payment_method or a sent_by tag (when admin sets it manually).
      case 'sent_bari': filtered = filteredByDate.filter(o => o.payment_method === 'sent_bari' || o.sent_by === 'ברי'); break;
      case 'sent_yaniv': filtered = filteredByDate.filter(o => o.payment_method === 'sent_yaniv' || o.sent_by === 'יניב'); break;
      case 'sent_liran': filtered = filteredByDate.filter(o => o.payment_method === 'sent_liran' || o.sent_by === 'לירן'); break;
      case 'sent_daniel': filtered = filteredByDate.filter(o => o.payment_method === 'sent_daniel' || o.sent_by === 'דניאל'); break;
      case 'shipped': filtered = filteredByDate.filter(o => o.payment_method === 'shipped'); break;
      case 'not_purchased': filtered = filteredByDate.filter(o => {
        if (o.payment_method === 'not_purchased') return true;
        // Inline copy of isAbandoned (defined later in the file) so this
        // useMemo doesn't hit a temporal dead zone on first render.
        // Keep in sync with the canonical isAbandoned() check.
        if (o.payment_status === 'paid' || o.transaction_id || o.paid_at) return false;
        if (o.shipping_provider || o.tracking_number || o.shipment_number) return false;
        if (o.pickup_provider || o.pickup_point_id) return false;
        if (o.hfd_shipment_number) return false;
        if (o.label_url || o.shipping_label_url) return false;
        return (o.payment_method === 'credit_card' || o.payment_method === 'bit') && o.payment_status === 'pending';
      }); break;
      case 'pickup': filtered = filteredByDate.filter(o => o.shipping_method === 'pickup'); break;
      case 'self_pickup': filtered = filteredByDate.filter(o => o.shipping_method === 'self_pickup'); break;
      case 'home_delivery': filtered = filteredByDate.filter(o => (o.shipping_method || 'delivery') === 'delivery' && !['sent_bari','sent_yaniv','sent_liran','sent_daniel'].includes(o.payment_method) && !['ברי','יניב','לירן','דניאל'].includes(o.sent_by)); break;
      default: filtered = filteredByDate;
    }
    // When showing cancelled/completed, add them back from the raw orders
    // list (filteredByDate excludes cancelled orders by default now).
    // IMPORTANT: when a payment/courier filter is active we MUST keep the
    // strict filter — otherwise clicking "כרטיס אשראי" while showCompleted
    // is on would pollute the table with bank/bit/cash orders.
    const paymentFilters = ['credit_card','bank_transfer','bit','cash','returned','private','sent_bari','sent_yaniv','sent_liran','sent_daniel','shipped','not_purchased','pickup','self_pickup','home_delivery'];
    const matchesActiveFilter = (o) => {
      switch (activeFilter) {
        case 'all': return true;
        case 'all_completed': return true;
        case 'pending': return o.status === 'processing';
        case 'completed': return o.status === 'completed';
        case 'credit_card': return o.payment_method === 'credit_card';
        case 'bank_transfer': return o.payment_method === 'bank_transfer';
        case 'bit': return o.payment_method === 'bit';
        case 'cash': return o.payment_method === 'cash';
        case 'returned': return o.payment_method === 'returned';
        case 'private': return ['private','sent_bari','sent_liran','sent_yaniv','sent_daniel'].includes(o.payment_method);
        case 'sent_bari': return o.payment_method === 'sent_bari' || o.sent_by === 'ברי';
        case 'sent_yaniv': return o.payment_method === 'sent_yaniv' || o.sent_by === 'יניב';
        case 'sent_liran': return o.payment_method === 'sent_liran' || o.sent_by === 'לירן';
        case 'sent_daniel': return o.payment_method === 'sent_daniel' || o.sent_by === 'דניאל';
        case 'shipped': return o.payment_method === 'shipped';
        case 'pickup': return o.shipping_method === 'pickup';
        case 'self_pickup': return o.shipping_method === 'self_pickup';
        case 'home_delivery': return (o.shipping_method || 'delivery') === 'delivery';
        default: return true;
      }
    };
    if (showCancelled) {
      const cancelledOrders = orders.filter(o => {
        if (o.status !== 'cancelled') return false;
        if (!matchesActiveFilter(o)) return false;
        if (!dateFrom && !dateTo) return true;
        const d = o.created_at ? new Date(o.created_at) : null;
        if (!d) return true;
        if (dateFrom && d < new Date(dateFrom)) return false;
        if (dateTo) { const end = new Date(dateTo); end.setHours(23,59,59,999); if (d > end) return false; }
        return true;
      });
      const existingIds = new Set(filtered.map(o => o.id));
      cancelledOrders.forEach(o => { if (!existingIds.has(o.id)) filtered = [...filtered, o]; });
    }
    if (showCompleted) {
      const doneOrders = filteredByDate.filter(o => o.status === 'completed' && matchesActiveFilter(o));
      const existingIds = new Set(filtered.map(o => o.id));
      doneOrders.forEach(o => { if (!existingIds.has(o.id)) filtered = [...filtered, o]; });
    }
    // Final hard guard: after every other transformation, drop any row that
    // doesn't match the currently selected filter chip. This protects
    // against future code paths that might inject unrelated orders.
    if (paymentFilters.includes(activeFilter)) {
      filtered = filtered.filter(matchesActiveFilter);
    }
    if (phoneSearch.trim()) {
      // Multi-field search: phone / customer name / order number / address /
      // email. The user types one query and we look across all common
      // identification fields. Case-insensitive + ignores dashes/spaces
      // for phone matches.
      const raw = phoneSearch.trim();
      const q = raw.toLowerCase();
      const qDigits = raw.replace(/[-\s]/g, '');
      filtered = filtered.filter(o => {
        const phone = (o.customer_phone || '').replace(/[-\s]/g, '');
        if (phone && phone.includes(qDigits)) return true;
        const name = (o.customer_name || '').toLowerCase();
        if (name && name.includes(q)) return true;
        const orderNum = String(o.order_number || '').toLowerCase();
        if (orderNum && orderNum.includes(q)) return true;
        const shortId = String(o.id || '').slice(0, 8).toLowerCase();
        if (shortId && shortId.includes(q)) return true;
        // HFD shipment number — admin often pastes "HFD #89566389" or just
        // "89566389" from the table column or HFD's site to find an order.
        const hfdNum = String(o.hfd_shipment_number || '').toLowerCase();
        if (hfdNum && (hfdNum.includes(q) || hfdNum.includes(qDigits))) return true;
        // eHouse shipment number (Zigzag / Cargo)
        const ehouseId = String(o.ehouse_shipment_id || '').toLowerCase();
        if (ehouseId && ehouseId.includes(q)) return true;
        const addr = (o.customer_address || '').toLowerCase();
        if (addr && addr.includes(q)) return true;
        const email = (o.customer_email || '').toLowerCase();
        if (email && email.includes(q)) return true;
        return false;
      });
    }
    // Filter by shipping provider
    if (providerFilter !== 'all') {
      filtered = filtered.filter(o => {
        if (providerFilter === 'hfd') return o.hfd_shipment_number || o.pickup_provider === 'hfd';
        if (providerFilter === 'private') return o.private_shipment;
        if (providerFilter === 'self_pickup') return o.shipping_method === 'self_pickup';
        return true;
      });
    }
    // Filter by sender
    if (senderFilter !== 'all') {
      filtered = filtered.filter(o => orderBelongsToCourier(o, senderFilter));
    }
    // Hide completed orders by default — UNLESS the user filtered by a
    // courier (senderFilter) or the "completed" status filter explicitly,
    // in which case they're explicitly looking for that group. Without
    // this exception, clicking the "ברי/יניב/לירן" card showed an empty
    // table (most team-shipped orders are completed) and felt like a
    // freeze. Same for the "הושלמו" filter chip.
    const wantsCompleted = senderFilter !== 'all' || activeFilter === 'completed';
    if (!showCompleted && !wantsCompleted) {
      filtered = filtered.filter(o => o.status !== 'completed');
    }
    // Hide cancelled orders by default
    if (!showCancelled) {
      filtered = filtered.filter(o => o.status !== 'cancelled');
    }
    // HFD alert priority: orders with active (non-dismissed) HFD alerts
    // (problem=red / attention=orange) jump to the TOP of the list so the
    // admin sees them first. Problem beats attention. Within each bucket
    // we keep the original creation order.
    const alertPriority = (o) => {
      // Prefer the LIVE status from this session's poll over the stale
      // status stored on the order doc — we may just have refreshed.
      const live = o.hfd_shipment_number ? hfdStatuses[o.hfd_shipment_number] : null;
      const level = live?.level || o.hfd_status_level || '';
      const currentStatus = live?.status || o.hfd_status_text || '';
      // A dismiss only counts if it was for the SAME status text. If the
      // status changed (e.g. HFD now reports a different problem), the
      // alert re-fires.
      const dismissed = o.hfd_alert_dismissed_at &&
                        o.hfd_alert_dismissed_status === currentStatus;
      if (dismissed) return 99;
      if (level === 'problem') return 0;
      if (level === 'attention') return 1;
      return 99;
    };
    // Stable sort: decorate → sort → undecorate.
    filtered = filtered
      .map((o, i) => ({ o, i, p: alertPriority(o) }))
      .sort((a, b) => (a.p - b.p) || (a.i - b.i))
      .map(x => x.o);
    return filtered;
  }, [activeFilter, filteredByDate, pendingOrders, completedOrders, byCreditCard, byBankTransfer, byBit, byCash, byPrivate, byReturned, phoneSearch, providerFilter, senderFilter, showCompleted, showCancelled, hfdStatuses, orders, dateFrom, dateTo]);

  // Financial calculations - filtered by financial date range
  const finFilteredOrders = useMemo(() => {
    return orders.filter(o => {
      if (o.status !== 'completed') return false;
      const d = o.created_at ? new Date(o.created_at) : null;
      if (!d) return false;
      if (finDateFrom) {
        const from = new Date(finDateFrom);
        from.setHours(0, 0, 0, 0);
        if (d < from) return false;
      }
      if (finDateTo) {
        const to = new Date(finDateTo);
        to.setHours(23, 59, 59, 999);
        if (d > to) return false;
      }
      return true;
    });
  }, [orders, finDateFrom, finDateTo]);

  const countedOrders = finFilteredOrders;

  // Filter handlers — purely client-side filtering. We DO NOT refetch
  // from the server here (used to call fetchOrders and made every filter
  // click feel laggy by 2-3 seconds). The orders array is already in
  // memory; switching filters just re-runs the displayedOrders memo.
  const handleActiveFilter = (filter) => {
    setActiveFilter(activeFilter === filter ? 'all' : filter);
    setOrderLimit(6);
  };

  const handleProviderFilter = (filter) => {
    setProviderFilter(providerFilter === filter ? 'all' : filter);
    setOrderLimit(6);
  };

  // Single source of truth for courier/order matching.
  // Function declaration is intentionally used here because it is hoisted
  // and may safely be called earlier in the component render.
  
  const handleSenderFilter = (name) => {
    setSenderFilter(senderFilter === name ? 'all' : name);
    setOrderLimit(6);
  };

  // Reset pagination whenever the filter/search criteria change so the
  // user sees the most relevant 6 results, not the tail of an old window.
  useEffect(() => { setOrderLimit(6); }, [phoneSearch, dateFrom, dateTo, showCompleted, showCancelled]);

  const senderCounts = useMemo(() => {
    const counts = { 'ברי': 0, 'לירן': 0, 'יניב': 0, 'דניאל': 0 };
    orders.forEach(o => { if (o.private_shipment && o.sent_by && counts[o.sent_by] !== undefined) counts[o.sent_by]++; });
    return counts;
  }, [orders]);

  const nonPrivateOrders = useMemo(() => countedOrders.filter(o => !['private','sent_bari','sent_liran','sent_yaniv','sent_daniel'].includes(o.payment_method)), [countedOrders]);
  const totalVat = useMemo(() => Math.round(sumRevenue(nonPrivateOrders) * VAT_RATE), [nonPrivateOrders]);
  const totalProductCost = useMemo(() => countedOrders.length * PRODUCT_COST, [countedOrders]);

  // Shipping costs calculated from counted orders only (skip private shipments - no cost)
  const shippingBreakdown = useMemo(() => {
    let cash = 0, home = 0, pickup = 0;
    countedOrders.forEach(o => {
      if (o.private_shipment) return; // Private = owner delivers, no shipping cost
      const sm = o.shipping_method;
      if (sm === 'pickup') { pickup += SHIP_COST_PICKUP; }
      else if (sm === 'self_pickup') { /* 0 */ }
      else if (o.payment_method === 'cash') { cash += SHIP_COST_CASH; }
      else if (sm === 'delivery' || sm === 'home' || !sm) {
        if (!['private','sent_bari','sent_liran','sent_yaniv','sent_daniel'].includes(o.payment_method)) home += SHIP_COST_HOME;
      }
    });
    return { cash, home, pickup, total: cash + home + pickup };
  }, [countedOrders]);

  // Net profit: Total Revenue - VAT - Product Cost - Shipping
  // cashReportRevenue (cash remittances uploaded from HFD/Zigzag/Cargo)
  // is added on top of the order-based revenue so the headline "סה״כ
  // הכנסות" reflects EVERY shekel actually received this period.
  const orderRevenue = useMemo(() => sumRevenue(countedOrders), [countedOrders]);
  const totalRevenue = orderRevenue + cashReportRevenue;
  const fbPeriodSpend = fbStats ? Math.round(fbStats.period?.spend || fbStats.this_month?.spend || 0) : 0;
  const netProfit = useMemo(() => Math.round(totalRevenue - totalVat - totalProductCost - shippingBreakdown.total - fbPeriodSpend), [totalRevenue, totalVat, totalProductCost, shippingBreakdown, fbPeriodSpend]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === displayedOrders.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayedOrders.map(o => o.id)));
    }
  };

  const bulkUpdateStatus = async (newStatus) => {
    for (const orderId of selectedIds) {
      try {
        await axios.put(`${API}/admin/orders/${orderId}/status`, { status: newStatus });
      } catch (e) { console.error(e); }
    }
    setSelectedIds(new Set());
    fetchOrders();
  };

  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const bulkDelete = async () => {
    if (selectedIds.size === 0) {
      toast.error('לא נבחרו הזמנות');
      return;
    }
    // Two-click confirmation instead of window.confirm (sometimes blocked by browsers/extensions)
    if (!confirmBulkDelete) {
      setConfirmBulkDelete(true);
      toast('לחץ שוב על "מחק נבחרים" לאישור', { icon: '⚠️', duration: 4000 });
      setTimeout(() => setConfirmBulkDelete(false), 4000);
      return;
    }
    setConfirmBulkDelete(false);
    const ids = Array.from(selectedIds);
    toast.loading(`מוחק ${ids.length} הזמנות...`, { id: 'bulk-del' });
    const results = await Promise.allSettled(
      ids.map(id => axios.delete(`${API}/admin/orders/${id}`))
    );
    toast.dismiss('bulk-del');
    const failed = results.filter(r => r.status === 'rejected');
    if (failed.length === 0) {
      toast.success(`נמחקו ${ids.length} הזמנות בהצלחה`);
    } else {
      toast.error(`${ids.length - failed.length} נמחקו, ${failed.length} נכשלו`);
      console.error('Bulk delete failures:', failed);
    }
    setSelectedIds(new Set());
    setSelectedOrder(null);
    fetchOrders();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password
      });
      
      if (response.data.success) {
        setIsLoggedIn(true);
        fetchOrders();
      }
    } catch (error) {
      setLoginError('שם משתמש או סיסמה שגויים');
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/orders`);
      setOrders(response.data);
      // HFD statuses come from the persisted fields on each order
      // (`hfd_status_text` / `hfd_status_level`) populated by the
      // background worker every 15 min. We DO NOT live-fetch HFD here
      // anymore — that used to fire one HTTP request per order on every
      // refresh and froze the browser when the team marked deliveries
      // (each successful action triggered fetchOrders → 50+ HFD calls).
      // For a fresh pull on demand, use the "🔄 רענן HFD" button.
      const hfdMap = {};
      for (const o of response.data) {
        if (o.hfd_shipment_number && o.hfd_status_text) {
          hfdMap[o.hfd_shipment_number] = {
            shipment_number: o.hfd_shipment_number,
            status: o.hfd_status_text,
            status_date: o.hfd_status_date || '',
            level: o.hfd_status_level || 'transit',
            delivered: o.hfd_status_level === 'delivered',
            cancelled: o.hfd_status_level === 'problem' && (o.hfd_status_text || '').includes('בוטל'),
          };
        }
      }
      setHfdStatuses(hfdMap);
      // Release the loading flag IMMEDIATELY once orders are rendered.
      // UPS was removed May 2026 — no more background tracking fetch.
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setLoading(false);
    }
  };

  // Fetch orders on mount (no auto-refresh)
  useEffect(() => {
    fetchOrders();
    const fetchClicks = async () => {
      try {
        const r = await axios.get(`${API}/admin/clicks`);
        setClickStats(r.data);
      } catch (e) { console.error('Click stats error:', e); }
    };
    fetchClicks();
    const fetchClickSlots = async () => {
      try {
        const r = await axios.get(`${API}/admin/click-slots`);
        setClickSlots(r.data || []);
      } catch (e) { console.error('Click slots error:', e); }
    };
    fetchClickSlots();
    const fetchFbStats = async () => {
      try {
        const r = await axios.get(`${API}/admin/fb-ads-stats`, { params: { since: finDateFrom, until: finDateTo } });
        if (!r.data.error) setFbStats(r.data);
      } catch (e) { console.error('FB stats error:', e); }
    };
    fetchFbStats();
    const fetchFbCampaigns = async () => {
      try {
        const r = await axios.get(`${API}/admin/fb-campaigns`);
        if (r.data.campaigns) setFbCampaigns(r.data.campaigns);
      } catch (e) { console.error('FB campaigns error:', e); }
    };
    fetchFbCampaigns();
    const fetchStockOverrides = async () => {
      try {
        const r = await axios.get(`${API}/team/stock-overrides`);
        setStockOverrides(Array.isArray(r.data) ? r.data : []);
      } catch (e) { /* graceful */ }
    };
    fetchStockOverrides();
    const stockTimer = setInterval(fetchStockOverrides, 30000);
    return () => clearInterval(stockTimer);
  }, [finDateFrom, finDateTo]);

  const toggleCampaignStatus = async (campaignId, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    setCampaignLoading(campaignId);
    try {
      await axios.post(`${API}/admin/fb-campaign/${campaignId}/status`, { status: newStatus });
      setFbCampaigns(prev => prev.map(c => c.id === campaignId ? { ...c, status: newStatus, effective_status: newStatus } : c));
      toast.success(newStatus === 'ACTIVE' ? 'קמפיין הופעל' : 'קמפיין הושהה');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שגיאה בעדכון סטטוס');
    } finally { setCampaignLoading(null); }
  };

  const saveCampaignBudget = async (id, budget, level, field = 'daily_budget') => {
    const val = parseFloat(budget);
    if (!val || val < 1) { toast.error('תקציב חייב להיות לפחות ₪1'); return; }
    setCampaignLoading(id);
    try {
      const url = level === 'adset' ? `${API}/admin/fb-adset/${id}/budget` : `${API}/admin/fb-campaign/${id}/budget`;
      await axios.post(url, { [field]: val });
      if (level === 'adset') {
        setFbCampaigns(prev => prev.map(c => ({...c, adsets: c.adsets?.map(a => a.id === id ? {...a, [field]: val} : a)})));
      } else {
        setFbCampaigns(prev => prev.map(c => c.id === id ? { ...c, [field]: val } : c));
      }
      setEditingBudget(null);
      toast.success('תקציב עודכן בהצלחה');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שגיאה בעדכון תקציב');
    } finally { setCampaignLoading(null); }
  };

  // Fetch active FB budget schedules and refresh every 30s
  const fetchBudgetSchedules = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/fb-budget-schedules`);
      setBudgetSchedules((r.data.schedules || []).filter(s => s.status === 'pending'));
    } catch (e) {}
  }, []);
  useEffect(() => {
    fetchBudgetSchedules();
    const t = setInterval(fetchBudgetSchedules, 30000);
    return () => clearInterval(t);
  }, [fetchBudgetSchedules]);

  const openScheduleModal = (targetId, level, name, currentBudget) => {
    setScheduleModal({ targetId, level, name, currentBudget });
    const next = new Date();
    next.setHours(next.getHours() + 1, 0, 0, 0);
    setSchedDate(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`);
    setSchedTime(`${String(next.getHours()).padStart(2, '0')}:00`);
    setSchedAmount('');
  };

  const submitBudgetSchedule = async () => {
    if (!scheduleModal) return;
    const amt = parseFloat(schedAmount);
    if (!amt || amt < 1) { toast.error('תקציב חייב להיות לפחות ₪1'); return; }
    if (!schedDate || !schedTime) { toast.error('בחר תאריך ושעה'); return; }
    const target = new Date(`${schedDate}T${schedTime}:00`);
    if (isNaN(target.getTime()) || target <= new Date()) { toast.error('זמן חייב להיות בעתיד'); return; }
    try {
      await axios.post(`${API}/admin/fb-budget-schedules`, {
        target_id: scheduleModal.targetId,
        level: scheduleModal.level,
        scheduled_at: `${schedDate}T${schedTime}:00`,
        new_budget: amt,
      });
      toast.success(`תוזמן: ${schedDate} ${schedTime} → ₪${amt}`);
      setScheduleModal(null);
      fetchBudgetSchedules();
    } catch (e) { toast.error(e.response?.data?.detail || 'שגיאה'); }
  };

  const cancelBudgetSchedule = async (sid) => {
    if (!window.confirm('לבטל את התזמון?')) return;
    try {
      await axios.delete(`${API}/admin/fb-budget-schedules/${sid}`);
      toast.success('התזמון בוטל');
      fetchBudgetSchedules();
    } catch { toast.error('שגיאה'); }
  };

  const createShipment = async (order) => {
    setShippingLoading(order.id);
    try {
      const isPickup = order.shipping_method === 'pickup';
      const resp = await axios.post(`${API}/shipping/create-shipment`, {
        order_id: order.id,
        shipment_type: isPickup ? 'pickup' : 'home',
      });
      if (resp.data.success) {
        toast.success(`משלוח נוצר בהצלחה! מספר מעקב: ${resp.data.tracking_number}`);
        fetchOrders();
      } else {
        toast.error(resp.data.error || 'שגיאה לא ידועה');
      }
    } catch (error) {
      toast.error(`שגיאה ביצירת משלוח: ${error.response?.data?.detail || error.message}`);
    }
    setShippingLoading(null);
  };

  // Modal state for the eHouse send-with-carrier flow. When the admin
  // clicks "זיגזג" or "קרגו" on an order card we open a modal showing
  // the customer details + 3 mode buttons (pickup / home / home-cash).
  const [ehouseModal, setEhouseModal] = useState({ open: false, order: null, carrier: null });

  // Push an order to eHouse (ihouse.ai) shipping system. Manual trigger
  // from the order card — sends customer + items via Basic Auth to their
  // /api/v1/orders endpoint. Server persists the returned shipment id on
  // the order so we don't double-push.
  const createEhouseShipment = async (order, opts = {}) => {
    setShippingLoading(order.id);
    try {
      const resp = await axios.post(`${API}/shipping/create-ehouse-shipment`, {
        order_id: order.id,
        carrier: opts.carrier || null,
        mode: opts.mode || null,
      });
      if (resp.data.success) {
        const sid = resp.data.shipment_id || resp.data.order_id || '—';
        const carrierTxt = resp.data.carrier ? ` (${resp.data.carrier})` : '';
        if (resp.data.duplicate) {
          toast.info(`כבר נשלח ל-E-HOUSE (#${sid})`);
        } else {
          toast.success(`נשלח ל-E-HOUSE${carrierTxt}! מזהה: ${sid}`);
        }
        // Close modal if it was open
        setEhouseModal({ open: false, order: null, carrier: null });
        fetchOrders();
      } else {
        toast.error(resp.data.error || 'שגיאה ב-E-HOUSE');
      }
    } catch (error) {
      toast.error(`שגיאה E-HOUSE: ${error.response?.data?.detail || error.message}`);
    }
    setShippingLoading(null);
  };

  const createHfdShipment = async (order, forceRecreate = false) => {
    setShippingLoading(order.id);
    try {
      const resp = await axios.post(`${API}/shipping/create-hfd-shipment`, {
        order_id: order.id,
        shipment_type: 'home',
        force_recreate: forceRecreate,
      });
      if (resp.data.success) {
        toast.success(`משלוח HFD נוצר! מספר: ${resp.data.shipment_number}`);
        fetchOrders();
      } else if (resp.data.duplicate || resp.data.duplicate_reference) {
        // Either we already have a shipment for this order, or HFD itself
        // rejected the reference number as a duplicate. Ask the user
        // whether to force-create a brand-new one (with a unique reference).
        const existing = resp.data.existing_shipment_number;
        const msg = existing
          ? `להזמנה זו כבר קיים משלוח HFD #${existing}.\n\nלשלוח בקשה חדשה עם מספר אסמכתא ייחודי?`
          : `HFD זיהה את הבקשה ככפולה (Duplicated request).\n\nלשלוח שוב עם מספר אסמכתא חדש?`;
        if (window.confirm(msg)) {
          setShippingLoading(null);
          await createHfdShipment(order, true);
          return;
        }
      } else {
        toast.error(resp.data.error || 'שגיאה ב-HFD');
      }
    } catch (error) {
      toast.error(`שגיאה HFD: ${error.response?.data?.detail || error.message}`);
    }
    setShippingLoading(null);
  };

  const markPrivateShipment = async (order, sentBy = 'ברי') => {
    // First print the internal label
    printLabel(order);
    // Then mark as private with sender
    setShippingLoading(order.id);
    try {
      const resp = await axios.post(`${API}/admin/orders/${order.id}/mark-private`, { sent_by: sentBy });
      if (resp.data.success) {
        toast.success(`ההזמנה סומנה כנשלח ע"י ${sentBy}`);
        // Auto-open WhatsApp to the assigned team member so the admin
        // can notify them in one click that a new shipment was created.
        // Phones match the dashboard sender-card config below.
        const STAFF_PHONES = {
          'ברי': '0539406051',
          'לירן': '0509959513',
          'יניב': '0515112205',
          'דניאל': '0532260855',
        };
        const phone = STAFF_PHONES[sentBy];
        if (phone) {
          const intl = `972${phone.replace(/^0/, '').replace(/[-\s]/g, '')}`;
          const msg = `שלום, ${sentBy}\nהקמתי לך משלוח במערכת\n\nבהצלחה ויום טוב`;
          const waUrl = `https://wa.me/${intl}?text=${encodeURIComponent(msg)}`;
          // Use a slight delay so the toast renders first; window.open
          // inside a click handler keeps the user-gesture chain valid
          // so the browser doesn't block the popup.
          window.open(waUrl, '_blank', 'noopener,noreferrer');
        }
        fetchOrders();
        // Count packages this courier shipped TODAY only (not all-time).
        // Previous version counted every Bari shipment ever, producing
        // bogus "ברי שלח 18 חבילות היום!" toasts the user reported.
        const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
        const updatedOrders = orders.map(o => o.id === order.id ? { ...o, private_shipment: true, sent_by: sentBy, _justMarked: true } : o);
        const senderCount = updatedOrders.filter(o => {
          if (o.sent_by !== sentBy || !o.private_shipment) return false;
          const ts = o.private_shipped_at || o._justMarked ? new Date() : (o.created_at ? new Date(o.created_at) : null);
          if (!ts || isNaN(ts.getTime())) return false;
          return ts >= todayStart;
        }).length;
        if (senderCount >= 5) {
          toast(`${sentBy} שלח ${senderCount} חבילות היום!`, { icon: '📦', duration: 5000 });
        }
      } else {
        toast.error('שגיאה בסימון');
      }
    } catch (error) {
      toast.error(`שגיאה: ${error.response?.data?.detail || error.message}`);
    }
    setShippingLoading(null);
  };


  const printShipLabel = async (trackingNumber) => {
    try {
      const resp = await axios.get(`${API}/shipping/print-label/${trackingNumber}`);
      if (resp.data.FileByteArray) {
        // Open PDF from base64
        const byteChars = atob(resp.data.FileByteArray);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        alert('לא ניתן לייצר מדבקה');
      }
    } catch (error) {
      alert(`שגיאה בהדפסת מדבקה: ${error.message}`);
    }
  };

  const [duplicateArmed, setDuplicateArmed] = useState(null);

  const duplicateShipment = async (order) => {
    if (duplicateArmed !== order.id) {
      setDuplicateArmed(order.id);
      toast(`לחץ שוב לשכפול ההזמנה של ${order.customer_name}`, { icon: '⚠️', duration: 4000 });
      setTimeout(() => setDuplicateArmed((v) => (v === order.id ? null : v)), 4000);
      return;
    }
    setDuplicateArmed(null);
    const dupToastId = `dup-${order.id}`;
    toast.loading('משכפל הזמנה...', { id: dupToastId });
    try {
      const resp = await axios.post(`${API}/orders`, {
        customer_name: order.customer_name,
        customer_phone: order.customer_phone,
        customer_email: order.customer_email || '',
        customer_address: order.customer_address,
        city: order.city || '',
        street: order.street || '',
        house_number: order.house_number || '',
        pickup_point_name: order.pickup_point_name || '',
        pickup_point_id: order.pickup_point_id || '',
        pickup_provider: order.pickup_provider || '',
        hfd_spot_id: order.hfd_spot_id || '',
        items: order.items || [],
        total_amount: order.total_amount,
        payment_method: order.payment_method,
        shipping_method: order.shipping_method,
        shipping_cost: order.shipping_cost || 0,
        notes: `שוכפל מהזמנה ${order.id.slice(0, 8)}`,
      });
      toast.dismiss(dupToastId);
      toast.success(`הזמנה שוכפלה בהצלחה! #${resp.data.id.slice(0,8)}`);
      fetchOrders();
    } catch (error) {
      toast.dismiss(dupToastId);
      toast.error(`שגיאה בשיכפול: ${error.response?.data?.detail || error.message}`);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, {
        status: newStatus
      });
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const deleteOrder = async (orderId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק את ההזמנה?')) {
      try {
        await axios.delete(`${API}/admin/orders/${orderId}`);
        fetchOrders();
        setSelectedOrder(null);
      } catch (error) {
        console.error('Error deleting order:', error);
      }
    }
  };

  const updatePaymentMethod = async (orderId, newMethod) => {
    // Optimistically update local state so the row leaves the active
    // filter IMMEDIATELY (e.g. switching אשראי→העברה בנקאית while the
    // אשראי filter is active should hide the row instantly). Without
    // this, the row hangs around until `fetchOrders()` finishes.
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, payment_method: newMethod } : o));
    try {
      await axios.put(`${API}/admin/orders/${orderId}/payment`, {
        payment_method: newMethod
      });
      // Auto-link shipping category when the new payment method implies a
      // specific courier (sent_bari / sent_yaniv / sent_liran). This keeps
      // the shipping dropdown in sync with the payment dropdown and makes
      // sure the courier-attribution numbers below reconcile.
      const autoCat = PAYMENT_TO_CATEGORY[newMethod];
      if (autoCat) {
        try {
          await axios.patch(`${API}/admin/orders/${orderId}/field`, { shipping_category: autoCat });
        } catch (e) { /* non-fatal — category can be fixed manually */ }
      }
      fetchOrders();
    } catch (error) {
      console.error('Error updating payment method:', error);
    }
  };

  const printLabel = (order) => {
    const paymentText = getPaymentMethodText(order.payment_method);
    const isCash = order.payment_method === 'cash';
    const itemsCount = (order.items || []).reduce((s, i) => s + (i.quantity || 1), 0);
    const amount = order.total_amount || 0;

    // Payment banner: cash = "יש לשלם" | others = "שולם + איך"
    const paymentBanner = isCash
      ? `<div class="pay-banner pay-due">
           <div class="pay-title">יש לשלם</div>
           <div class="pay-amount">${amount} ש"ח</div>
         </div>`
      : `<div class="pay-banner pay-paid">
           <div class="pay-title">שולם · ${paymentText}</div>
           <div class="pay-amount">${amount} ש"ח</div>
         </div>`;

    // WhatsApp QR - opens chat with preset message
    const phoneForWa = `972${(order.customer_phone || '').replace(/^0/, '').replace(/[-\s]/g, '')}`;
    const waMsg = 'שלום, זה השליח, אתה נמצא בכתובת במהלך היום?';
    const waUrl = `whatsapp://send?phone=${phoneForWa}&text=${encodeURIComponent(waMsg)}`;
    const waQr = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=4&data=${encodeURIComponent(waUrl)}`;

    // Waze QR - opens Waze navigation to the address
    const addr = order.customer_address || '';
    const wazeUrl = `https://waze.com/ul?q=${encodeURIComponent(addr)}&navigate=yes`;
    const wazeQr = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=4&data=${encodeURIComponent(wazeUrl)}`;

    const html = `<!DOCTYPE html>
<html dir="rtl" lang="he">
<head>
<meta charset="UTF-8">
<title>מדבקת משלוח</title>
<style>
  @page { size: 100mm 150mm; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    width: 100mm; height: 150mm;
    font-family: Arial, sans-serif;
    padding: 4mm;
    display: flex; flex-direction: column;
    direction: rtl;
  }
  .header {
    text-align: center;
    border-bottom: 2px solid #000;
    padding-bottom: 2mm;
    margin-bottom: 3mm;
    font-size: 11pt; font-weight: bold;
  }
  .details { font-size: 11pt; line-height: 1.8; margin-bottom: 3mm; }
  .row { display: flex; gap: 2mm; }
  .label { font-weight: bold; min-width: 16mm; }
  .pay-banner {
    text-align: center;
    padding: 3mm;
    margin: 2mm 0 3mm 0;
    border-radius: 2mm;
    border: 2px solid;
  }
  .pay-due { background: #fff1f0; border-color: #dc2626; color: #991b1b; }
  .pay-paid { background: #ecfdf5; border-color: #16a34a; color: #166534; }
  .pay-title { font-size: 14pt; font-weight: 900; letter-spacing: 0.5mm; }
  .pay-amount { font-size: 18pt; font-weight: 900; margin-top: 1mm; }
  .qr-wrap { display: flex; justify-content: space-between; gap: 8mm; margin-top: auto; padding: 0 2mm; }
  .qr-item { flex: 1; text-align: center; border: 1px solid #ddd; border-radius: 2mm; padding: 2mm; }
  .qr-item img { width: 32mm; height: 32mm; display: block; margin: 0 auto; }
  .qr-item .qr-label { font-size: 8pt; font-weight: bold; margin-top: 1mm; }
  .footer {
    border-top: 1px solid #ccc;
    padding-top: 2mm;
    margin-top: 2mm;
    text-align: center;
    font-size: 8pt; color: #666;
  }
</style>
</head>
<body>
  <div class="header">משלוח · בריאות הגבר</div>
  <div class="details">
    <div class="row"><span class="label">שם:</span><span>${order.customer_name}</span></div>
    <div class="row"><span class="label">טלפון:</span><span dir="ltr">${order.customer_phone}</span></div>
    <div class="row"><span class="label">כתובת:</span><span>${addr || '-'}</span></div>
    <div class="row"><span class="label">כמות:</span><span>${itemsCount} פריטים</span></div>
    ${order.notes ? `<div class="row"><span class="label">הערות:</span><span>${order.notes}</span></div>` : ''}
  </div>
  ${paymentBanner}
  <div class="qr-wrap">
    <div class="qr-item">
      <img src="${waQr}" alt="WhatsApp QR" />
      <div class="qr-label">וואטסאפ ללקוח</div>
    </div>
    <div class="qr-item">
      <img src="${wazeQr}" alt="Waze QR" />
      <div class="qr-label">Waze לכתובת</div>
    </div>
  </div>
  <div class="footer">#${order.id.slice(0, 8)}</div>
</body>
</html>`;

    const printWin = window.open('', '_blank', 'width=420,height=620');
    printWin.document.write(html);
    printWin.document.close();
    printWin.focus();
    // Wait for QR images to load before printing
    printWin.onload = () => {
      setTimeout(() => printWin.print(), 500);
    };
  };

  const printPickupLabel = (order) => {
    const paymentText = getPaymentMethodText(order.payment_method);
    const itemsCount = (order.items || []).reduce((s, i) => s + (i.quantity || 1), 0);
    const date = formatDate(order.created_at);

    const html = `<!DOCTYPE html>
<html dir="rtl" lang="he">
<head>
<meta charset="UTF-8">
<title>מדבקת איסוף עצמי</title>
<style>
  @page { size: 100mm 100mm; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    width: 100mm; height: 100mm;
    font-family: Arial, sans-serif;
    padding: 5mm;
    display: flex; flex-direction: column;
    direction: rtl;
  }
  .header {
    text-align: center;
    border: 2px solid #7c3aed;
    background: #f5f3ff;
    color: #5b21b6;
    padding: 3mm;
    margin-bottom: 4mm;
    font-size: 13pt; font-weight: bold;
    border-radius: 4mm;
  }
  .details { flex: 1; font-size: 12pt; line-height: 2; }
  .row { display: flex; gap: 2mm; }
  .label { font-weight: bold; min-width: 18mm; }
  .footer {
    border-top: 2px solid #000;
    padding-top: 3mm;
    margin-top: auto;
    text-align: center;
    font-size: 9pt; color: #666;
  }
</style>
</head>
<body>
  <div class="header">איסוף עצמי</div>
  <div class="details">
    <div class="row"><span class="label">שם:</span><span>${order.customer_name}</span></div>
    <div class="row"><span class="label">טלפון:</span><span dir="ltr">${order.customer_phone}</span></div>
    <div class="row"><span class="label">כמות:</span><span>${itemsCount} פריטים</span></div>
    <div class="row"><span class="label">סכום:</span><span style="font-size:14pt;font-weight:bold">${order.total_amount} ש"ח</span></div>
    <div class="row"><span class="label">תשלום:</span><span>${paymentText}</span></div>
    <div class="row"><span class="label">תאריך:</span><span>${date}</span></div>
    ${order.notes ? `<div class="row"><span class="label">הערות:</span><span>${order.notes}</span></div>` : ''}
  </div>
  <div class="footer">הזמנה #${order.id.slice(0, 8)}</div>
</body>
</html>`;

    const printWin = window.open('', '_blank', 'width=400,height=400');
    printWin.document.write(html);
    printWin.document.close();
    printWin.focus();
    printWin.print();
  };

  const markSelfPickup = async (order) => {
    // Print pickup label first
    printPickupLabel(order);
    // Mark order as completed (picked up)
    try {
      await axios.put(`${API}/admin/orders/${order.id}/status`, { status: 'completed' });
      toast.success('ההזמנה סומנה כנאספה והושלמה');
      fetchOrders();
    } catch (error) {
      toast.error(`שגיאה: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Abandoned = customer started credit_card/bit checkout but didn't complete payment,
  // OR admin manually marked as 'not_purchased'. These rows show red + "לא רכש" label.
  // Per user feedback: successfully-paid credit-card orders were still showing
  // the "לא רכש / סמן כנרכש / קישור תשלום" buttons. The logic is now more
  // conservative — any signal of a successful payment (transaction_id,
  // paid_at, payment_status='paid', shipped/completed status, assigned to
  // team, printed shipping label, etc.) disables the abandoned label.
  const isAbandoned = (order) => {
    if (!order) return false;
    // Manual override wins over everything — admin explicitly flagged.
    if (order.payment_method === 'not_purchased') return true;
    // Any non-processing status means admin already progressed the order.
    if (['completed','shipped'].includes(order.status)) return false;
    if ((order.payment_method || '').startsWith('sent_')) return false;
    // Hard signals of a completed payment.
    if (order.payment_status === 'paid') return false;
    if (order.transaction_id) return false;
    if (order.paid_at) return false;
    // Shipping-provider side effects that only happen after payment.
    // ANY shipment/tracking artifact means we've physically shipped — we
    // don't ship without payment, so this is a strong "paid" signal even
    // when the CardCom webhook never fired (May 2026 fix: UPS pickup
    // orders use `pickup_provider`/`pickup_point_id` and HFD orders use
    // `hfd_shipment_number`, none of which the original check covered,
    // causing legitimate paid orders to flash a red "לא רכש" banner).
    if (order.shipping_provider || order.tracking_number || order.shipment_number) return false;
    if (order.pickup_provider || order.pickup_point_id) return false;
    if (order.hfd_shipment_number) return false;
    if (order.label_url || order.shipping_label_url) return false;
    // CardCom verification (June 2026): if we ran an explicit API check
    // against CardCom and it confirmed the transaction succeeded, the
    // order is paid no matter what other signals say. `cardcom_verified`
    // is set by /api/admin/orders/{id}/verify-cardcom.
    if (order.cardcom_verified === true) return false;
    // Only now do we mark as abandoned — credit_card/bit that's still
    // waiting for callback AND has none of the above success signals.
    if ((order.payment_method === 'credit_card' || order.payment_method === 'bit')
        && order.payment_status === 'pending') return true;
    return false;
  };

  const markAsPaid = async (order) => {
    try {
      await axios.post(`${API}/admin/orders/${order.id}/mark-paid`);
      toast.success('ההזמנה סומנה כנרכשה');
      fetchOrders();
    } catch (error) {
      toast.error(`שגיאה: ${error.response?.data?.detail || error.message}`);
    }
  };

  const resendPaymentLink = async (order) => {
    try {
      toast.loading('יוצר קישור תשלום חדש...', { id: `pay-${order.id}` });
      const resp = await axios.post(`${API}/admin/orders/${order.id}/resend-payment`);
      toast.dismiss(`pay-${order.id}`);
      if (resp.data?.success && resp.data.payment_url) {
        // Copy to clipboard + open in new tab
        try { await navigator.clipboard.writeText(resp.data.payment_url); } catch (_e) {}
        window.open(resp.data.payment_url, '_blank', 'noopener');
        toast.success('הקישור נפתח והועתק - ניתן לשלוח ללקוח');
      } else {
        toast.error('לא נוצר קישור');
      }
    } catch (error) {
      toast.dismiss(`pay-${order.id}`);
      toast.error(`שגיאה: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUsername('');
    setPassword('');
    setOrders([]);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
      case 'processing':
        return <Package className="w-5 h-5 text-blue-500" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
      case 'processing':
        return 'בטיפול';
      case 'completed':
        return 'הושלם';
      case 'cancelled':
        return 'בוטל';
      default:
        return status;
    }
  };

  const getPaymentMethodText = (method) => {
    switch (method) {
      case 'credit_card':
        return 'כרטיס אשראי';
      case 'paypal':
        return 'PayPal';
      case 'paybox':
        return 'פייבוקס';
      case 'bit':
        return 'Bit';
      case 'cash':
        return 'מזומן';
      case 'private':
        return 'נשלח ע"י ברי';
      case 'sent_bari':
        return 'נשלח ע"י ברי';
      case 'sent_liran':
        return 'נשלח ע"י לירן';
      case 'sent_yaniv':
        return 'נשלח ע"י יניב';
      case 'shipped':
        return 'נשלח סחורה';
      case 'bank_transfer':
        return 'העברה בנקאית';
      case 'not_purchased':
        return 'לא רכש - לחזור אליו';
      case 'returned':
        return 'חזר משלוח';
      default:
        return method;
    }
  };

  const getUpsStatusStyle = (state) => {
    switch (state) {
      case 0: return 'bg-stone-100 text-stone-600';
      case 1: return 'bg-blue-100 text-blue-700';
      case 3: return 'bg-yellow-100 text-yellow-700';
      case 4: return 'bg-green-100 text-green-700';
      case 5: case 6: return 'bg-purple-100 text-purple-700';
      case 7: return 'bg-red-100 text-red-700';
      case 8: return 'bg-red-100 text-red-700';
      case 9: case 10: return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-stone-100 text-stone-500';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  // Admin Dashboard
  return (
    <div className="min-h-screen bg-stone-100" dir="rtl">
      {/* Sidebar Toggle Button */}
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="fixed top-20 right-3 z-50 bg-[#1a1a2e] text-white rounded-xl px-3 py-2 shadow-lg hover:bg-[#2a2a4e] transition-colors flex items-center gap-2 text-sm font-bold"
          data-testid="sidebar-open-btn"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          תפריט
        </button>
      )}

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      {sidebarOpen && (
        <AdminSidebar
          activeSection={activeSection}
          setActiveSection={(s) => { setActiveSection(s); setSidebarOpen(false); }}
          collapsed={false}
          setCollapsed={() => {}}
          orderCount={orders.filter(o => o.status === 'processing').length}
        />
      )}

      <div className="flex-1">
        {/* Section: Products */}
        {activeSection === 'products' && (
          <div className="max-w-5xl mx-auto p-6"><AdminProducts /></div>
        )}
        {/* Section: Customers */}
        {activeSection === 'customers' && (
          <div className="max-w-5xl mx-auto p-6"><AdminCustomers /></div>
        )}
        {/* Section: Filtered customer export */}
        {activeSection === 'customer_export' && (
          <div className="max-w-3xl mx-auto p-6" dir="rtl">
            <div className="bg-white rounded-2xl shadow-lg border border-stone-200 overflow-hidden">
              <div className="bg-gradient-to-l from-emerald-600 to-teal-500 p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <FileSpreadsheet className="w-7 h-7" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black">ייצוא לקוחות ל-Google Sheets</h2>
                    <p className="text-white/80 text-sm mt-1">סינון לקוחות לפי תאריך ואמצעי תשלום</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="space-y-2">
                    <span className="text-sm font-bold text-stone-700">מתאריך</span>
                    <input type="date" value={exportDateFrom} onChange={e => setExportDateFrom(e.target.value)} className="w-full border border-stone-300 rounded-xl px-4 py-3 bg-white" />
                  </label>
                  <label className="space-y-2">
                    <span className="text-sm font-bold text-stone-700">עד תאריך</span>
                    <input type="date" value={exportDateTo} onChange={e => setExportDateTo(e.target.value)} className="w-full border border-stone-300 rounded-xl px-4 py-3 bg-white" />
                  </label>
                </div>
                <label className="space-y-2 block">
                  <span className="text-sm font-bold text-stone-700">אופן תשלום</span>
                  <select value={exportPaymentMethod} onChange={e => setExportPaymentMethod(e.target.value)} className="w-full border border-stone-300 rounded-xl px-4 py-3 bg-white">
                    <option value="all">כל אמצעי התשלום</option>
                    <option value="cash">מזומן</option>
                    <option value="credit_card">כרטיס אשראי</option>
                    <option value="bit">Bit</option>
                    <option value="paypal">PayPal</option>
                    <option value="paybox">PayBox</option>
                    <option value="bank_transfer">העברה בנקאית</option>
                    <option value="private">נשלח על ידי ברי</option>
                  </select>
                </label>
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900">
                  הקובץ יכיל עמודות שם וטלפון בלבד. מספרים כפולים יאוחדו אוטומטית.
                </div>
                <button onClick={exportCustomersToExcel} disabled={exportLoading} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-black py-3.5 flex items-center justify-center gap-2">
                  {exportLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                  {exportLoading ? 'מכין את הקובץ...' : 'הורד ל-Google Sheets (CSV)'}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Section: Shipping */}
        {activeSection === 'shipping' && (
          <div className="max-w-5xl mx-auto p-6"><AdminShipping /></div>
        )}
        {/* Section: WhatsApp */}
        {activeSection === 'whatsapp' && (
          <div className="max-w-5xl mx-auto p-6"><AdminWhatsApp /></div>
        )}
        {/* Section: AI Personal Assistant */}
        {activeSection === 'assistant' && (
          <div className="max-w-5xl mx-auto p-6"><AdminAssistant /></div>
        )}
        {/* Section: Site Editor Chat */}
        {activeSection === 'site_editor' && (
          <div className="max-w-5xl mx-auto p-6"><SiteEditorChat /></div>
        )}
        {/* Section: Coupons */}
        {activeSection === 'coupons' && (
          <div className="max-w-5xl mx-auto p-6"><AdminCoupons /></div>
        )}
        {/* Section: Banner */}
        {activeSection === 'banner' && (
          <div className="max-w-3xl mx-auto p-6"><AdminBanner /></div>
        )}
        {/* Section: Backup */}
        {activeSection === 'backup' && (
          <div className="max-w-3xl mx-auto p-6"><AdminBackup /></div>
        )}
        {/* Section: WaSender */}
        {activeSection === 'wasender' && <WaBotApp />}
        {/* Section: Settings */}
        {activeSection === 'settings' && (
          <div className="max-w-5xl mx-auto p-6"><AdminSettings /></div>
        )}
        {/* Section: Dashboard / Orders / Facebook / Financial / Analytics */}
        {(activeSection === 'orders' || activeSection === 'dashboard' || activeSection === 'facebook' || activeSection === 'financial' || activeSection === 'analytics') && (
    <div className="min-h-screen bg-stone-100">
      {/* Header - full-width, menu/refresh buttons on the LEFT. */}
      <header className="bg-[#2C3E2D] text-white py-4 px-6 shadow-lg">
        <div className="w-full flex items-center justify-between">
          {/* LEFT side: refresh + menu (layout flips naturally in RTL) */}
          <button
            onClick={() => { fetchOrders(); axios.get(`${API}/admin/clicks`).then(r => setClickStats(r.data)).catch(() => {}); }}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            data-testid="refresh-btn"
          >
            <Package className="w-5 h-5" />
            <span>רענן</span>
          </button>
          <div className="flex items-center gap-3">
            <Truck className="w-8 h-8 text-[#D97706]" />
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Heebo, sans-serif' }}>
              ניהול הזמנות
            </h1>
          </div>
        </div>
      </header>

      {/* Main content - full-width, no container narrowing. */}
      <div className="w-full p-6">
        {/* Row 1: Order Stats by Payment Method */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          {/* Month picker card - shows "אפריל 2026" with prev/next arrows.
              Updates `finDateFrom`/`finDateTo` which cascades to the
              daily calendar, Facebook ads stats, and every other card
              on the page. Replaces the previous "סה"כ הזמנות" tile on
              the right; the full-totals summary still appears on the
              left-most card. */}
          {/* Rightmost column: shrunk month-picker (top) + team-income card
              (bottom). Stacked to free up half the original height. */}
          <div className="flex flex-col gap-2" data-testid="col-month-team">
          {(() => {
            const HEB_MONTHS = ['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר'];
            const cur = new Date(finDateFrom || todayStr);
            const y = cur.getFullYear();
            const m = cur.getMonth();
            const setMonth = (year, month) => {
              const firstDay = `${year}-${String(month+1).padStart(2,'0')}-01`;
              const last = new Date(year, month+1, 0);
              const lastDay = `${year}-${String(month+1).padStart(2,'0')}-${String(last.getDate()).padStart(2,'0')}`;
              // Sync BOTH the financial date range AND the orders-list date
              // range so all cards, the daily calendar, FB stats, and the
              // orders table update to the selected month in one click.
              setFinDateFrom(firstDay);
              setFinDateTo(lastDay);
              setDateFrom(firstDay);
              setDateTo(lastDay);
            };
            const prev = () => { const d = new Date(y, m-1, 1); setMonth(d.getFullYear(), d.getMonth()); };
            const next = () => { const d = new Date(y, m+1, 1); setMonth(d.getFullYear(), d.getMonth()); };
            return (
              <div
                className="bg-gradient-to-br from-[#D97706] to-[#b86205] rounded-xl p-2 shadow-sm text-white border-2 border-amber-300 flex items-center justify-between gap-2"
                data-testid="month-picker-card"
              >
                <button
                  type="button"
                  onClick={prev}
                  className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 active:scale-95 flex items-center justify-center transition-all flex-shrink-0"
                  data-testid="month-picker-prev"
                  aria-label="חודש קודם"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <div className="flex-1 text-center">
                  <p className="text-[10px] font-medium text-white/75 leading-none">תקופה</p>
                  <p className="text-sm font-black leading-tight mt-0.5" data-testid="month-picker-label">{HEB_MONTHS[m]} {y}</p>
                  <select
                    value={`${y}-${m}`}
                    onChange={(e) => { const [yy, mm] = e.target.value.split('-').map(Number); setMonth(yy, mm); }}
                    className="bg-white/15 hover:bg-white/25 text-white text-[9px] font-bold rounded px-1 py-0 mt-0.5 border border-white/20 cursor-pointer"
                    data-testid="month-picker-select"
                    aria-label="בחר חודש ושנה"
                  >
                    {[y-1, y, y+1].map(yr => (
                      HEB_MONTHS.map((mn, mi) => (
                        <option key={`${yr}-${mi}`} value={`${yr}-${mi}`} style={{color: '#000'}}>{mn} {yr}</option>
                      ))
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={next}
                  className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 active:scale-95 flex items-center justify-center transition-all flex-shrink-0"
                  data-testid="month-picker-next"
                  aria-label="חודש הבא"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              </div>
            );
          })()}
          {/* ============================================================
              SINGLE CARD — "כל ההכנסות שהושלמו"
              June 2026 user spec: only ONE revenue card on this row. It
              shows completed-only revenue broken down by payment method
              (אשראי / מזומן / ביט / העברה בנקאית), filtered by the chosen
              month. The previous team/credit/bit/external-cash cards are
              all merged here so totals never double-count.
              ============================================================ */}
          {(() => {
            const allCompleted = filteredByDate.filter(o => o.status === 'completed');
            // Pending = orders with revenue value not yet collected (NOT
            // completed and NOT cancelled). Breakdown by payment method so
            // we can show "what's still owed in this category".
            const allPending = filteredByDate.filter(o => o.status !== 'completed' && o.status !== 'cancelled');
            const CASH_LIKE = ['cash','private','sent_yaniv','sent_liran','sent_bari','sent_daniel'];
            const byMethod = (matcher) => allCompleted.filter(matcher);
            const pendingByMethod = (matcher) => allPending.filter(matcher);
            const sumGross = (arr) => arr.reduce((s, o) => s + (o.total_amount || 0), 0);
            const cashOrders = byMethod(o => CASH_LIKE.includes(o.payment_method));
            const ccOrders = byMethod(o => o.payment_method === 'credit_card');
            const bitOrders = byMethod(o => o.payment_method === 'bit');
            const bankOrders = byMethod(o => o.payment_method === 'bank_transfer');
            const cashPending = pendingByMethod(o => CASH_LIKE.includes(o.payment_method));
            const ccPending = pendingByMethod(o => o.payment_method === 'credit_card');
            const bitPending = pendingByMethod(o => o.payment_method === 'bit');
            const bankPending = pendingByMethod(o => o.payment_method === 'bank_transfer');
            const totalGross = sumGross(allCompleted);
            // Tiny helper: "(טרם: X ₪)" line under each method total, only
            // when there's something pending in that category. Reads
            // smoothly in Hebrew, kept on its own line, slightly muted.
            const pendingLine = (arr) => arr.length > 0 ? (
              <p className="text-[10px] text-amber-700 font-bold mt-0.5">
                (טרם התקבל: {sumGross(arr).toLocaleString()} ₪ · {arr.length})
              </p>
            ) : null;
            return (
              <div
                onClick={() => handleActiveFilter('all_completed')}
                className={`bg-white rounded-xl p-4 shadow-md cursor-pointer transition-all duration-200 border-2 col-span-2 ${activeFilter === 'all_completed' ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-emerald-300 hover:border-emerald-400'}`}
                data-testid="all-completed-card"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-emerald-900 leading-none" data-testid="all-completed-count">{allCompleted.length}</p>
                    <p className="text-emerald-700 text-sm font-bold mt-0.5">כל ההכנסות שהושלמו</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5 mt-2 text-[11px]">
                  <div className="bg-blue-50 rounded p-1.5">
                    <div className="flex items-center gap-1 mb-0.5">
                      <CreditCard className="w-3 h-3 text-blue-600" />
                      <span className="font-bold text-blue-700">אשראי <span className="text-stone-400 font-normal">({ccOrders.length})</span></span>
                    </div>
                    <strong className="text-blue-900 text-sm" data-testid="all-completed-credit">{sumGross(ccOrders).toLocaleString()} ₪</strong>
                    {pendingLine(ccPending)}
                  </div>
                  <div className="bg-green-50 rounded p-1.5">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Banknote className="w-3 h-3 text-green-600" />
                      <span className="font-bold text-green-700">מזומן <span className="text-stone-400 font-normal">({cashOrders.length})</span></span>
                    </div>
                    <strong className="text-green-900 text-sm" data-testid="all-completed-cash">{sumGross(cashOrders).toLocaleString()} ₪</strong>
                    {pendingLine(cashPending)}
                  </div>
                  <div className="bg-cyan-50 rounded p-1.5">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Receipt className="w-3 h-3 text-cyan-600" />
                      <span className="font-bold text-cyan-700">Bit <span className="text-stone-400 font-normal">({bitOrders.length})</span></span>
                    </div>
                    <strong className="text-cyan-900 text-sm" data-testid="all-completed-bit">{sumGross(bitOrders).toLocaleString()} ₪</strong>
                    {pendingLine(bitPending)}
                  </div>
                  <div className="bg-indigo-50 rounded p-1.5">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Receipt className="w-3 h-3 text-indigo-600" />
                      <span className="font-bold text-indigo-700">בנקאית <span className="text-stone-400 font-normal">({bankOrders.length})</span></span>
                    </div>
                    <strong className="text-indigo-900 text-sm" data-testid="all-completed-bank">{sumGross(bankOrders).toLocaleString()} ₪</strong>
                    {pendingLine(bankPending)}
                  </div>
                </div>
                <p className="text-emerald-900 font-black text-lg mt-2 pt-2 border-t-2 border-emerald-200 text-center" data-testid="all-completed-total">סה״כ: {totalGross.toLocaleString()} ש"ח</p>
              </div>
            );
          })()}
          </div>

          {/* חזרו — הקובייה הוסרה. עלות המשלוח של הזמנות שחזרו (60₪ × N)
              מצטרפת אוטומטית לסה״כ המשלוחים בקובייה השמאלית (סה״כ הזמנות). */}

          {/* ============================================================
              EXPENSES CARD — single tile showing the 3 expense lines:
                א. פרסום (FB ad spend)   - from fbStats.this_month.spend
                ב. מוצרים (cost of goods) - sum of calcProductCost on completed orders
                ג. משלוחים               - shipping costs by tier
              ============================================================ */}
          {(() => {
            const completedOfMonth = filteredByDate.filter(o => o.status === 'completed');
            const productCost = completedOfMonth.reduce((s, o) => s + calcProductCost(o), 0);
            const shipCost = shippingBreakdown.total;
            const adsCost = fbStats ? Math.round(fbStats.this_month?.spend || 0) : 0;
            // Shipping breakdown by category (for collapsible detail view).
            // Mirrors the categorisation in the left-most card so numbers
            // reconcile exactly.
            const shipDetail = { pickup:{n:0,sum:0}, home:{n:0,sum:0}, yaniv:{n:0,sum:0}, liran:{n:0,sum:0}, bari:{n:0,sum:0}, self:{n:0,sum:0} };
            completedOfMonth.forEach(o => {
              if (o.private_shipment) return;
              const cat = getShippingCategory(o);
              if (cat === 'team_bari')      { shipDetail.bari.n++; }
              else if (cat === 'team_yaniv'){ shipDetail.yaniv.n++; shipDetail.yaniv.sum += 40; }
              else if (cat === 'team_liran'){ shipDetail.liran.n++; shipDetail.liran.sum += 40; }
              else if (cat === 'pickup')    { shipDetail.pickup.n++; shipDetail.pickup.sum += 20; }
              else if (cat === 'self_pickup'){ shipDetail.self.n++; }
              else                          { shipDetail.home.n++; shipDetail.home.sum += 70; }
            });
            // Product breakdown by NAME — groups items across all completed
            // orders. Each unit costs PRODUCT_UNIT_COST (60₪) per user
            // spec. Legacy orders without `items[]` get a synthetic
            // "מוצר" row with 1 unit.
            const productDetail = {};
            completedOfMonth.forEach(o => {
              const items = Array.isArray(o.items) ? o.items : [];
              if (items.length === 0) {
                const k = 'מוצר';
                productDetail[k] = productDetail[k] || { qty: 0, sum: 0 };
                productDetail[k].qty += 1;
                productDetail[k].sum += PRODUCT_UNIT_COST;
              } else {
                items.forEach(it => {
                  const k = (it.product_name || 'מוצר').trim();
                  const q = parseInt(it.quantity, 10) || 1;
                  productDetail[k] = productDetail[k] || { qty: 0, sum: 0 };
                  productDetail[k].qty += q;
                  productDetail[k].sum += PRODUCT_UNIT_COST * q;
                });
              }
            });
            const productDetailEntries = Object.entries(productDetail).sort((a, b) => b[1].sum - a[1].sum);
            // Fixed monthly expenses ("הוצאות קבועות") — partner-level recurring
            // costs the user has every month regardless of sales volume.
            const FIXED_EXPENSES = [
              { name: 'רואה חשבון', amount: 550 },
              { name: 'משרד',        amount: 750 },
              { name: 'קארדקום',     amount: 250 },
              { name: 'UPAY',        amount: 200 },
              { name: 'פלאפון',      amount: 200 },
            ];
            const fixedTotal = FIXED_EXPENSES.reduce((s, e) => s + e.amount, 0);
            const variableTotal = Math.round(productCost + shipCost + adsCost);
            const totalExpenses = Math.round(variableTotal + fixedTotal);
            return (
              <div className="bg-white rounded-xl p-4 shadow-md border-2 border-red-300" data-testid="expenses-card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-red-700 text-sm font-bold leading-none">הוצאות החודש</p>
                    <p className="text-3xl font-black text-red-900 mt-1" data-testid="expenses-total">{totalExpenses.toLocaleString()} ₪</p>
                  </div>
                </div>
                <div className="space-y-1.5 mt-3 text-[11px]">
                  <div className="flex justify-between items-center bg-red-50 rounded p-1.5">
                    <span className="font-bold text-red-700">📣 פרסום (פייסבוק)</span>
                    <strong className="text-red-900" data-testid="expenses-ads">{adsCost.toLocaleString()} ₪</strong>
                  </div>
                  {/* PRODUCTS — collapsible. Closed: just total. Open:
                      per-product breakdown (qty × 60₪ each). */}
                  <div className="bg-red-50 rounded" data-testid="expenses-products-block">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setProductBreakdownOpen(v => !v); }}
                      className="w-full flex justify-between items-center p-1.5 hover:bg-red-100 transition-colors rounded"
                      data-testid="expenses-products-toggle"
                    >
                      <span className="font-bold text-red-700 flex items-center gap-1">
                        <span className={`inline-block transition-transform ${productBreakdownOpen ? 'rotate-180' : ''}`}>▼</span>
                        📦 מוצרים
                      </span>
                      <strong className="text-red-900" data-testid="expenses-products">{Math.round(productCost).toLocaleString()} ₪</strong>
                    </button>
                    {productBreakdownOpen && (
                      <div className="px-2 pb-1.5 space-y-0.5" data-testid="expenses-products-detail">
                        {productDetailEntries.length === 0 ? (
                          <p className="text-[10px] text-red-400 italic">אין מוצרים בחודש זה</p>
                        ) : productDetailEntries.map(([name, info]) => (
                          <div key={name} className="flex justify-between items-center text-[10px] py-0.5">
                            <span className="text-red-600 flex-1 truncate" title={name}>• {name} <span className="text-stone-400">({info.qty})</span></span>
                            <strong className="text-red-800">{info.sum.toLocaleString()} ₪</strong>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* SHIPPING — collapsible. Closed: just total. Open:
                      per-tier breakdown matching the left card. */}
                  <div className="bg-red-50 rounded" data-testid="expenses-shipping-block">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShipBreakdownOpen(v => !v); }}
                      className="w-full flex justify-between items-center p-1.5 hover:bg-red-100 transition-colors rounded"
                      data-testid="expenses-shipping-toggle"
                    >
                      <span className="font-bold text-red-700 flex items-center gap-1">
                        <span className={`inline-block transition-transform ${shipBreakdownOpen ? 'rotate-180' : ''}`}>▼</span>
                        🚚 משלוחים
                      </span>
                      <strong className="text-red-900" data-testid="expenses-shipping">{Math.round(shipCost).toLocaleString()} ₪</strong>
                    </button>
                    {shipBreakdownOpen && (
                      <div className="px-2 pb-1.5 space-y-0.5" data-testid="expenses-shipping-detail">
                        {shipDetail.pickup.n > 0 && (
                          <div className="flex justify-between text-[10px] py-0.5"><span className="text-red-600">• נקודת איסוף <span className="text-stone-400">({shipDetail.pickup.n}×20)</span></span><strong className="text-red-800">{shipDetail.pickup.sum.toLocaleString()} ₪</strong></div>
                        )}
                        {shipDetail.home.n > 0 && (
                          <div className="flex justify-between text-[10px] py-0.5"><span className="text-red-600">• עד הבית <span className="text-stone-400">({shipDetail.home.n}×70)</span></span><strong className="text-red-800">{shipDetail.home.sum.toLocaleString()} ₪</strong></div>
                        )}
                        {shipDetail.yaniv.n > 0 && (
                          <div className="flex justify-between text-[10px] py-0.5"><span className="text-red-600">• נשלח ע"י יניב <span className="text-stone-400">({shipDetail.yaniv.n}×40)</span></span><strong className="text-red-800">{shipDetail.yaniv.sum.toLocaleString()} ₪</strong></div>
                        )}
                        {shipDetail.liran.n > 0 && (
                          <div className="flex justify-between text-[10px] py-0.5"><span className="text-red-600">• נשלח ע"י לירן <span className="text-stone-400">({shipDetail.liran.n}×40)</span></span><strong className="text-red-800">{shipDetail.liran.sum.toLocaleString()} ₪</strong></div>
                        )}
                        {shipDetail.bari.n > 0 && (
                          <div className="flex justify-between text-[10px] py-0.5"><span className="text-red-600">• נשלח ע"י ברי <span className="text-stone-400">({shipDetail.bari.n})</span></span><strong className="text-stone-400">0 ₪</strong></div>
                        )}
                        {shipDetail.self.n > 0 && (
                          <div className="flex justify-between text-[10px] py-0.5"><span className="text-red-600">• איסוף עצמי <span className="text-stone-400">({shipDetail.self.n})</span></span><strong className="text-stone-400">0 ₪</strong></div>
                        )}
                        {Object.values(shipDetail).every(d => d.n === 0) && (
                          <p className="text-[10px] text-red-400 italic">אין משלוחים בחודש זה</p>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Fixed expenses block — visually separated. */}
                  <div className="mt-2 pt-2 border-t-2 border-dashed border-red-200" data-testid="expenses-fixed-block">
                    <p className="text-[10px] font-bold text-red-700 mb-1">🔒 הוצאות קבועות</p>
                    {FIXED_EXPENSES.map(exp => (
                      <div key={exp.name} className="flex justify-between items-center bg-red-50/60 rounded px-1.5 py-1 mb-0.5" data-testid={`expense-fixed-${exp.name}`}>
                        <span className="text-red-700">• {exp.name}</span>
                        <strong className="text-red-900">{exp.amount.toLocaleString()} ₪</strong>
                      </div>
                    ))}
                    <div className="flex justify-between items-center bg-red-100 rounded px-1.5 py-1 mt-1">
                      <span className="font-bold text-red-700 text-[11px]">סה״כ קבועות</span>
                      <strong className="text-red-900 text-[12px]" data-testid="expenses-fixed-total">{fixedTotal.toLocaleString()} ₪</strong>
                    </div>
                  </div>
                  <div className="flex justify-between items-center bg-red-200 rounded p-1.5 mt-2 border-t-2 border-red-400">
                    <span className="font-black text-red-800 text-[12px]">סה״כ הכל</span>
                    <strong className="text-red-900 text-base" data-testid="expenses-grand-total">{totalExpenses.toLocaleString()} ₪</strong>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ============================================================
              "סיכום חודשי" card was REMOVED per user spec (June 2026) —
              the same summary already lives in the left-most white card
              ("סה״כ הזמנות" → "נטו בסיכום" + "טרם שולם"). Keeping the
              tile would have been duplicate info.
              ============================================================ */}

          {/* פרטי - left-most card: SIMPLIFIED (per user spec, June 2026).
              Shows only 4 rows: הוצאות, הכנסות, סה"כ (= net), טרם יתקבל.
              All detailed shipping/product breakdowns moved out — the red
              expenses card now owns the cost breakdown (collapsible). */}
          {(() => {
            const completedOfMonth = filteredByDate.filter(o => o.status === 'completed');
            const productCost = completedOfMonth.reduce((s, o) => s + calcProductCost(o), 0);
            const shipCost = shippingBreakdown.total;
            const adCost = Math.round(fbStats?.period?.spend || fbStats?.this_month?.spend || 0);
            // Same fixed expenses constant as the red card. Kept inline
            // to avoid a config round-trip; future improvement is a
            // /api/admin/fixed-expenses endpoint.
            const FIXED_EXPENSES_TOTAL = 550 + 750 + 250 + 200 + 200;
            const expensesTotal = Math.round(productCost + shipCost + adCost + FIXED_EXPENSES_TOTAL);
            const completedRevenue = sumRevenue(completedOfMonth);
            const net = Math.round(completedRevenue - expensesTotal);
            const pending = filteredByDate.filter(o => o.status === 'processing');
            const pendingRevenue = pending.reduce((s, o) => s + (o.total_amount || 0), 0);
            return (
              <div
                onClick={() => handleActiveFilter('private')}
                className={`bg-white rounded-xl p-3 shadow-sm cursor-pointer transition-all duration-200 border-2 ${activeFilter === 'private' ? 'border-[#D97706] ring-2 ring-[#D97706]/20' : 'border-stone-200 hover:border-[#D97706]/30'}`}
                data-testid="filter-total"
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="w-8 h-8 bg-[#D97706]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-4 h-4 text-[#D97706]" />
                  </div>
                  <p className="text-xl font-bold text-[#2C3E2D]">{filteredByDate.length}</p>
                </div>
                <p className="text-stone-600 text-xs font-medium" style={{ fontFamily: 'Heebo, sans-serif' }}>סה"כ הזמנות</p>
                {/* Compact 4-row summary. Each row sits on its own coloured
                    band so they read instantly at-a-glance:
                      red    = הוצאות
                      green  = הכנסות
                      yellow = נטו (סה"כ)
                      amber  = טרם יתקבל
                */}
                <div className="mt-2 space-y-1" data-testid="summary-rows">
                  <div className="flex items-center justify-between bg-red-50 rounded px-2 py-1.5" data-testid="row-expenses">
                    <span className="text-[11px] font-bold text-red-700">הוצאות</span>
                    <strong className="text-red-700 text-sm">-{expensesTotal.toLocaleString()} ₪</strong>
                  </div>
                  <div className="flex items-center justify-between bg-emerald-50 rounded px-2 py-1.5" data-testid="row-revenue">
                    <span className="text-[11px] font-bold text-emerald-700">הכנסות</span>
                    <strong className="text-emerald-700 text-sm">{completedRevenue.toLocaleString()} ₪</strong>
                  </div>
                  <div className={`flex items-center justify-between rounded px-2 py-2 border-t-2 ${net >= 0 ? 'bg-green-100 border-green-400' : 'bg-red-100 border-red-400'}`} data-testid="row-net">
                    <span className="text-xs font-black text-stone-800">סה״כ</span>
                    <strong className={`text-lg font-black ${net >= 0 ? 'text-green-800' : 'text-red-700'}`} data-testid="net-profit-value">{net.toLocaleString()} ₪</strong>
                  </div>
                  <div className="flex items-center justify-between bg-amber-50 rounded px-2 py-1.5" data-testid="row-pending">
                    <span className="text-[11px] font-bold text-amber-700">טרם יתקבל <span className="font-normal opacity-70">({pending.length})</span></span>
                    <strong className="text-amber-700 text-sm" data-testid="pending-net-value">{pendingRevenue.toLocaleString()} ₪</strong>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* AI (GPT) key management — status, live validation, replace key */}
        <AiKeyCard />

        {/* Click Tracking Card — collapsible.
            5 launchable share-links: edit URL → press "שגר" → backend
            tracks visits to /api/go/{slot_id} and redirects. */}
        <div className="bg-gradient-to-br from-[#351C15] to-[#4a2a1f] rounded-xl shadow-sm mb-4 text-white overflow-hidden" data-testid="click-tracking-card">
          {(() => {
            const active = clickSlots.find(s => s.id === activeSlotId) || { id: activeSlotId, target_url: '', active: false, stats: { total: 0, total_raw: 0, today: 0, last_7_days: [] } };
            const stats = active.stats || { total: 0, total_raw: 0, today: 0, last_7_days: [] };
            const launchedCount = clickSlots.filter(s => s.active).length;
            // Use the SAME origin as the website (menshealth.co.il)
            // instead of the backend host (intimate-care-pro-1.emergent.host).
            // Reasons: nicer-looking link for sharing on FB/IG, faster
            // (no separate DNS), and avoids the cross-domain reliability
            // issue the user reported (clicks weren't being counted on
            // some networks). k8s ingress routes /api/* to the backend
            // anyway so the recording side still works.
            const trackUrl = `${window.location.origin}/api/go/${active.id}`;
            const editedUrl = slotEdits[active.id] !== undefined ? slotEdits[active.id] : (active.target_url || '');
            const dirty = editedUrl !== (active.target_url || '');
            const launchSlot = async () => {
              const url = (editedUrl || '').trim();
              if (!url) { toast.error('הכנס דומיין/קישור לפני השיגור'); return; }
              if (url.includes('/api/go/')) {
                toast.error('זה קישור מעקב — לא יעד! כאן מזינים את הדף שאליו הגולש יגיע (למשל menshealth.co.il/products)', { duration: 6000 });
                return;
              }
              try {
                const r = await axios.put(`${API}/admin/click-slots/${active.id}`, { target_url: url, active: true });
                setClickSlots(prev => prev.map(s => s.id === active.id ? r.data : s));
                setSlotEdits(prev => { const n = { ...prev }; delete n[active.id]; return n; });
                toast.success('שוגר ✓ הקישור פעיל');
              } catch (e) {
                toast.error('שגיאה בשיגור');
              }
            };
            const stopSlot = async () => {
              try {
                const r = await axios.put(`${API}/admin/click-slots/${active.id}`, { active: false });
                setClickSlots(prev => prev.map(s => s.id === active.id ? r.data : s));
                toast.success('הסלוט הושבת');
              } catch (e) {
                toast.error('שגיאה בהשבתה');
              }
            };
            const copyTrack = () => {
              navigator.clipboard.writeText(trackUrl);
              toast.success(`הועתק: ${trackUrl}`, { duration: 1800 });
            };
            return (
              <>
                <button
                  type="button"
                  onClick={() => setShowClickTracking(v => !v)}
                  className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
                  data-testid="toggle-click-tracking"
                >
                  <div className="flex items-center gap-2">
                    <MousePointerClick className="w-5 h-5 text-[#FFB500]" />
                    <h3 className="font-bold text-sm" style={{ fontFamily: 'Heebo, sans-serif' }}>מעקב כניסות לאתר</h3>
                    <span className="text-xs text-stone-300">({launchedCount}/{clickSlots.length || 5} פעילים)</span>
                  </div>
                  <ChevronLeft className={`w-5 h-5 transition-transform ${showClickTracking ? '-rotate-90' : 'rotate-0'}`} />
                </button>
                {showClickTracking && (
                  <div className="p-4 pt-0">
                    {/* Tabs row: 5 slots */}
                    <div className="flex items-center gap-1.5 mb-3 overflow-x-auto" data-testid="click-slot-tabs">
                      {(clickSlots.length ? clickSlots : [1,2,3,4,5].map(id => ({ id, active: false, target_url: '', stats: { total: 0 } }))).map(s => {
                        const isActive = s.id === activeSlotId;
                        const liveDot = s.active ? 'bg-emerald-400' : 'bg-stone-500';
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => setActiveSlotId(s.id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap max-w-[160px] ${isActive ? 'bg-[#FFB500] text-[#351C15]' : 'bg-white/10 text-white hover:bg-white/20'}`}
                            data-testid={`click-slot-tab-${s.id}`}
                          >
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${liveDot}`} />
                            <span className="truncate">{(s.name && s.name.trim()) || `קישור ${s.id}`}</span>
                            <span className={`text-[10px] ${isActive ? 'text-[#351C15]/70' : 'text-stone-300'}`}>({s.stats?.total_raw || s.stats?.total || 0})</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Campaign name editor — replaces the generic
                        "קישור 1/2/3" labels with whatever name the
                        admin gives the campaign. Saved on blur or
                        Enter so each keystroke doesn't hit the API. */}
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-xs text-stone-300 whitespace-nowrap">שם קמפיין:</label>
                      <input
                        type="text"
                        value={slotNameEdits[active.id] !== undefined ? slotNameEdits[active.id] : (active.name || '')}
                        onChange={(e) => setSlotNameEdits(prev => ({ ...prev, [active.id]: e.target.value }))}
                        onBlur={async () => {
                          const nameVal = slotNameEdits[active.id];
                          if (nameVal === undefined) return;
                          if (nameVal === (active.name || '')) {
                            setSlotNameEdits(prev => { const n = { ...prev }; delete n[active.id]; return n; });
                            return;
                          }
                          try {
                            const r = await axios.put(`${API}/admin/click-slots/${active.id}`, { name: nameVal });
                            setClickSlots(prev => prev.map(s => s.id === active.id ? r.data : s));
                            setSlotNameEdits(prev => { const n = { ...prev }; delete n[active.id]; return n; });
                            toast.success('שם נשמר ✓', { duration: 1500 });
                          } catch (_e) { toast.error('שגיאה בשמירה'); }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        placeholder="למשל: פייסבוק 1.5.26 / סטורי באינסטגרם / רימרקטינג"
                        maxLength={60}
                        className="flex-1 bg-white/10 text-white placeholder:text-white/40 border border-white/20 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[#FFB500]"
                        data-testid={`click-slot-name-input-${active.id}`}
                      />
                    </div>

                    {/* URL editor + שגר button */}
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <input
                        type="text"
                        value={editedUrl}
                        onChange={(e) => setSlotEdits(prev => ({ ...prev, [active.id]: e.target.value }))}
                        placeholder="הכנס דומיין יעד (למשל menshealth.co.il/promo)"
                        dir="ltr"
                        className="flex-1 min-w-[200px] bg-white/10 text-white placeholder:text-white/40 border border-white/20 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FFB500]"
                        data-testid={`click-slot-url-input-${active.id}`}
                      />
                      <button
                        type="button"
                        onClick={launchSlot}
                        disabled={!editedUrl || (!dirty && active.active)}
                        className="bg-[#FFB500] hover:bg-[#e8a400] disabled:bg-stone-600 disabled:text-stone-400 text-[#351C15] font-bold px-4 py-2 rounded-lg text-sm transition-all"
                        data-testid={`click-slot-launch-${active.id}`}
                      >
                        {active.active && !dirty ? 'פעיל ✓' : (active.active ? 'עדכן ושגר' : 'שגר')}
                      </button>
                      {active.active && (
                        <button
                          type="button"
                          onClick={stopSlot}
                          className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-xs"
                          data-testid={`click-slot-stop-${active.id}`}
                        >
                          השבת
                        </button>
                      )}
                    </div>

                    {/* Copy trackable links */}
                    {(() => {
                      const t = (editedUrl || active.target_url || '').trim();
                      const badTarget = t.includes('/api/go/');
                      const directUrl = (t && !badTarget) ? `${t.startsWith('http') ? '' : 'https://'}${t}${t.includes('?') ? '&' : '?'}go=${active.id}` : '';
                      return (
                        <div className="space-y-2 mb-3">
                          {badTarget && (
                            <div className="bg-red-500/15 border border-red-400/40 rounded-lg px-3 py-2 text-xs text-red-200" data-testid={`click-slot-bad-target-${active.id}`}>
                              ⚠️ שמת בשדה היעד את קישור המעקב עצמו — זה יוצר לולאה. הזן את <b>דף היעד</b> שאליו הגולש צריך להגיע (למשל menshealth.co.il/products).
                            </div>
                          )}
                          {directUrl && (
                            <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-400/30 rounded-lg px-3 py-2">
                              <span className="text-xs text-emerald-300 whitespace-nowrap font-bold">הקישור שלך למעקב:</span>
                              <code className="flex-1 text-xs text-emerald-200 truncate" dir="ltr" data-testid={`click-slot-direct-url-${active.id}`}>{directUrl}</code>
                              <button
                                type="button"
                                onClick={() => { navigator.clipboard.writeText(directUrl); toast.success('הועתק ✓', { duration: 1500 }); }}
                                className="bg-emerald-500/20 hover:bg-emerald-500/30 text-white px-2.5 py-1 rounded text-[11px] whitespace-nowrap"
                                data-testid={`click-slot-copy-direct-${active.id}`}
                              >
                                העתק
                              </button>
                            </div>
                          )}
                          <div className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
                            <span className="text-xs text-stone-300 whitespace-nowrap">קישור הפניה (חלופי):</span>
                            <code className="flex-1 text-xs text-[#FFB500] truncate" dir="ltr" data-testid={`click-slot-track-url-${active.id}`}>{trackUrl}</code>
                            <button
                              type="button"
                              onClick={copyTrack}
                              className="bg-white/10 hover:bg-white/20 text-white px-2.5 py-1 rounded text-[11px] whitespace-nowrap"
                              data-testid={`click-slot-copy-${active.id}`}
                            >
                              העתק
                            </button>
                          </div>
                          <p className="text-[11px] text-stone-400 leading-relaxed">
                            שתף את <b className="text-emerald-300">"הקישור שלך למעקב"</b> — זו בדיוק הכתובת שהזנת למעלה עם תוספת מזהה (go={active.id}), וכל מי שנכנס דרכה נספר.
                            הקישור החלופי מעביר דרך המערכת ואז מפנה ליעד — מתאים גם לאתרים חיצוניים.
                          </p>
                        </div>
                      );
                    })()}

                    {/* Stats display (same structure as before, scoped to active slot) */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <MousePointerClick className="w-5 h-5 text-[#FFB500]" />
                        <h3 className="font-bold text-sm" style={{ fontFamily: 'Heebo, sans-serif' }}>
                          {(active.name && active.name.trim()) ? `פירוט: ${active.name}` : `פירוט קישור ${active.id}`}
                        </h3>
                        <button
                          type="button"
                          onClick={() => { refreshClickSlots(); toast.success('הנתונים עודכנו', { duration: 1200 }); }}
                          className="bg-white/10 hover:bg-white/20 text-white px-2.5 py-1 rounded text-[11px] whitespace-nowrap"
                          data-testid={`click-slot-refresh-${active.id}`}
                        >
                          ↻ רענן
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm('לאפס את המונה של הקישור הזה?')) return;
                            try {
                              const r = await axios.post(`${API}/admin/click-slots/${active.id}/reset`);
                              setClickSlots(prev => prev.map(s => s.id === active.id ? r.data : s));
                              toast.success('המונה אופס ✓');
                            } catch (_e) { toast.error('שגיאה באיפוס'); }
                          }}
                          className="bg-red-500/15 hover:bg-red-500/25 text-red-200 px-2.5 py-1 rounded text-[11px] whitespace-nowrap"
                          data-testid={`click-slot-reset-${active.id}`}
                        >
                          אפס מונה
                        </button>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-3xl font-black text-[#FFB500]" data-testid={`click-slot-unique-${active.id}`}>{stats.total || 0}</p>
                          <p className="text-xs text-stone-300">מבקרים ייחודיים</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xl font-bold text-white/70" data-testid={`click-slot-raw-${active.id}`}>{stats.total_raw || 0}</p>
                          <p className="text-xs text-stone-400">סה"כ כניסות</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="bg-white/10 rounded-lg px-3 py-1.5">
                        <span className="text-stone-300">חדשים היום: </span>
                        <span className="font-bold text-[#FFB500]" data-testid={`click-slot-today-${active.id}`}>{stats.today || 0}</span>
                      </div>
                      {stats.last_7_days && stats.last_7_days.length > 0 && (
                        <div className="flex items-end gap-1 flex-1 h-10">
                          {stats.last_7_days.slice().reverse().map((day, i) => {
                            const max = Math.max(...stats.last_7_days.map(d => d.count), 1);
                            const h = Math.max((day.count / max) * 100, 8);
                            return (
                              <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${day.date}: ${day.count} מבקרים חדשים`}>
                                <div className="w-full bg-[#FFB500] rounded-t" style={{ height: `${h}%`, minHeight: '3px' }} />
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="text-xs text-stone-400 text-left truncate max-w-[200px]" dir="ltr">
                        {active.target_url || '— לא הוגדר —'}
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Facebook Token Config Row - always visible */}
        <FbTokenConfig />

        {/* eHouse shipping integration config (collapsible) */}
        <div className="mt-2">
          <EhouseConfig />
        </div>

        {/* Facebook Ads Stats — collapsible */}
        {fbStats && (
          <div className={`bg-gradient-to-l ${fbStats.account?.is_blocked ? 'from-red-700 to-red-900 ring-4 ring-red-400/60' : 'from-[#1877F2] to-[#0D47A1]'} rounded-2xl shadow-lg text-white overflow-hidden mb-4 transition-all`} data-testid="fb-ads-widget">
            <button
              type="button"
              onClick={() => setShowFbStats(v => !v)}
              className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
              data-testid="toggle-fb-stats"
            >
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                <h3 className="font-bold text-sm" style={{ fontFamily: 'Heebo, sans-serif' }}>הוצאות פרסום פייסבוק</h3>
                {fbStats.account?.is_blocked && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-black bg-white text-red-700 animate-pulse" data-testid="fb-blocked-pill">
                    ⚠️ חשבון חסום
                  </span>
                )}
                {/* Month-to-date FB spend pill. Always shows the FULL current
                    calendar month (1st → today), plus the human-readable
                    month name and date range underneath. The `this_month`
                    field returned by /admin/fb-ads-stats uses FB's
                    `date_preset=this_month` which is exactly "1st of month
                    to today". */}
                <div className="flex flex-col items-start leading-tight">
                  <span className="text-xs text-blue-200 font-bold">
                    ₪{Math.round(fbStats.this_month.spend).toLocaleString()}
                  </span>
                  {(() => {
                    const now = new Date();
                    const monthHe = now.toLocaleDateString('he-IL', { month: 'long' });
                    const day = now.getDate();
                    const mm = String(now.getMonth() + 1).padStart(2, '0');
                    return (
                      <span className="text-[9px] text-blue-300/80">
                        {monthHe} · 01/{mm} - {String(day).padStart(2, '0')}/{mm}
                      </span>
                    );
                  })()}
                </div>
              </div>
              <ChevronLeft className={`w-5 h-5 transition-transform ${showFbStats ? '-rotate-90' : 'rotate-0'}`} />
            </button>
            {showFbStats && (
            <div className="p-4 pt-0">
            {/* Account-level alert banner — ALL CAPS RED. Shows when FB
                has blocked the entire ad account (payment failure, risk
                review, etc). At this point campaigns SHOW as ACTIVE but
                ads aren't running. */}
            {fbStats.account?.is_blocked && (
              <div className="bg-red-600 border-2 border-red-300 rounded-lg p-3 mb-3 animate-pulse" data-testid="fb-account-blocked-banner">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-black text-white text-sm">חשבון המודעות חסום!</p>
                    <p className="text-white/95 text-xs mt-1">{fbStats.account.status_label}</p>
                    {fbStats.account.disable_reason && (
                      <p className="text-red-100 text-[10px] mt-1">סיבה: {fbStats.account.disable_reason}</p>
                    )}
                    <a
                      href="https://www.facebook.com/adsmanager/manage/campaigns"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block mt-2 bg-white text-red-700 px-3 py-1 rounded-md text-xs font-bold hover:bg-red-50"
                      data-testid="fb-account-fix-link"
                    >
                      ⛔ פתח Ads Manager לתיקון →
                    </a>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between mb-3">
              <div />
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-3xl font-black text-yellow-300">₪{Math.round(fbStats.today.spend)}</p>
                  <p className="text-xs text-blue-200">הוצאה היום</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-white/80">₪{Math.round(fbStats.this_month.spend).toLocaleString()}</p>
                  <p className="text-xs text-blue-200">
                    {(() => {
                      const now = new Date();
                      const monthHe = now.toLocaleDateString('he-IL', { month: 'long' });
                      const day = now.getDate();
                      const mm = String(now.getMonth() + 1).padStart(2, '0');
                      return `${monthHe} · 01/${mm}-${String(day).padStart(2,'0')}/${mm}`;
                    })()}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="bg-white/10 rounded-lg px-3 py-1.5">
                <span className="text-blue-200">חשיפות היום: </span>
                <span className="font-bold text-yellow-300">{fbStats.today.impressions.toLocaleString()}</span>
              </div>
              <div className="bg-white/10 rounded-lg px-3 py-1.5">
                <span className="text-blue-200">לחיצות היום: </span>
                <span className="font-bold text-yellow-300">{fbStats.today.clicks}</span>
              </div>
              {fbStats.last_7_days && fbStats.last_7_days.length > 0 && (
                <div className="flex items-end gap-1 flex-1 h-10">
                  {fbStats.last_7_days.map((day, i) => {
                    const max = Math.max(...fbStats.last_7_days.map(d => d.spend), 1);
                    const h = Math.max((day.spend / max) * 100, 8);
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${day.date}: ₪${Math.round(day.spend)} | ${day.clicks} לחיצות`}>
                        <div className="w-full bg-yellow-300 rounded-t" style={{ height: `${h}%`, minHeight: '3px' }} />
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="text-xs text-blue-200">
                בתקופה: {(fbStats.period?.impressions || fbStats.this_month.impressions).toLocaleString()} חשיפות
              </div>
            </div>

            {/* Campaigns Section */}
            {fbCampaigns.length > 0 && (
              <div className="mt-3 border-t border-white/20 pt-3 space-y-2" data-testid="fb-campaigns-section">
                <p className="text-xs font-bold text-blue-200 mb-1">קמפיינים</p>
                {fbCampaigns.map(c => {
                  // Detect campaign issues (payment fail, disapproval,
                  // policy violation, budget exhausted, ad-account block).
                  // FB returns these via `effective_status` while the
                  // user-controlled `status` stays ACTIVE/PAUSED. We
                  // highlight the card in red and surface the reason so
                  // the admin can act on it immediately.
                  const eff = (c.effective_status || '').toUpperCase();
                  const ISSUE_MAP = {
                    'WITH_ISSUES': 'לקמפיין יש בעיה - בדוק במנהל המודעות',
                    'DISAPPROVED': 'הקמפיין נדחה - הפר את מדיניות פייסבוק',
                    'PENDING_REVIEW': 'בבדיקה ע"י פייסבוק',
                    'PREAPPROVED': 'אושר מראש - ממתין להפעלה',
                    'PENDING_BILLING_INFO': 'חסר פרטי תשלום!',
                    'CAMPAIGN_PAUSED': 'הקמפיין הושהה אוטומטית',
                    'ADSET_PAUSED': 'קבוצת המודעות הושהתה',
                    'IN_PROCESS': 'בעיבוד',
                    'ARCHIVED': 'הקמפיין בארכיון',
                    'DELETED': 'הקמפיין נמחק',
                  };
                  const issueReason = ISSUE_MAP[eff] || (c.status === 'ACTIVE' && eff !== 'ACTIVE' ? `סטטוס לא תקין: ${eff}` : null);
                  const hasIssue = !!issueReason && eff !== 'PAUSED';
                  return (
                  <React.Fragment key={c.id}>
                  <div
                    className={`rounded-xl px-3 py-2.5 flex items-center justify-between gap-3 transition-all ${
                      hasIssue
                        ? 'bg-red-500/20 border-2 border-red-400 ring-2 ring-red-400/50 animate-pulse'
                        : 'bg-white/10'
                    }`}
                    data-testid={`fb-campaign-${c.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* Toggle */}
                      <button
                        data-testid={`campaign-toggle-${c.id}`}
                        onClick={() => toggleCampaignStatus(c.id, c.status)}
                        disabled={campaignLoading === c.id}
                        className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${c.status === 'ACTIVE' ? 'bg-green-400' : 'bg-white/30'}`}
                      >
                        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${c.status === 'ACTIVE' ? 'left-5' : 'left-0.5'}`} />
                      </button>
                      {/* Campaign Name */}
                      <span className="text-sm font-medium truncate" title={c.name}>{c.name}</span>
                    </div>
                    {/* Center - Conversations stats */}
                    <div className="flex items-center gap-2 text-xs">
                      {c.today_conversations > 0 && (
                        <div className="bg-green-500/20 border border-green-400/30 rounded-lg px-2 py-1 text-center">
                          <span className="font-black text-green-300">{c.today_conversations}</span>
                          <span className="text-green-200 mr-1">היום</span>
                        </div>
                      )}
                      {c.conversations > 0 && (
                        <>
                          <div className="bg-white/10 rounded-lg px-2 py-1 text-center">
                            <span className="font-black text-yellow-300">{c.conversations}</span>
                            <span className="text-blue-200 mr-1">שיחות</span>
                          </div>
                          <div className="bg-white/10 rounded-lg px-2 py-1 text-center">
                            <span className="font-black text-yellow-300">₪{c.cost_per_conversation}</span>
                            <span className="text-blue-200 mr-1">לפנייה</span>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Budget */}
                      {(c.daily_budget !== null || c.lifetime_budget) ? (
                        editingBudget?.id === c.id ? (
                          <div className="flex items-center gap-1">
                            <input
                              data-testid={`campaign-budget-input-${c.id}`}
                              type="number"
                              className="w-20 px-2 py-1 rounded text-black text-sm text-center"
                              value={editingBudget.value}
                              onChange={e => setEditingBudget({...editingBudget, value: e.target.value})}
                              onKeyDown={e => { if (e.key === 'Enter') saveCampaignBudget(c.id, editingBudget.value, 'campaign', editingBudget.field); if (e.key === 'Escape') setEditingBudget(null); }}
                              autoFocus
                            />
                            <button onClick={() => saveCampaignBudget(c.id, editingBudget.value, 'campaign', editingBudget.field)} className="bg-green-500 hover:bg-green-600 text-white rounded px-2 py-1 text-xs font-bold" data-testid={`campaign-budget-save-${c.id}`}>
                              {campaignLoading === c.id ? '...' : '✓'}
                            </button>
                            <button onClick={() => setEditingBudget(null)} className="bg-white/20 hover:bg-white/30 rounded px-2 py-1 text-xs">✕</button>
                          </div>
                        ) : (
                          <button
                            data-testid={`campaign-budget-btn-${c.id}`}
                            onClick={() => setEditingBudget(c.daily_budget !== null
                              ? { id: c.id, value: c.daily_budget, level: 'campaign', field: 'daily_budget' }
                              : { id: c.id, value: c.lifetime_budget, level: 'campaign', field: 'lifetime_budget' })}
                            className="bg-yellow-400/20 hover:bg-yellow-400/30 border border-yellow-300/40 rounded-lg px-3 py-1 text-sm font-bold text-yellow-300 flex items-center gap-1 transition-colors"
                          >
                            <Pencil className="w-3 h-3" />
                            {c.daily_budget !== null ? `₪${c.daily_budget} / יום` : `₪${c.lifetime_budget} סה"כ`}
                          </button>
                        )
                      ) : (
                        /* Ad set level budgets */
                        (c.adsets && c.adsets.some(a => a.daily_budget !== null || a.lifetime_budget !== null)) ? c.adsets.filter(a => a.daily_budget !== null || a.lifetime_budget !== null).map(a => {
                          const aField = a.daily_budget !== null ? 'daily_budget' : 'lifetime_budget';
                          const aVal = a.daily_budget !== null ? a.daily_budget : a.lifetime_budget;
                          return (
                            editingBudget?.id === a.id ? (
                              <div key={a.id} className="flex items-center gap-1">
                                <input
                                  data-testid={`adset-budget-input-${a.id}`}
                                  type="number"
                                  className="w-20 px-2 py-1 rounded text-black text-sm text-center"
                                  value={editingBudget.value}
                                  onChange={e => setEditingBudget({...editingBudget, value: e.target.value})}
                                  onKeyDown={e => { if (e.key === 'Enter') saveCampaignBudget(a.id, editingBudget.value, 'adset', editingBudget.field); if (e.key === 'Escape') setEditingBudget(null); }}
                                  autoFocus
                                />
                                <button onClick={() => saveCampaignBudget(a.id, editingBudget.value, 'adset', editingBudget.field)} className="bg-green-500 hover:bg-green-600 text-white rounded px-2 py-1 text-xs font-bold">
                                  {campaignLoading === a.id ? '...' : '✓'}
                                </button>
                                <button onClick={() => setEditingBudget(null)} className="bg-white/20 hover:bg-white/30 rounded px-2 py-1 text-xs">✕</button>
                              </div>
                            ) : (
                              <button
                                key={a.id}
                                data-testid={`adset-budget-btn-${a.id}`}
                                onClick={() => setEditingBudget({ id: a.id, value: aVal, level: 'adset', field: aField })}
                                className="bg-yellow-400/20 hover:bg-yellow-400/30 border border-yellow-300/40 rounded-lg px-3 py-1 text-sm font-bold text-yellow-300 flex items-center gap-1 transition-colors"
                              >
                                <Pencil className="w-3 h-3" />
                                {aField === 'daily_budget' ? `₪${aVal} / יום` : `₪${aVal} סה"כ`}
                              </button>
                            )
                          );
                        }) : (
                          <span className="text-xs text-blue-200">ללא תקציב</span>
                        )
                      )}
                      {/* Budget schedule clock - separate from edit, appears next to status */}
                      {c.daily_budget !== null && editingBudget?.id !== c.id && (
                        <BudgetScheduleControl
                          targetId={c.id}
                          level="campaign"
                          name={c.name}
                          currentBudget={c.daily_budget}
                          schedules={budgetSchedules}
                          onOpen={openScheduleModal}
                          onCancel={cancelBudgetSchedule}
                        />
                      )}
                      {/* Status badge - red ALERT for issues, green for active, grey for paused */}
                      {hasIssue ? (
                        <span
                          className="text-[10px] px-2 py-0.5 rounded-full font-black bg-red-600 text-white border border-red-300 animate-pulse flex items-center gap-1"
                          title={issueReason}
                          data-testid={`campaign-issue-badge-${c.id}`}
                        >
                          ⚠️ תקלה
                        </span>
                      ) : (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${c.status === 'ACTIVE' ? 'bg-green-500/30 text-green-200' : 'bg-white/10 text-white/50'}`}>
                          {c.status === 'ACTIVE' ? 'פעיל' : 'מושהה'}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Issue detail row — shows the reason in red below the card */}
                  {hasIssue && (
                    <div className="bg-red-600/30 border border-red-400 rounded-lg px-3 py-1.5 mt-1 flex items-center gap-2 text-xs font-bold text-white" data-testid={`campaign-issue-reason-${c.id}`}>
                      <AlertCircle className="w-4 h-4 text-red-200 flex-shrink-0" />
                      <span className="flex-1">{issueReason}</span>
                      <span className="text-[10px] text-red-200 font-normal">סטטוס FB: {eff}</span>
                    </div>
                  )}
                  </React.Fragment>
                );})}
              </div>
            )}
            </div>
            )}
          </div>
        )}

        {/* Financial Date Picker - month/year is controlled by the
            month-picker card at the top. Content collapsible per request. */}
        <div className="mb-3 bg-white rounded-xl shadow-sm overflow-hidden" data-testid="financial-date-picker">
          <button
            type="button"
            onClick={() => setShowDailyCalendar(v => !v)}
            className="w-full flex items-center justify-between p-3 hover:bg-stone-50 transition-colors"
            data-testid="toggle-daily-calendar"
          >
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#D97706]" />
              <h3 className="font-bold text-sm text-[#2C3E2D]" style={{ fontFamily: 'Heebo, sans-serif' }}>מעקב תשלומים יומי</h3>
              <span className="text-xs text-stone-500">(ימים של החודש)</span>
            </div>
            <ChevronLeft className={`w-5 h-5 text-stone-400 transition-transform ${showDailyCalendar ? '-rotate-90' : 'rotate-0'}`} />
          </button>
          {showDailyCalendar && (
          <div className="p-3 pt-0">
          {/* Daily calendar grid */}
          {(() => {
            const startDate = new Date(finDateFrom || todayStr);
            const year = startDate.getFullYear();
            const month = startDate.getMonth();
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const today = new Date();
            
            // Get FB daily spend for the month
            const fbDailySpend = {};
            if (fbStats?.month_daily) {
              fbStats.month_daily.forEach(d => { fbDailySpend[d.date] = d.spend; });
            } else if (fbStats?.last_7_days) {
              fbStats.last_7_days.forEach(d => { fbDailySpend[d.date] = d.spend; });
            }
            
            return (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-10 gap-2 mb-6">
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
                  const isFuture = new Date(dateStr) > today;
                  
                  // Filter orders for this day (all statuses except cancelled)
                  const dayOrders = orders.filter(o => {
                    if (o.status === 'cancelled') return false;
                    if (!o.created_at) return false;
                    const ca = typeof o.created_at === 'string' ? o.created_at : new Date(o.created_at).toISOString();
                    const d = ca.substring(0, 10);
                    return d === dateStr;
                  });
                  
                  const completedDay = dayOrders.filter(o => o.status === 'completed');
                  const pendingDay = dayOrders.filter(o => o.status !== 'completed');
                  const revenue = completedDay.reduce((s, o) => s + (o.total_amount || 0), 0);
                  // VAT removed from daily view per request — all numbers
                  // displayed are gross, not net-of-VAT.
                  // Per-SKU product cost sums quantity × unit-cost for
                  // each line item (see PRODUCT_COSTS map).
                  const productCost = completedDay.reduce((s, o) => s + calcProductCost(o), 0);
                  let shipCost = 0;
                  completedDay.forEach(o => { shipCost += calcShipCost(o); });
                  const fbDay = fbDailySpend[dateStr] || 0;
                  const profit = Math.round(revenue - productCost - shipCost - fbDay);
                  const pendingAmount = Math.round(pendingDay.reduce((s, o) => s + (o.total_amount || 0), 0));
                  
                  return (
                    <div key={day} className={`rounded-lg p-2 text-[9px] border transition-all ${
                      isToday ? 'border-[#D97706] border-2 bg-[#D97706]/5' :
                      isFuture ? 'border-stone-100 bg-stone-50 opacity-40' :
                      dayOrders.length > 0 ? 'border-stone-200 bg-white shadow-sm' : 'border-stone-100 bg-stone-50'
                    }`} data-testid={`day-box-${day}`}>
                      <div className={`font-bold text-center mb-1 ${isToday ? 'text-[#D97706]' : 'text-stone-500'}`}>
                        {day} <span className="font-normal text-[8px] text-stone-400">{['א','ב','ג','ד','ה','ו','ש'][new Date(year, month, day).getDay()]}'</span>
                      </div>
                      {!isFuture && (
                        <div className="space-y-0.5">
                          <div className="flex justify-between"><span className="text-stone-400">הושלם</span><span className="font-bold text-green-600">{Math.round(revenue)}</span></div>
                          <div className="text-center text-[8px] text-stone-400 mb-0.5">{completedDay.length}</div>
                          <div className="flex justify-between"><span className="text-stone-400">משלוח</span><span className="text-red-500">{shipCost}</span></div>
                          <div className="flex justify-between"><span className="text-stone-400">מוצר</span><span className="text-red-500">{productCost}</span></div>
                          {fbDay > 0 && <div className="flex justify-between"><span className="text-stone-400">פרסום</span><span className="text-red-500">{Math.round(fbDay)}</span></div>}
                          <div className={`border-t border-stone-200 pt-0.5 flex justify-between font-bold ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            <span>רווח</span><span>{profit}</span>
                          </div>
                          {pendingAmount > 0 && (
                            <div className="flex justify-between text-[8px] text-orange-500 mt-0.5">
                              <span>בטיפול</span><span>{pendingAmount}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {dayOrders.length === 0 && !isFuture && null}
                    </div>
                  );
                })}
              </div>
            );
          })()}
          {/* Monthly pending summary */}
          {(() => {
            const startDate = new Date(finDateFrom || todayStr);
            const year = startDate.getFullYear();
            const month = startDate.getMonth();
            const monthPending = orders.filter(o => {
              if (o.status === 'cancelled' || o.status === 'completed') return false;
              if (!o.created_at) return false;
              const ca = typeof o.created_at === 'string' ? o.created_at : new Date(o.created_at).toISOString();
              const d = new Date(ca);
              return d.getFullYear() === year && d.getMonth() === month;
            });
            const pendingTotal = Math.round(monthPending.reduce((s, o) => s + (o.total_amount || 0), 0));
            if (pendingTotal === 0) return null;
            return (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-center justify-between" data-testid="monthly-pending-summary">
                <span className="text-sm font-bold text-orange-700" style={{ fontFamily: 'Heebo, sans-serif' }}>ממתין לקבלת תשלום</span>
                <span className="text-lg font-black text-orange-600">{pendingTotal.toLocaleString()} ש"ח ({monthPending.length} הזמנות)</span>
              </div>
            );
          })()}
          </div>
          )}
        </div>

        {/* Row 2: Financial Summary - hidden, replaced by calendar */}
        <div className="hidden">
          {/* מע"מ */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-stone-200" data-testid="vat-box">
            <div className="flex items-center gap-2 mb-3">
              <Receipt className="w-5 h-5 text-red-500" />
              <h3 className="font-bold text-[#2C3E2D]" style={{ fontFamily: 'Heebo, sans-serif' }}>סה"כ מע"מ (18%)</h3>
            </div>
            <p className="text-2xl font-bold text-red-600">{totalVat.toLocaleString()} ש"ח</p>
            <p className="text-xs text-stone-400 mt-1">רק הזמנות בסטטוס "הושלם"</p>
          </div>

          {/* עלות מוצר */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-stone-200" data-testid="product-cost-box">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-5 h-5 text-orange-500" />
              <h3 className="font-bold text-[#2C3E2D]" style={{ fontFamily: 'Heebo, sans-serif' }}>סה"כ עלות מוצר</h3>
            </div>
            <p className="text-2xl font-bold text-orange-600">{totalProductCost.toLocaleString()} ש"ח</p>
            <p className="text-xs text-stone-400 mt-1">{countedOrders.length} הזמנות x {PRODUCT_COST} ש"ח</p>
          </div>

          {/* עלויות משלוח */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-stone-200" data-testid="shipping-cost-box">
            <div className="flex items-center gap-2 mb-3">
              <Truck className="w-5 h-5 text-blue-500" />
              <h3 className="font-bold text-[#2C3E2D]" style={{ fontFamily: 'Heebo, sans-serif' }}>עלויות משלוח</h3>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-600">מזומן ({SHIP_COST_CASH} ש"ח)</span>
                <span className="font-bold text-[#2C3E2D]">{shippingBreakdown.cash.toLocaleString()} ש"ח</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-600">אשראי/ביט עד הבית ({SHIP_COST_HOME} ש"ח)</span>
                <span className="font-bold text-[#2C3E2D]">{shippingBreakdown.home.toLocaleString()} ש"ח</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-600">נקודת איסוף ({SHIP_COST_PICKUP} ש"ח)</span>
                <span className="font-bold text-[#2C3E2D]">{shippingBreakdown.pickup.toLocaleString()} ש"ח</span>
              </div>
              <div className="border-t border-stone-200 pt-2 flex items-center justify-between text-sm">
                <span className="font-bold text-stone-700">סה"כ משלוחים</span>
                <span className="font-bold text-blue-600">{shippingBreakdown.total.toLocaleString()} ש"ח</span>
              </div>
            </div>
          </div>

          {/* סיכום רווח נקי */}
          <div className={`bg-white rounded-xl p-5 shadow-sm border-2 ${netProfit >= 0 ? 'border-green-400' : 'border-red-400'}`} data-testid="net-profit-box">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-5 h-5 text-green-600" />
              <h3 className="font-bold text-[#2C3E2D]" style={{ fontFamily: 'Heebo, sans-serif' }}>סיכום רווח</h3>
            </div>
            <div className="space-y-1 text-sm mb-3">
              <div className="flex justify-between">
                <span className="text-stone-500">סה"כ הזמנות</span>
                <span className="font-medium">{totalRevenue.toLocaleString()} ש"ח</span>
              </div>
              <div className="flex justify-between text-red-500">
                <span>- מע"מ</span>
                <span>{totalVat.toLocaleString()} ש"ח</span>
              </div>
              <div className="flex justify-between text-red-500">
                <span>- עלות מוצר</span>
                <span>{totalProductCost.toLocaleString()} ש"ח</span>
              </div>
              <div className="flex justify-between text-red-500">
                <span>- עלות משלוחים</span>
                <span>{shippingBreakdown.total.toLocaleString()} ש"ח</span>
              </div>
              {fbPeriodSpend > 0 && (
                <div className="flex justify-between text-red-500">
                  <span>- פרסום פייסבוק</span>
                  <span>{fbPeriodSpend.toLocaleString()} ש"ח</span>
                </div>
              )}
            </div>
            <div className="border-t-2 border-stone-200 pt-2">
              <div className="flex justify-between items-center">
                <span className="font-bold text-[#2C3E2D]">נטו</span>
                <span className={`text-2xl font-bold ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{netProfit.toLocaleString()} ש"ח</span>
              </div>
            </div>
          </div>
        </div>
        {activeFilter !== 'all' && (
          <div className="mb-4 flex items-center gap-2 text-sm">
            <span className="text-stone-500">מציג:</span>
            <span className="bg-[#D97706]/10 text-[#D97706] px-3 py-1 rounded-full font-bold" style={{ fontFamily: 'Heebo, sans-serif' }}>
              {activeFilter === 'pending' && 'בטיפול'}
              {activeFilter === 'completed' && 'הושלם'}
              {activeFilter === 'credit_card' && 'כרטיס אשראי'}
              {activeFilter === 'bank_transfer' && 'העברה בנקאית'}
              {activeFilter === 'bit' && 'Bit'}
              {activeFilter === 'cash' && 'מזומן'}
              {activeFilter === 'private' && 'נשלח ע״י צוות'}
            </span>
            <button onClick={() => setActiveFilter('all')} className="text-stone-400 hover:text-stone-600 underline">הצג הכל</button>
          </div>
        )}

        {/* Team Sender Cards - positioned above orders list */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {[
            {name: 'ברי', key: 'bari', color: 'purple', methods: ['private','sent_bari'], maxPackages: 6, phone: '0539406051'},
            {name: 'לירן', key: 'liran', color: 'teal', methods: ['sent_liran'], maxPackages: 6, phone: '0509959513'},
            {name: 'יניב', key: 'yaniv', color: 'indigo', methods: ['sent_yaniv'], maxPackages: 6, phone: '0515112205'},
            {name: 'דניאל', key: 'daniel', color: 'orange', methods: ['sent_daniel'], maxPackages: 6, phone: '0532260855'},
          ].map(sender => {
            // Sync rule (per user, 2026-04-28): the dashboard cube must
            // show EXACTLY what the courier sees in his TeamApp. The app
            // filters orders by:
            //   payment_method ∈ courier.methods   OR   sent_by === courier.name
            // Anything else (delivery+phone, team_*, pickup) is no longer
            // considered — the app is the single source of truth.
            const senderOrders = filteredByDate.filter(o =>
              orderBelongsToCourier(o, sender.name)
            );
            const completedSenderOrders = senderOrders.filter(o => o.status === 'completed');
            const sent = completedSenderOrders.length;
            const totalCollected = completedSenderOrders.reduce((s, o) => s + (o.total_amount || 0), 0);
            // Split settled revenue into 2 buckets per user request:
            //
            //  1. "שולם עד כה מראש"  = orders that were paid to the business
            //     DIRECTLY (credit card / bit / bank_transfer / paypal / paybox).
            //     These don't need the courier to transfer anything — the
            //     money already arrived. Count once the order is completed.
            //
            //  2. "שולם עד כה מזומן" = cash orders that the courier collected
            //     from the customer and then manually settled via the team
            //     app ("שולמו לברי/יניב/לירן"). Only counts when settled=true.
            //
            // The top "הכנסות הצוות ששולמו" card on the dashboard sums both
            // buckets. A cash order that's completed but not yet settled
            // still owes money to the business — shown separately as
            // "ממתין למזומן" below so it's obvious the courier needs to
            // transfer it.
            const PREPAID_METHODS = ['credit_card','bit','bank_transfer','paypal','paybox'];
            const prepaidOrders = completedSenderOrders.filter(o => PREPAID_METHODS.includes(o.payment_method));
            // Cash-like = money collected in-person by the courier.
            // Includes legacy sent_{yaniv,liran,bari} values (old payment
            // dropdown) so Yaniv's existing orders keep flowing through
            // the new settlement logic. Must be settled via the team app
            // before counting as paid.
            const CASH_LIKE = ['cash','private','sent_yaniv','sent_liran','sent_bari'];
            const cashOrders = completedSenderOrders.filter(o => CASH_LIKE.includes(o.payment_method));
            // All senderOrders here match the TeamApp filter
            // (`payment_method ∈ courier.methods` OR `sent_by === name`).
            // Inside this set:
            //  • prepaid (bit/credit/bank/paypal/paybox) → full total once completed
            //  • cash    (cash/private/sent_*)          → total − 40 ₪ ONLY if
            //    the courier actually settled it (handed cash over via the
            //    team app). Without `settled`, the money is still in the
            //    courier's pocket — counting it as "שולם עד כה" was the
            //    bug the user reported (Yaniv showing 6,208₪ when only
            //    1,552₪ had actually been transferred).
            const prepaidSettled = completedSenderOrders
              .filter(o => PREPAID_METHODS.includes(o.payment_method))
              .reduce((s, o) => s + (o.total_amount || 0), 0);
            const cashSettled = completedSenderOrders
              .filter(o => CASH_LIKE.includes(o.payment_method) && o.settled === true)
              .reduce((s, o) => s + Math.max(0, (o.total_amount || 0) - 40), 0);
            // Cash that the courier collected but hasn't transferred yet.
            // Surfaced separately so the admin sees what's still owed.
            const cashPending = completedSenderOrders
              .filter(o => CASH_LIKE.includes(o.payment_method) && o.settled !== true)
              .reduce((s, o) => s + Math.max(0, (o.total_amount || 0) - 40), 0);
            const totalSettled = prepaidSettled + cashSettled;
            const driverPay = sent * 40;
            const toBusiness = Math.max(0, totalCollected - driverPay);
            const remaining = sender.maxPackages > 0 ? Math.max(0, sender.maxPackages - sent) : null;
            // Stock: starts at 0. Each refill (admin order with staff phone) adds 6. Each completed delivery subtracts 1.
            const normPhone = (sender.phone || '').replace(/[-\s]/g, '').replace(/^972/, '').replace(/^0/, '');
            const refillOrders = normPhone ? orders.filter(o => ((o.customer_phone || '').replace(/[-\s]/g, '').replace(/^972/, '').replace(/^0/, '')) === normPhone) : [];
            // Stock-quantity: count REAL units across refill orders, not a
            // hard-coded 6-per-order. Each refill order's `items` array has
            // {quantity} per line - sum them up so e.g. a single order with
            // x23 of Testo Man correctly registers as +23 stock instead of
            // the misleading +6.
            const refillQty = refillOrders.reduce((sum, o) => {
              const items = Array.isArray(o.items) ? o.items : [];
              const itemQty = items.reduce((s, it) => s + (parseInt(it.quantity, 10) || 0), 0);
              return sum + (itemQty || 6); // legacy refills with no items still count as 6
            }, 0);
            // Stock override (May 2026): when Bari has saved a manual stock
            // value for this courier, it's the REFILL count. We subtract
            // completed orders since the refill so the dashboard mirrors
            // what the courier sees in his own app.
            const override = stockOverrides.find(x => x.courier_name === sender.name);
            let completedSinceRefill = sent;
            if (override) {
              const baseAt = new Date(override.base_set_at || 0).getTime();
              completedSinceRefill = senderOrders.filter(o => {
                if (o.status !== 'completed') return false;
                const tsStr = o.completed_at || o.updated_at || o.settled_at || o.created_at || '';
                const ts = tsStr ? new Date(tsStr).getTime() : 0;
                return ts >= baseAt;
              }).length;
            }
            const stockBalance = override
              ? (override.base_stock - completedSinceRefill)
              : (sender.maxPackages > 0 ? (refillQty - sent) : null);
            const stockRemaining = stockBalance !== null ? Math.max(0, stockBalance) : null;
            const stockTotal = stockBalance !== null
              ? Math.max(override ? override.base_stock : refillQty, 6)
              : null;
            const lowStock = false; // low-stock blink disabled per user request (Oct 2026)
            const isActive = senderFilter === sender.name;
            const origin = typeof window !== 'undefined' ? window.location.origin : '';
            const appUrl = `${origin}/team/${sender.key}`;
            const waIntl = `972${(sender.phone || '').replace(/^0/, '').replace(/[-\s]/g, '')}`;
            const notifyMsg = `היי ${sender.name}, קבלת משלוח חדש באפליקציה\n${appUrl}\nבהצלחה ויום טוב!`;
            const notifyHref = `whatsapp://send?phone=${waIntl}&text=${encodeURIComponent(notifyMsg)}`;
            return (
              <div
                key={sender.name}
                className={`bg-white rounded-xl p-3 shadow-sm transition-all duration-200 border-2 ${lowStock ? 'border-red-500 animate-pulse ring-4 ring-red-300' : (isActive ? '' : 'border-stone-200')}`}
                style={!lowStock && isActive ? (() => {
                  const palette = {
                    purple: { border: '#9333ea', bg: '#f3e8ff' },
                    teal:   { border: '#0d9488', bg: '#ccfbf1' },
                    indigo: { border: '#4f46e5', bg: '#e0e7ff' },
                    orange: { border: '#ea580c', bg: '#ffedd5' },
                  };
                  const p = palette[sender.color] || palette.indigo;
                  return { borderColor: p.border, boxShadow: `0 0 0 3px ${p.border}20` };
                })() : {}}
                data-testid={`filter-sender-card-${sender.name}`}
              >
                <div onClick={() => handleSenderFilter(sender.name)} className="cursor-pointer">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={(() => {
                        const palette = { purple: '#f3e8ff', teal: '#ccfbf1', indigo: '#e0e7ff', orange: '#ffedd5' };
                        return { backgroundColor: palette[sender.color] || palette.indigo };
                      })()}>
                      <Send className="w-4 h-4" style={(() => {
                        const palette = { purple: '#9333ea', teal: '#0d9488', indigo: '#4f46e5', orange: '#ea580c' };
                        return { color: palette[sender.color] || palette.indigo };
                      })()} />
                    </div>
                    {/* Headline = courier name only. The big "sent" number was
                        moved to the bottom-left of the card per the dashboard
                        redesign request. */}
                    <p className="text-base font-bold text-[#2C3E2D]">{sender.name}</p>
                  </div>
                  {/* Unified revenue = completed shipments only, matches the
                      top "הכנסות הצוות" card per user request so the ברי
                      figures reconcile across the dashboard. */}
                  <p className="text-[#D97706] font-bold text-xs mt-0.5">{sumRevenue(completedSenderOrders).toLocaleString()} ש"ח</p>
                  {/* Per-member financial breakdown - only for Liran/Yaniv who get driver pay */}
                  {sender.maxPackages > 0 && sent > 0 && (
                    <div className="mt-1.5 pt-1.5 border-t border-stone-100 space-y-0.5">
                      <div className="flex justify-between text-[9px]">
                        <span className="text-stone-400">סה״כ נגבה</span>
                        <strong className="text-green-600">{totalCollected.toLocaleString()} ₪</strong>
                      </div>
                      <div className="flex justify-between text-[9px]">
                        <span className="text-stone-400">תשלום משלוחים</span>
                        <strong className="text-blue-600">{driverPay.toLocaleString()} ₪</strong>
                      </div>
                      <div className="flex justify-between text-[9px]">
                        <span className="text-stone-400">לבריאות הגבר</span>
                        <strong className="text-amber-700">{toBusiness.toLocaleString()} ₪</strong>
                      </div>
                    </div>
                  )}
                  {/* Bottom row: total-settled-so-far (left) + stock summary (right).
                      Was previously a delivered-count "X נמסרו" — replaced
                      per user request with the cumulative ש״ח the courier
                      has already transferred via the settlement flow. */}
                  {sender.maxPackages > 0 && (
                    <div className="mt-2 pt-2 border-t border-stone-100 space-y-1">
                      {/* Two-bucket breakdown: prepaid (auto, from direct
                          payments) + cash-settled (manually transferred by
                          the courier through the team app). Any outstanding
                          cash is shown in orange below so the admin knows
                          there's still money to collect. */}
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-stone-500">שולם עד כה מראש</span>
                        <strong className="text-emerald-700 text-sm">{prepaidSettled.toLocaleString()} ₪</strong>
                      </div>
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-stone-500">שולם עד כה מזומן</span>
                        <strong className="text-emerald-700 text-sm">{cashSettled.toLocaleString()} ₪</strong>
                      </div>
                      {cashPending > 0 && (
                        <div className="flex items-center justify-between text-[10px]">
                          <span className="text-orange-600">ממתין</span>
                          <strong className="text-orange-700 text-sm">{cashPending.toLocaleString()} ₪</strong>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-[10px] pt-1 border-t border-stone-100">
                        <span className="font-bold text-stone-700">סה״כ שולמו עד כה</span>
                        <strong className="text-emerald-700 text-base">{totalSettled.toLocaleString()} ₪</strong>
                      </div>
                      {stockRemaining !== null && (
                        <div className={`flex items-center justify-between text-[10px] pt-1 border-t border-stone-100 ${lowStock ? 'text-red-600 font-bold' : 'text-stone-600'}`}>
                          <span>מלאי</span>
                          <span>
                            <strong>{stockRemaining}</strong> / <strong className="text-green-600">{sent} נמכר</strong>
                            {lowStock && ' ⚠️'}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* App link & notify buttons - only for Liran and Yaniv */}
                {sender.phone && (
                  <div className="mt-2 pt-2 border-t border-stone-100 flex gap-1">
                    <a
                      href={notifyHref}
                      onClick={(e) => e.stopPropagation()}
                      className="flex-1 flex items-center justify-center gap-1 bg-green-500 hover:bg-green-600 text-white rounded-lg py-1 text-[10px] font-bold transition-colors"
                      data-testid={`notify-${sender.key}`}
                      title="שלח הודעת וואטסאפ"
                    >
                      <Send className="w-3 h-3" />
                      וואטסאפ
                    </a>
                    <button
                      onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(appUrl); toast.success(`הועתק: ${appUrl}`); }}
                      className="flex-1 flex items-center justify-center gap-1 bg-stone-200 hover:bg-stone-300 text-stone-700 rounded-lg py-1 text-[10px] font-bold transition-colors"
                      data-testid={`copy-link-${sender.key}`}
                      title="העתק קישור לאפליקציה"
                    >
                      העתק לינק
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-stone-200 flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-xl font-bold text-[#2C3E2D]" style={{ fontFamily: 'Heebo, sans-serif' }}>
              רשימת הזמנות
            </h2>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-sm text-stone-600 whitespace-nowrap">מתאריך:</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-[#D97706] focus:border-transparent outline-none"
                  data-testid="date-from-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-stone-600 whitespace-nowrap">עד תאריך:</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-[#D97706] focus:border-transparent outline-none"
                  data-testid="date-to-input"
                />
              </div>
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(''); setDateTo(''); setSelectedMonth(''); setSelectedYear(''); }}
                  className="text-xs text-red-500 hover:text-red-700 underline whitespace-nowrap"
                >
                  נקה תאריכים
                </button>
              )}
              <div className="relative">
                <input
                  type="text"
                  placeholder="חיפוש לפי טלפון / שם / מס׳ הזמנה / כתובת..."
                  value={phoneSearch}
                  onChange={(e) => setPhoneSearch(e.target.value)}
                  className="pl-4 pr-10 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-[#D97706] focus:border-transparent outline-none w-72"
                  dir="auto"
                  data-testid="phone-search-input"
                />
                <svg xmlns="http://www.w3.org/2000/svg" className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              </div>
              {/* Quick-pay link — opens the standalone /quick-order PWA
                  in a new tab with a cache-busting param so the form
                  always starts on default values (no stale React state
                  or browser cache between sessions). */}
              <a
                href={`/quick-order?t=${Date.now()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white text-sm font-bold shadow-md transition-all whitespace-nowrap"
                title="פותח חלון חדש ליצירת הזמנה מהירה — שמור גם בטלפון לקיצור דרך"
                data-testid="open-quick-order-btn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z"/></svg>
                תשלום מהיר
              </a>
              {/* Cash report uploader — Bari's weekly HFD cash settlement
                  flow (May 2026). Pick an image/PDF → Gemini extracts the
                  table → server matches each row to a processing cash
                  order → flips them to completed+settled. */}
              <CashReportUploader onMatched={() => fetchOrders()} />
              {/* Provider filter — UPS removed May 2026 (we stopped working with them) */}
              <div className="flex items-center gap-1 mr-2">
                <button onClick={() => setProviderFilter('all')} className={`px-2 py-1 rounded text-[10px] font-bold transition-all ${providerFilter === 'all' ? 'bg-stone-800 text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`} data-testid="filter-all">הכל</button>
                <button onClick={() => handleProviderFilter('hfd')} className={`px-2 py-1 rounded text-[10px] font-bold transition-all ${providerFilter === 'hfd' ? 'bg-orange-600 text-white' : 'bg-orange-50 text-orange-700 hover:bg-orange-100'}`} data-testid="filter-hfd">HFD</button>
                <button onClick={() => handleProviderFilter('self_pickup')} className={`px-2 py-1 rounded text-[10px] font-bold transition-all ${providerFilter === 'self_pickup' ? 'bg-violet-600 text-white' : 'bg-violet-50 text-violet-700 hover:bg-violet-100'}`} data-testid="filter-self-pickup">איסוף עצמי</button>
              </div>
              <label className="flex items-center gap-1.5 cursor-pointer mr-2" data-testid="show-completed-toggle"
                onClick={() => setShowCompleted(prev => !prev)}>
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${showCompleted ? 'bg-[#D97706] border-[#D97706]' : 'border-stone-300 bg-white'}`}>
                  {showCompleted && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                </div>
                <span className="text-[10px] font-bold text-stone-500">חשוף הזמנות שהושלמו</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer mr-2" data-testid="show-cancelled-toggle"
                onClick={() => setShowCancelled(prev => !prev)}>
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${showCancelled ? 'bg-red-500 border-red-500' : 'border-stone-300 bg-white'}`}>
                  {showCancelled && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                </div>
                <span className="text-[10px] font-bold text-stone-500">חשוף הזמנות שבוטלו</span>
              </label>
              {/* "סינון לפי" — unified status / payment / shipping
                  dropdown. Sits next to the cancelled-toggle (May 2026
                  per user request). Filtering is purely client-side
                  through the displayedOrders memo — no server roundtrip. */}
              <div className="flex items-center gap-1.5 mr-3" data-testid="status-filter-wrap">
                <span className="text-[10px] font-bold text-stone-500">סינון:</span>
                <select
                  value={activeFilter}
                  onChange={(e) => { setActiveFilter(e.target.value); setOrderLimit(6); }}
                  className={`text-[11px] font-bold rounded-md px-2 py-1 border cursor-pointer transition-colors max-w-[140px] ${
                    activeFilter !== 'all'
                      ? 'bg-[#D97706] text-white border-[#D97706]'
                      : 'bg-white text-stone-700 border-stone-300 hover:border-[#D97706]'
                  }`}
                  data-testid="status-filter-select"
                  title="סינון לפי סטטוס/תשלום/משלוח"
                >
                  <option value="all">הכל</option>
                  <optgroup label="סטטוס">
                    <option value="pending">בטיפול</option>
                    <option value="completed">הושלמו</option>
                    <option value="not_purchased">לא רכשו</option>
                  </optgroup>
                  <optgroup label="תשלום">
                    <option value="credit_card">אשראי</option>
                    <option value="bit">ביט</option>
                    <option value="bank_transfer">העברה בנקאית</option>
                    <option value="cash">מזומן</option>
                  </optgroup>
                  <optgroup label="צוות">
                    <option value="sent_bari">נשלח ע״י ברי</option>
                    <option value="sent_yaniv">נשלח ע״י יניב</option>
                    <option value="sent_liran">נשלח ע״י לירן</option>
                    <option value="sent_daniel">נשלח ע״י דניאל</option>
                  </optgroup>
                  <optgroup label="משלוח">
                    <option value="home_delivery">עד הבית</option>
                    <option value="pickup">נקודת איסוף</option>
                    <option value="self_pickup">איסוף עצמי</option>
                    <option value="returned">חזר משלוח</option>
                  </optgroup>
                </select>
                {activeFilter !== 'all' && (
                  <button
                    onClick={() => setActiveFilter('all')}
                    className="text-[10px] text-stone-500 hover:text-red-600 underline"
                    data-testid="status-filter-clear"
                  >
                    נקה
                  </button>
                )}
              </div>
              {/* CardCom Daily Sync — fires a backend call that verifies
                  every credit-card/Bit order created today against
                  CardCom. Returns a comparison of dashboard total vs
                  CardCom total. */}
              <button
                onClick={async () => {
                  const today = new Date();
                  const tz = today.getTimezoneOffset();
                  const iso = new Date(today.getTime() - tz * 60000).toISOString().slice(0, 10);
                  try {
                    toast.loading('סנכרון עם CardCom…', { id: 'cc-sync' });
                    const res = await axios.post(`${API}/admin/sync-cardcom-day`, { date: iso });
                    const d = res.data;
                    const match = d.match ? '✅ זהה' : '⚠️ אי-התאמה';
                    toast.success(
                      `${match} | דאשבורד: ${d.dashboard_total.toLocaleString()} ₪ · CardCom: ${d.cardcom_total.toLocaleString()} ₪ | אומתו ${d.verified} · קיימים ${d.already} · נכשלו ${d.failed}`,
                      { id: 'cc-sync', duration: 12000 }
                    );
                    fetchOrders();
                  } catch (err) {
                    toast.error('סנכרון נכשל', { id: 'cc-sync' });
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-700 hover:from-blue-600 hover:to-indigo-800 text-white text-xs font-bold shadow-md whitespace-nowrap"
                title="אימות כל הזמנות האשראי של היום מול CardCom והשוואת סכומים"
                data-testid="cardcom-sync-day-btn"
              >
                🔄 סנכרן CardCom (יום)
              </button>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-sm text-stone-600 whitespace-nowrap">חודש:</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => { setSelectedMonth(e.target.value); applyMonthYear(e.target.value, selectedYear); }}
                  className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-[#D97706] focus:border-transparent outline-none"
                  data-testid="month-select"
                >
                  <option value="">בחר חודש</option>
                  {hebrewMonths.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-stone-600 whitespace-nowrap">שנה:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => { setSelectedYear(e.target.value); applyMonthYear(selectedMonth, e.target.value); }}
                  className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-[#D97706] focus:border-transparent outline-none"
                  data-testid="year-select"
                >
                  <option value="">בחר שנה</option>
                  {[2025, 2026, 2027, 2028, 2029, 2030].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {selectedIds.size > 0 && (
            <div className="p-4 bg-blue-50 border-b border-blue-200 flex items-center gap-4 flex-wrap" data-testid="bulk-actions-bar">
              <span className="font-bold text-blue-800" style={{ fontFamily: 'Heebo, sans-serif' }}>
                {selectedIds.size} הזמנות נבחרו
              </span>
              <div className="flex items-center gap-2">
                <button onClick={() => bulkUpdateStatus('processing')} className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors font-medium" data-testid="bulk-status-processing">
                  <Package className="w-4 h-4 inline ml-1" />בטיפול
                </button>
                <button onClick={() => bulkUpdateStatus('completed')} className="px-3 py-1.5 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition-colors font-medium" data-testid="bulk-status-completed">
                  <CheckCircle className="w-4 h-4 inline ml-1" />הושלם
                </button>
                <button onClick={() => bulkUpdateStatus('cancelled')} className="px-3 py-1.5 bg-stone-500 text-white text-sm rounded-lg hover:bg-stone-600 transition-colors font-medium" data-testid="bulk-status-cancelled">
                  <XCircle className="w-4 h-4 inline ml-1" />בוטל
                </button>
                {/* Bulk CardCom verify — sync every selected credit_card/bit
                    order against CardCom in one shot. Skips orders that are
                    already verified and reports per-order pass/fail. */}
                <button
                  onClick={async () => {
                    const ids = Array.from(selectedIds);
                    if (!ids.length) return;
                    setCardcomLoading(true);
                    toast.loading(`מסנכרן ${ids.length} הזמנות עם CardCom...`, { id: 'cc-bulk' });
                    try {
                      const res = await axios.post(`${API}/admin/orders/verify-cardcom-batch`, { order_ids: ids });
                      toast.dismiss('cc-bulk');
                      setCardcomReport(res.data);
                      await fetchOrders();
                    } catch (err) {
                      toast.error('סנכרון נכשל: ' + (err.response?.data?.detail || err.message), { id: 'cc-bulk' });
                    } finally {
                      setCardcomLoading(false);
                    }
                  }}
                  disabled={cardcomLoading}
                  className="px-3 py-1.5 bg-gradient-to-br from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 disabled:opacity-50 text-white text-sm rounded-lg transition-colors font-medium shadow-sm"
                  title="אמת את כל ההזמנות הנבחרות מול CardCom"
                  data-testid="bulk-verify-cardcom"
                >
                  {cardcomLoading ? '⏳ מסנכרן...' : '🔄 סנכרן CardCom נבחרים'}
                </button>
                {/* Re-open the last reconciliation report without triggering
                    another sync. Only shown once a report exists in state. */}
                {cardcomReport && (
                  <button
                    onClick={() => setCardcomReport({ ...cardcomReport })}
                    className="px-3 py-1.5 bg-white hover:bg-stone-100 text-blue-700 text-sm rounded-lg transition-colors font-medium border border-blue-300"
                    title="הצג שוב את דוח ההתאמה האחרון"
                    data-testid="cardcom-report-reopen"
                  >
                    📊 דוח אחרון
                  </button>
                )}
                <button onClick={bulkDelete} className={`px-3 py-1.5 text-white text-sm rounded-lg transition-colors font-medium ${confirmBulkDelete ? 'bg-red-700 animate-pulse hover:bg-red-800' : 'bg-red-500 hover:bg-red-600'}`} data-testid="bulk-delete">
                  <Trash2 className="w-4 h-4 inline ml-1" />{confirmBulkDelete ? 'אישור סופי - לחץ שוב' : 'מחק נבחרים'}
                </button>
                <button onClick={() => setSelectedIds(new Set())} className="px-3 py-1.5 bg-white text-stone-600 text-sm rounded-lg hover:bg-stone-100 transition-colors border border-stone-300 font-medium" data-testid="bulk-clear">
                  בטל בחירה
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="p-12 text-center">
              <p className="text-stone-500">טוען הזמנות...</p>
            </div>
          ) : displayedOrders.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">{activeFilter !== 'all' ? 'אין הזמנות בקטגוריה זו' : 'אין הזמנות עדיין'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto" style={{ direction: 'rtl' }}>
              {/* HFD status legend + manual refresh. Only shown when at least
                  one HFD shipment exists in the current view. Colors here
                  MUST match the pill styling in the tracking column. */}
              {displayedOrders.some(o => o.hfd_shipment_number) && (
                <div className="flex flex-wrap items-center gap-2 px-4 py-2 mb-2 bg-stone-50 border border-stone-200 rounded-lg text-[11px]" data-testid="hfd-legend">
                  <span className="font-bold text-stone-700">מקרא HFD:</span>
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-300">
                    <span className="w-2 h-2 rounded-full bg-green-500" /> נמסר
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 border border-orange-400">
                    <span className="w-2 h-2 rounded-full bg-orange-500" /> אי מסירה / חסרה כתובת
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300">
                    <span className="w-2 h-2 rounded-full bg-red-500" /> סורב / בוטל
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                    <span className="w-2 h-2 rounded-full bg-blue-500" /> בדרך / במיון
                  </span>
                  <span className="text-stone-500 mx-1">•</span>
                  <span className="text-stone-600">משלוחים בהתראה קופצים לראש הרשימה</span>
                  <button
                    onClick={async () => {
                      try {
                        toast.loading('מרענן סטטוסים מ-HFD...', { id: 'hfd-refresh' });
                        const r = await axios.post(`${API}/admin/hfd/refresh-all`);
                        toast.success(`רועננו ${r.data.updated} משלוחים`, { id: 'hfd-refresh' });
                        await fetchOrders();
                      } catch (e) {
                        toast.error('שגיאה ברענון', { id: 'hfd-refresh' });
                      }
                    }}
                    className="ml-auto px-3 py-1 rounded-md bg-orange-600 hover:bg-orange-700 text-white font-bold text-[11px] transition"
                    data-testid="hfd-refresh-all-btn"
                    title="משוך סטטוסים עדכניים מ-HFD"
                  >
                    🔄 רענן HFD
                  </button>
                </div>
              )}
              <table className="w-full" style={{ minWidth: '1100px' }}>
                <thead className="bg-stone-50">
                  <tr>
                    <th className="px-4 py-4 text-center">
                      <input
                        type="checkbox"
                        checked={displayedOrders.length > 0 && selectedIds.size === displayedOrders.length}
                        onChange={toggleSelectAll}
                        className="w-5 h-5 rounded border-stone-300 text-[#D97706] focus:ring-[#D97706] cursor-pointer"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#2C3E2D]">הזמנה</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#2C3E2D]">לקוח</th>
                    <th className="px-2 py-3 text-right text-xs font-semibold text-[#2C3E2D]">מוצרים</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#2C3E2D]">סכום</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#2C3E2D]">שליחה</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#2C3E2D]">תשלום</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#2C3E2D]">סטטוס</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#2C3E2D]">מעקב משלוח</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#2C3E2D]">תאריך</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#2C3E2D]">פעולות</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {displayedOrders.slice(0, orderLimit).map((order) => {
                    // Compute alert style: red ring for 'problem', orange for
                    // 'attention'. Dismissed alerts reset to normal.
                    const hfdLive = order.hfd_shipment_number ? hfdStatuses[order.hfd_shipment_number] : null;
                    const hfdLevel = hfdLive?.level || order.hfd_status_level || '';
                    const hfdText = hfdLive?.status || order.hfd_status_text || '';
                    const hfdDismissed = order.hfd_alert_dismissed_at &&
                                         order.hfd_alert_dismissed_status === hfdText;
                    const rowAlertClass = !hfdDismissed && (
                      hfdLevel === 'problem' ? 'ring-2 ring-red-400 ring-inset bg-red-50/60 hover:bg-red-100/60' :
                      hfdLevel === 'attention' ? 'ring-2 ring-orange-400 ring-inset bg-orange-50/60 hover:bg-orange-100/60' :
                      ''
                    );
                    return (
                    <tr key={order.id} className={`transition-colors ${rowAlertClass || (isAbandoned(order) ? 'bg-red-50 hover:bg-red-100' : (selectedIds.has(order.id) ? 'bg-blue-50' : 'hover:bg-stone-50'))}`}>
                      <td className="px-4 py-4 text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(order.id)}
                          onChange={() => toggleSelect(order.id)}
                          className="w-5 h-5 rounded border-stone-300 text-[#D97706] focus:ring-[#D97706] cursor-pointer"
                          data-testid={`select-order-${order.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-[#2C3E2D]">
                        {order.id.slice(0, 8)}
                      </td>
                      <td className="px-3 py-2 text-xs" data-testid={`customer-cell-${order.id}`}>
                        <div className="space-y-1">
                          {/* Coordination badge - mirrors team app's button. Click toggles.
                              Per user spec (May 2026): the תאום workflow
                              ONLY applies to orders the team itself
                              delivers (ברי/לירן/יניב). For internet/HFD/
                              UPS/pickup orders the team has no role, so
                              we hide the badge entirely. */}
                          {(['sent_bari','sent_yaniv','sent_liran','sent_daniel','private'].includes(order.payment_method)
                            || ['ברי','יניב','לירן','דניאל'].includes(order.sent_by)) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const next = !order.coordinated;
                              axios.patch(`${API}/admin/orders/${order.id}/field`, {
                                coordinated: next,
                                coordinated_by: next ? (order.coordinated_by || 'אדמין') : '',
                                coordinated_at: next ? new Date().toISOString() : '',
                              }).then(() => {
                                setOrders(prev => prev.map(o => o.id === order.id ? { ...o, coordinated: next, coordinated_by: next ? (o.coordinated_by || 'אדמין') : null } : o));
                                toast.success(next ? 'סומן כתואם ✓' : 'סומן כלא תואם');
                              }).catch(() => toast.error('שגיאה'));
                            }}
                            className={`w-full text-[10px] font-bold rounded-md px-2 py-1 flex items-center justify-center gap-1 transition-all ${
                              order.coordinated
                                ? 'bg-green-500 hover:bg-green-600 text-white'
                                : 'bg-orange-500 hover:bg-orange-600 text-white'
                            }`}
                            title={order.coordinated ? `תואם ע"י ${order.coordinated_by || ''}` : 'לחץ אחרי תיאום עם הלקוח'}
                            data-testid={`coordinate-${order.id}`}
                          >
                            {order.coordinated ? `✓ תואם${order.coordinated_by ? ` (${order.coordinated_by})` : ''}` : '⏰ לא תואם'}
                          </button>
                          )}
                          {/* Name */}
                          <div className="flex items-center gap-1 group cursor-pointer font-bold text-[#2C3E2D]" onClick={() => setEditingField({ orderId: order.id, field: 'customer_name', value: order.customer_name })} data-testid={`edit-name-trigger-${order.id}`}>
                            {editingField?.orderId === order.id && editingField?.field === 'customer_name' ? (
                              <input autoFocus value={editingField.value ?? ''} onChange={(e) => { const v = e.target.value; setEditingField(prev => prev ? { ...prev, value: v } : prev); }} onBlur={() => saveField(order.id, 'customer_name', editingField.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveField(order.id, 'customer_name', editingField.value); if (e.key === 'Escape') setEditingField(null); }} className="w-full px-1 py-0.5 border border-[#D97706] rounded text-xs outline-none" />
                            ) : (
                              <>
                                {order.source === 'lior_link' && (
                                  <span
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black text-white ml-1"
                                    style={{ background: 'linear-gradient(135deg,#ff8a3d,#ff5b00)' }}
                                    title="הזמנה דרך לינק LIOR"
                                    data-testid={`lior-badge-${order.id}`}
                                  >💪 LIOR</span>
                                )}
                                {order.customer_name} <svg className="w-3 h-3 text-stone-300 opacity-0 group-hover:opacity-100 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                              </>
                            )}
                          </div>
                          {/* Phone + WhatsApp */}
                          <div className="flex items-center gap-1 text-stone-500" dir="ltr">
                            {editingField?.orderId === order.id && editingField?.field === 'customer_phone' ? (
                              <input autoFocus value={editingField.value ?? ''} onChange={(e) => { const v = e.target.value; setEditingField(prev => prev ? { ...prev, value: v } : prev); }} onBlur={() => saveField(order.id, 'customer_phone', editingField.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveField(order.id, 'customer_phone', editingField.value); if (e.key === 'Escape') setEditingField(null); }} className="w-20 px-1 py-0.5 border border-[#D97706] rounded text-xs outline-none" dir="ltr" />
                            ) : (
                              <span className="group cursor-pointer flex items-center gap-0.5" onClick={() => setEditingField({ orderId: order.id, field: 'customer_phone', value: order.customer_phone })} data-testid={`edit-phone-trigger-${order.id}`}>
                                {order.customer_phone}
                                <svg className="w-3 h-3 text-stone-300 opacity-0 group-hover:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                              </span>
                            )}
                            <a href={(() => {
                              const phone = `972${(order.customer_phone || '').replace(/^0/, '').replace(/[-\s]/g, '')}`;
                              const name = order.customer_name || '';
                              const total = order.total_amount || 0;
                              const addr = order.customer_address || '';
                              const pickupName = order.pickup_point_name || '';
                              const destination = order.shipping_method === 'pickup' && pickupName ? `נקודת איסוף: ${pickupName}` + (addr ? ` (${addr})` : '') : addr;
                              const storeName = 'בריאות הגבר';
                              // Pull templates from DB so admin edits in the
                              // "תבניות הודעה" panel take effect immediately.
                              // Falls back to the original hard-coded copy if
                              // the template ID is missing.
                              const vars = { name, total, address: destination };
                              const msg = order.payment_method === 'cash'
                                ? renderWaTemplate(2, vars, `נא לשים לב\n${name} היקר!\n\nאנו משלמים 37 ש״ח עבורך על המשלוח מראש.\n\nבסה"כ של: ${total} ש"ח\n\nהמשלוח יגיע עד 2 ימי עסקים לכתובת: ${destination}\n\nאם תסרב לקבל את המשלוח או לא תהיה בכתובת והמשלוח יחזור.\n\nאנחנו נצטרך לגבות ממך 37 ש"ח\n\nיש בעיה עם המשלוח? עדכן אותנו ישר!\n\nמובן לי, ואני מאשר את המשלוח.\n\nלאישור השב: 1\n\n${storeName}`)
                                : renderWaTemplate(1, vars, `שלום ${name}\nהמשלוח שלך הוקם ויגיע עד 2 ימי עסקים\nל${destination}\n\nנא להשים לב שיתכנו שינויים בעקבות המצב הביטחוני\n\nתודה מראש,\n${storeName}`);
                              return `whatsapp://send?phone=${phone}&text=${encodeURIComponent(msg)}`;
                            })()} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-600" title="פתח וואטסאפ">
                              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            </a>
                          </div>
                          {/* Address */}
                          <div className="flex items-center gap-0.5 text-stone-400 group cursor-pointer" onClick={() => setEditingField({ orderId: order.id, field: 'customer_address', value: order.customer_address || '' })} data-testid={`edit-address-trigger-${order.id}`}>
                            {editingField?.orderId === order.id && editingField?.field === 'customer_address' ? (
                              <input autoFocus value={editingField.value ?? ''} onChange={(e) => { const v = e.target.value; setEditingField(prev => prev ? { ...prev, value: v } : prev); }} onBlur={() => saveField(order.id, 'customer_address', editingField.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveField(order.id, 'customer_address', editingField.value); if (e.key === 'Escape') setEditingField(null); }} className="w-full px-1 py-0.5 border border-[#D97706] rounded text-xs outline-none" data-testid={`edit-address-input-${order.id}`} />
                            ) : (
                              <>
                                {order.shipping_method === 'pickup' && order.pickup_point_name && <span className="text-blue-500 font-bold">{order.pickup_point_name} - </span>}
                                <span className="truncate max-w-[150px]">{order.customer_address || '-'}</span>
                                <svg className="w-3 h-3 text-stone-300 opacity-0 group-hover:opacity-100 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2" data-testid={`products-cell-${order.id}`}>
                        <div className="flex flex-col gap-0.5">
                          {(order.items || []).map((item, idx) => {
                            const img = getProductImage(item);
                            return (
                              <div key={idx} className="flex items-center gap-1">
                                {img && <img src={img} alt="" className="w-7 h-8 object-contain rounded" />}
                                <span className="text-[10px] text-stone-600 leading-tight max-w-[80px]">{item.product_name?.split(' - ')[0]} {item.quantity > 1 ? `x${item.quantity}` : ''}</span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs font-bold text-[#D97706]">
                        <div className="flex items-center gap-1 group cursor-pointer" onClick={() => setEditingField({ orderId: order.id, field: 'total_amount', value: order.total_amount })} data-testid={`edit-amount-trigger-${order.id}`}>
                          {editingField?.orderId === order.id && editingField?.field === 'total_amount' ? (
                            <input autoFocus type="number" value={editingField.value ?? ''} onChange={(e) => { const v = e.target.value; setEditingField(prev => prev ? { ...prev, value: v } : prev); }} onBlur={() => saveField(order.id, 'total_amount', parseFloat(editingField.value) || 0)} onKeyDown={(e) => { if (e.key === 'Enter') saveField(order.id, 'total_amount', parseFloat(editingField.value) || 0); if (e.key === 'Escape') setEditingField(null); }} className="w-16 px-1 py-0.5 border border-[#D97706] rounded text-xs outline-none" dir="ltr" />
                          ) : (
                            <>{order.total_amount} ש"ח <Pencil className="w-2.5 h-2.5 text-stone-300 opacity-0 group-hover:opacity-100 transition-opacity" /></>
                          )}
                        </div>
                      </td>
                      {/* Shipping category — source of truth for the cost
                          calculations above. Admin can override per-order
                          with the select below; persisted to `shipping_category`
                          via PATCH /admin/orders/{id}/field. */}
                      <td className="px-3 py-2 text-xs text-stone-600" data-testid={`shipping-cell-${order.id}`}>
                        {(() => {
                          const cat = getShippingCategory(order);
                          const cost = calcShipCost(order);
                          return (
                            <div className="flex flex-col gap-1 min-w-[100px]">
                              <select
                                value={cat}
                                onChange={(e) => {
                                  const next = e.target.value;
                                  const prev = order.shipping_category;
                                  setOrders(os => os.map(o => o.id === order.id ? { ...o, shipping_category: next } : o));
                                  axios.patch(`${API}/admin/orders/${order.id}/field`, { shipping_category: next })
                                    .then(() => toast.success('שיטת משלוח עודכנה'))
                                    .catch(() => {
                                      setOrders(os => os.map(o => o.id === order.id ? { ...o, shipping_category: prev } : o));
                                      toast.error('שגיאה בעדכון');
                                    });
                                }}
                                className="text-xs border border-stone-200 rounded px-1 py-0.5 focus:ring-1 focus:ring-[#D97706] outline-none"
                                data-testid={`shipping-select-${order.id}`}
                              >
                                {Object.entries(SHIPPING_CATEGORY_LABELS).map(([val, label]) => (
                                  <option key={val} value={val}>{label}</option>
                                ))}
                              </select>
                              <span className={`text-[10px] font-bold ${cost === 0 ? 'text-stone-400' : 'text-blue-600'}`}>
                                עלות משלוח: {cost} ₪
                              </span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2 text-xs text-stone-600">
                        <select
                          value={order.payment_method}
                          onChange={(e) => updatePaymentMethod(order.id, e.target.value)}
                          className="text-xs border border-stone-200 rounded px-1 py-0.5 focus:ring-1 focus:ring-[#D97706] outline-none"
                        >
                          <option value="credit_card">כרטיס אשראי</option>
                          <option value="bit">Bit</option>
                          <option value="paypal">PayPal</option>
                          <option value="paybox">פייבוקס</option>
                          <option value="bank_transfer">העברה בנקאית</option>
                          <option value="cash">מזומן</option>
                          {/* The remaining options (shipped / sent_bari / sent_liran /
                              sent_yaniv / not_purchased / returned) are now managed
                              via the "שליחה" column + order status, per user
                              request ("כל השאר מוגדרים כבר במקום אחר"). They are
                              kept in the options list ONLY when the row already
                              holds one of those legacy values, so existing data
                              stays editable instead of silently mutating. */}
                          {['shipped','sent_bari','sent_liran','sent_yaniv','not_purchased','returned'].includes(order.payment_method) && (
                            <option value={order.payment_method}>
                              {order.payment_method === 'shipped' && 'נשלח סחורה'}
                              {order.payment_method === 'sent_bari' && 'נשלח ע"י ברי'}
                              {order.payment_method === 'sent_liran' && 'נשלח ע"י לירן'}
                              {order.payment_method === 'sent_yaniv' && 'נשלח ע"י יניב'}
                              {order.payment_method === 'not_purchased' && 'לא רכש - לחזור אליו'}
                              {order.payment_method === 'returned' && 'חזר משלוח'}
                            </option>
                          )}
                        </select>
                        {/* Green "אומת ב-CardCom" badge — shown when the
                            CardCom monitor (or manual verify) confirmed a
                            successful transaction for this order. Replaces
                            the red "לא רכש" panel entirely. */}
                        {order.cardcom_verified && ['credit_card','bit'].includes(order.payment_method) && (
                          <div className="mt-1 inline-flex items-center gap-1 bg-emerald-600 text-white rounded px-2 py-0.5 text-[10px] font-bold" data-testid={`cardcom-verified-${order.id}`} title={`מס׳ חשבונית CardCom: ${order.cardcom_document_number || '—'}`}>
                            <CheckCircle className="w-3 h-3" />
                            בוצע תשלום בהצלחה בקארדקום {order.cardcom_document_number ? `#${order.cardcom_document_number}` : ''}
                          </div>
                        )}
                        {/* Per-row CardCom verify button — shown on every
                            credit_card / bit order that hasn't been verified
                            yet (the green badge above replaces it once the
                            transaction is confirmed). Lets the admin sync a
                            specific customer's payment against CardCom on
                            demand. */}
                        {['credit_card','bit'].includes(order.payment_method) && !order.cardcom_verified && !isAbandoned(order) && (
                          <button
                            onClick={async (e) => {
                              const btn = e.currentTarget;
                              const original = btn.innerHTML;
                              btn.disabled = true;
                              btn.innerHTML = '⏳ בודק...';
                              try {
                                const r = await axios.post(`${API}/admin/orders/${order.id}/verify-cardcom`);
                                if (r.data.verified) { toast.success(`✓ אומת ב-CardCom (חשבונית ${r.data.document_number || '—'})`); fetchOrders(); }
                                else { toast.info(`לא נמצאה עסקה ב-CardCom (${r.data.reason || '—'})`); btn.innerHTML = original; btn.disabled = false; }
                              } catch (err) { toast.error('שגיאה: ' + (err.response?.data?.detail || err.message)); btn.innerHTML = original; btn.disabled = false; }
                            }}
                            className="mt-1 inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white rounded px-2 py-0.5 text-[10px] font-bold transition"
                            title="בדוק את עסקת האשראי הזו ספציפית מול CardCom"
                            data-testid={`verify-cardcom-${order.id}`}
                          >
                            🔄 אמת ב-CardCom
                          </button>
                        )}
                        {isAbandoned(order) && order.payment_method !== 'not_purchased' && (
                          <div className="mt-1 flex items-center gap-1 flex-wrap">
                            <div className="inline-flex items-center gap-1 bg-red-600 text-white rounded px-2 py-0.5 text-[10px] font-bold" data-testid={`pending-label-${order.id}`}>
                              לא רכש - לחזור אליו
                            </div>
                            <button
                              onClick={async () => {
                                try {
                                  const r = await axios.post(`${API}/admin/orders/${order.id}/verify-cardcom`);
                                  if (r.data.verified) { toast.success(`✓ אומת ב-CardCom (חשבונית ${r.data.document_number || '—'})`); fetchOrders(); }
                                  else { toast.info(`לא נמצאה עסקה ב-CardCom (${r.data.reason || '—'})`); }
                                } catch (e) { toast.error('שגיאה: ' + (e.response?.data?.detail || e.message)); }
                              }}
                              className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white rounded px-2 py-0.5 text-[10px] font-bold"
                              title="בדוק ב-CardCom אם העסקה הסתיימה בהצלחה"
                              data-testid={`verify-cardcom-${order.id}`}
                            >
                              🔄 אמת ב-CardCom
                            </button>
                            <button
                              onClick={() => markAsPaid(order)}
                              className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white rounded px-2 py-0.5 text-[10px] font-bold"
                              title="סמן הזמנה זו כנרכשה (התשלום בוצע)"
                              data-testid={`mark-paid-${order.id}`}
                            >
                              ✓ סמן כנרכש
                            </button>
                          </div>
                        )}
                        {order.payment_method === 'bit' && order.payment_status !== 'pending' && (
                          <a
                            href={(() => {
                              const phone = `972${(order.customer_phone || '').replace(/^0/, '').replace(/[-\s]/g, '')}`;
                              const firstName = (order.customer_name || '').split(' ')[0];
                              const bitPageUrl = `${window.location.origin}/pay/bit?amount=${order.total_amount || 0}&order=${order.order_number || ''}&name=${encodeURIComponent(order.customer_name || '')}`;
                              const msg = `${firstName} שלום,\n\nתודה על ההזמנה, יש להיכנס ללינק מטה למעבר תשלום נוח בביט\n\n${bitPageUrl}`;
                              return `whatsapp://send?phone=${phone}&text=${encodeURIComponent(msg)}`;
                            })()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 bg-[#0070E0] hover:bg-[#005BBB] text-white rounded px-2 py-0.5 text-[10px] font-bold transition-colors"
                            data-testid={`bit-pay-link-${order.id}`}
                          >
                            ביט
                          </a>
                        )}
                        {isAbandoned(order) && (
                          <button
                            onClick={() => resendPaymentLink(order)}
                            className="mt-1 inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white rounded px-2 py-1 text-[10px] font-bold transition-colors w-full justify-center"
                            title="צור קישור אשראי חדש ושלח ללקוח"
                            data-testid={`resend-payment-${order.id}`}
                          >
                            <CreditCard className="w-3 h-3" />
                            קישור תשלום
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          {getStatusIcon(order.status)}
                          <select
                            value={order.status}
                            onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                            className="text-xs border border-stone-200 rounded px-1 py-0.5 focus:ring-1 focus:ring-[#D97706] outline-none"
                          >
                            <option value="processing">בטיפול</option>
                            <option value="completed">הושלם</option>
                            <option value="cancelled">בוטל</option>
                          </select>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-testid={`hfd-status-${order.id}`}>
                        {order.hfd_shipment_number && hfdStatuses[order.hfd_shipment_number] ? (
                          (() => {
                            const hfd = hfdStatuses[order.hfd_shipment_number];
                            const level = hfd.level || (hfd.delivered ? 'delivered' : hfd.cancelled ? 'problem' : 'transit');
                            // Color system — matches the legend at the top of the orders list.
                            //   delivered → green   | problem → red | attention → orange
                            //   transit   → blue    | unknown → gray
                            const pillStyle = {
                              delivered: 'bg-green-100 text-green-800 border border-green-300',
                              problem:   'bg-red-100 text-red-800 border border-red-300 animate-pulse',
                              attention: 'bg-orange-100 text-orange-800 border border-orange-400 animate-pulse',
                              transit:   'bg-blue-50 text-blue-700 border border-blue-200',
                              unknown:   'bg-stone-100 text-stone-600 border border-stone-300',
                            }[level] || 'bg-stone-100 text-stone-600';
                            const dot = {
                              delivered: 'bg-green-500',
                              problem:   'bg-red-500',
                              attention: 'bg-orange-500',
                              transit:   'bg-blue-500',
                              unknown:   'bg-stone-400',
                            }[level] || 'bg-stone-400';
                            const st = (hfd.status || '').toString();
                            const atPickup = !hfd.delivered && !hfd.cancelled && (st.includes('נקוד') || st.includes('הגיע') || st.includes('ממתין לאיסוף') || st.includes('מוכן לאיסוף'));
                            const isAlert = level === 'problem' || level === 'attention';
                            const dismissed = order.hfd_alert_dismissed_at && order.hfd_alert_dismissed_status === st;
                            const showDismiss = isAlert && !dismissed;
                            return (
                              <div className="flex flex-col items-start gap-1">
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${pillStyle}`} title={hfd.status_date ? `${st} (${hfd.status_date})` : st}>
                                  <span className={`w-2 h-2 rounded-full ${dot}`} />
                                  {st}
                                </span>
                                {showDismiss && (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await axios.post(`${API}/admin/orders/${order.id}/hfd-dismiss-alert`);
                                        // Mirror the dismiss locally so the order drops out of the
                                        // priority list without a full re-fetch.
                                        setOrders(prev => prev.map(o => o.id === order.id
                                          ? { ...o, hfd_alert_dismissed_at: new Date().toISOString(), hfd_alert_dismissed_status: st }
                                          : o));
                                        toast.success('התראה סומנה כטופלה ✓');
                                      } catch (err) {
                                        toast.error('שגיאה בביטול ההתראה');
                                      }
                                    }}
                                    className="text-[10px] px-2 py-0.5 rounded-md bg-stone-200 hover:bg-stone-300 text-stone-700 font-bold transition"
                                    title="הסר התראה - סומן כטופל"
                                    data-testid={`dismiss-hfd-${order.id}`}
                                  >
                                    ✓ טופל
                                  </button>
                                )}
                                {/* Red WhatsApp button for HFD alerts:
                                    - attention (orange) → "ניסיון מסירה כושל" template (id 5)
                                    - problem   (red)    → "משלוח חזר" template (id 6)
                                    Both templates are editable from the
                                    Admin → WhatsApp templates panel. */}
                                {isAlert && (() => {
                                  const phone = `972${(order.customer_phone || '').replace(/^0/, '').replace(/[-\s]/g, '')}`;
                                  const name = order.customer_name || '';
                                  const templateId = level === 'problem' ? 6 : 5;
                                  const fallback = level === 'problem'
                                    ? `שלום ${name}\n\nניסו אותך כמה וכמה פעמים השליחים\n\nהמשלוח שלך חזר, למה ככה?`
                                    : `מה שלומך ${name}?\n\nראינו שהיה ניסיון הפצה עם שליח וזה לא צלח\n\nקבעתם שוב?\nעדכן אותנו בהקדם שהמשלוח לא יחזור`;
                                  const msg = renderWaTemplate(templateId, { name }, fallback);
                                  const tip = level === 'problem'
                                    ? 'שלח ווצאפ - משלוח חזר'
                                    : 'שלח ווצאפ - ניסיון מסירה כושל';
                                  return (
                                    <a
                                      href={`whatsapp://send?phone=${phone}&text=${encodeURIComponent(msg)}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="flex items-center justify-center w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
                                      title={tip}
                                      data-testid={`wa-hfd-alert-${order.id}`}
                                    >
                                      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                    </a>
                                  );
                                })()}
                                {atPickup && (() => {
                                  const phone = `972${(order.customer_phone || '').replace(/^0/, '').replace(/[-\s]/g, '')}`;
                                  const name = order.customer_name || '';
                                  const pickupName = order.pickup_point_name || '';
                                  const addr = order.customer_address || '';
                                  const pickupInfo = pickupName ? `${pickupName}${addr ? ` (${addr})` : ''}` : addr;
                                  const msg = `${name} שלום\n\nהמשלוח שלך הגיע לנקודת המסירה - ${pickupInfo}\nמספר משלוח HFD: ${order.hfd_shipment_number}\n\nנא לגשת לאסוף\n\nאם כבר אספתם יש להתעלם מהודעה זאת\n\nיום טוב`;
                                  return (
                                    <a
                                      href={`whatsapp://send?phone=${phone}&text=${encodeURIComponent(msg)}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center justify-center w-6 h-6 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors"
                                      title="שלח הודעת ווצאפ - ממתין בנקודת מסירה"
                                      data-testid={`wa-pickup-ready-${order.id}`}
                                    >
                                      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                    </a>
                                  );
                                })()}
                              </div>
                            );
                          })()
                        ) : order.hfd_shipment_number ? (
                          <span className="text-xs text-stone-400">טוען...</span>
                        ) : order.private_shipment ? (
                          (() => {
                            // Per-staff colors so each delivery person has a
                            // visually distinct badge in the tracking column.
                            const staffPalette = {
                              'ברי':   { bg: 'bg-orange-100',  text: 'text-orange-700',  border: 'border-orange-300' },
                              'לירן':  { bg: 'bg-teal-100',    text: 'text-teal-700',    border: 'border-teal-300' },
                              'יניב':  { bg: 'bg-indigo-100',  text: 'text-indigo-700',  border: 'border-indigo-300' },
                            };
                            const sentBy = order.sent_by || 'ברי';
                            const c = staffPalette[sentBy] || { bg: 'bg-stone-100', text: 'text-stone-700', border: 'border-stone-300' };
                            const isDelivered = order.status === 'completed';
                            return (
                              <div className="flex flex-col gap-1">
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${c.bg} ${c.text} ${c.border}`}>
                                  נשלח ע״י {sentBy}
                                </span>
                                {/* Delivery status toggle - matches the team app's button colors */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const next = isDelivered ? 'pending' : 'completed';
                                    axios.put(`${API}/admin/orders/${order.id}/status`, { status: next })
                                      .then(() => {
                                        setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: next } : o));
                                        toast.success(next === 'completed' ? 'נמסר ✓' : 'הוחזר לטרם נמסר');
                                      })
                                      .catch(() => toast.error('שגיאה'));
                                  }}
                                  className={`w-full text-[10px] font-bold rounded-md px-2 py-1 transition-all ${
                                    isDelivered
                                      ? 'bg-green-500 hover:bg-green-600 text-white'
                                      : 'bg-orange-500 hover:bg-orange-600 text-white'
                                  }`}
                                  data-testid={`delivered-${order.id}`}
                                >
                                  {isDelivered ? '✓ נמסר' : '⏰ טרם נמסר'}
                                </button>
                              </div>
                            );
                          })()
                        ) : (
                          <span className="text-xs text-stone-300">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-600">
                        {formatDate(order.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-2">
                          {/* Show shipping status or buttons.
                              Priority order:
                              1. ehouse_carrier (Zigzag/Cargo only) → single
                                 "נשלח דרך אי-האוס" badge, all other
                                 shipping buttons hidden.
                              2. hfd_shipment_number set (direct HFD) →
                                 single label button.
                              3. private_shipment / self_pickup / open
                                 buttons fallback. */}
                          {order.ehouse_carrier && order.ehouse_carrier !== 'HFD' ? (
                            <div className="flex items-center gap-2">
                              <div
                                className={`flex items-center gap-1.5 px-3 py-1.5 ${
                                  order.ehouse_carrier === 'Zigzag' ? 'bg-yellow-50 border-yellow-300 text-yellow-800' :
                                  'bg-cyan-50 border-cyan-300 text-cyan-800'
                                } border rounded-lg text-xs font-bold`}
                                data-testid={`ehouse-badge-${order.id}`}
                                title={`נשלח דרך אי-האוס (${order.ehouse_mode || ''})`}
                              >
                                <Truck className="w-4 h-4" />
                                נשלח דרך אי-האוס - {order.ehouse_carrier === 'Zigzag' ? 'זיגזג' : 'קרגו'}
                              </div>
                            </div>
                          ) : order.hfd_shipment_number ? (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => window.open(`${API}/shipping/hfd-label/${order.hfd_shipment_number}`, '_blank', 'noopener')}
                                title="פתח מדבקת משלוח להדפסה"
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 hover:bg-orange-100 border border-orange-300 hover:border-orange-400 text-orange-700 rounded-lg text-xs font-bold cursor-pointer transition-colors active:scale-95"
                                data-testid={`hfd-label-${order.id}`}
                              >
                                <Truck className="w-4 h-4" />
                                HFD #{order.hfd_shipment_number}
                              </button>
                            </div>
                          ) : order.private_shipment ? (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 border border-purple-300 text-purple-700 rounded-lg text-xs font-bold"
                                data-testid={`private-badge-${order.id}`}
                              >
                                <Send className="w-4 h-4" />
                                נשלח ע״י {order.sent_by || 'ברי'}
                              </div>
                            </div>
                          ) : order.shipping_method === 'self_pickup' ? (
                            <div className="flex items-center gap-2">
                              {order.status === 'completed' ? (
                                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 border border-violet-300 text-violet-700 rounded-lg text-xs font-bold"
                                  data-testid={`self-pickup-badge-${order.id}`}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  נאסף
                                </div>
                              ) : (
                                <button
                                  onClick={() => markSelfPickup(order)}
                                  className="flex items-center gap-1 px-2 py-1 bg-violet-600 text-white rounded-lg text-[10px] font-bold hover:bg-violet-700 transition-colors"
                                  title="הדפס מדבקה וסמן כנאסף"
                                  data-testid={`mark-self-pickup-${order.id}`}
                                >
                                  <Printer className="w-3 h-3" />
                                  איסוף עצמי
                                </button>
                              )}
                              <button
                                onClick={() => printPickupLabel(order)}
                                className="flex items-center gap-1 px-2 py-1 bg-violet-100 text-violet-700 rounded-lg text-[10px] font-bold hover:bg-violet-200 transition-colors"
                                title="הדפס מדבקת איסוף עצמי"
                                data-testid={`print-pickup-label-${order.id}`}
                              >
                                <Printer className="w-3 h-3" />
                                מדבקה
                              </button>
                            </div>
                          ) : (
                            <div className="flex gap-1">
                              {/* HFD - direct integration with HFD API
                                  (not through eHouse). Sends shipment
                                  request directly to their endpoint. */}
                              <button
                                onClick={() => createHfdShipment(order)}
                                disabled={shippingLoading === order.id}
                                className="flex items-center gap-1 px-2 py-1 bg-orange-600 text-white rounded-lg text-[10px] font-bold hover:bg-orange-700 transition-colors disabled:opacity-50"
                                title="שגר משלוח HFD"
                                data-testid={`create-hfd-${order.id}`}
                              >
                                {shippingLoading === order.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Truck className="w-3 h-3" />}
                                HFD
                              </button>
                              {/* E-HOUSE carriers — open a confirmation
                                  modal with the customer's details and 3
                                  mode buttons (pickup / home / cash).
                                  Yellow = Zigzag, Cyan = Cargo.
                                  After successful send, the order's
                                  shipping_method is auto-updated so the
                                  admin only needs to print. */}
                              <button
                                onClick={() => setEhouseModal({ open: true, order, carrier: 'zigzag' })}
                                disabled={shippingLoading === order.id}
                                className={`flex items-center gap-1 px-2 py-1 ${order.ehouse_carrier === 'Zigzag' ? 'bg-yellow-600' : 'bg-yellow-400 hover:bg-yellow-500'} text-stone-900 rounded-lg text-[10px] font-bold transition-colors disabled:opacity-50`}
                                title={order.ehouse_carrier === 'Zigzag' ? `נשלח עם זיגזג (${order.ehouse_mode || ''})` : 'שלח עם זיגזג'}
                                data-testid={`send-zigzag-${order.id}`}
                              >
                                {shippingLoading === order.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Truck className="w-3 h-3" />}
                                זיגזג
                              </button>
                              <button
                                onClick={() => setEhouseModal({ open: true, order, carrier: 'cargo' })}
                                disabled={shippingLoading === order.id}
                                className={`flex items-center gap-1 px-2 py-1 ${order.ehouse_carrier === 'Cargo' ? 'bg-cyan-700' : 'bg-cyan-400 hover:bg-cyan-500'} text-stone-900 rounded-lg text-[10px] font-bold transition-colors disabled:opacity-50`}
                                title={order.ehouse_carrier === 'Cargo' ? `נשלח עם קרגו (${order.ehouse_mode || ''})` : 'שלח עם קרגו'}
                                data-testid={`send-cargo-${order.id}`}
                              >
                                {shippingLoading === order.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Truck className="w-3 h-3" />}
                                קרגו
                              </button>
                              {/* LIOR-link orders get HFD + print only — no team senders.
                                  Tagged by `source=lior_link` (created via /lior PWA). */}
                              {order.source !== 'lior_link' && (<>
                              <button
                                onClick={() => markPrivateShipment(order, 'ברי')}
                                disabled={shippingLoading === order.id}
                                className="flex items-center gap-1 px-2 py-1 bg-purple-600 text-white rounded-lg text-[10px] font-bold hover:bg-purple-700 transition-colors disabled:opacity-50"
                                title="נשלח ע״י ברי"
                                data-testid={`send-bari-${order.id}`}
                              >
                                {shippingLoading === order.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                ברי
                              </button>
                              <button
                                onClick={() => markPrivateShipment(order, 'לירן')}
                                disabled={shippingLoading === order.id}
                                className="flex items-center gap-1 px-2 py-1 bg-teal-600 text-white rounded-lg text-[10px] font-bold hover:bg-teal-700 transition-colors disabled:opacity-50"
                                title="נשלח ע״י לירן"
                                data-testid={`send-liran-${order.id}`}
                              >
                                <Send className="w-3 h-3" />
                                לירן
                              </button>
                              <button
                                onClick={() => markPrivateShipment(order, 'יניב')}
                                disabled={shippingLoading === order.id}
                                className="flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white rounded-lg text-[10px] font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50"
                                title="נשלח ע״י יניב"
                                data-testid={`send-yaniv-${order.id}`}
                              >
                                <Send className="w-3 h-3" />
                                יניב
                              </button>
                              <button
                                onClick={() => markPrivateShipment(order, 'דניאל')}
                                disabled={shippingLoading === order.id}
                                className="flex items-center gap-1 px-2 py-1 bg-orange-600 text-white rounded-lg text-[10px] font-bold hover:bg-orange-700 transition-colors disabled:opacity-50"
                                title="נשלח ע״י דניאל"
                                data-testid={`send-daniel-${order.id}`}
                              >
                                <Send className="w-3 h-3" />
                                דניאל
                              </button>
                              </>)}
                            </div>
                          )}
                          {/* Other actions */}
                          <div className="flex items-center gap-1.5">
                            {/* WhatsApp — pre-fill the "אנו משלמים 29 ש״ח
                                עליך" delivery-confirmation template so the
                                admin can send it in one click. Uses the
                                customer's phone + name + total + address. */}
                            <button
                              onClick={() => {
                                const rawPhone = (order.customer_phone || '').replace(/[-\s]/g, '');
                                if (!rawPhone) { toast.error('אין טלפון ללקוח'); return; }
                                const intl = `972${rawPhone.replace(/^0/, '')}`;
                                const name = order.customer_name || 'לקוח';
                                const total = Number(order.total_amount || 0).toLocaleString();
                                const addrParts = [order.customer_address, order.city].filter(Boolean);
                                const address = addrParts.length ? addrParts.join(', ') : 'הכתובת שנרשמה בהזמנה';
                                const msg = `נא לשים לב!\n${name} היקר!\n\nאנו משלמים 29 ש״ח עבורך על המשלוח מראש.\n\nבסה"כ של: ${total} ש"ח\n\nהמשלוח יגיע עד 2-5 ימי עסקים לכתובת: ${address}\n\nאם תסרב לקבל את המשלוח או לא תהיה בכתובת והמשלוח יחזור.\n\nאנחנו נצטרך לגבות ממך 29 ש"ח\n\nמאשר?`;
                                const waUrl = `whatsapp://send?phone=${intl}&text=${encodeURIComponent(msg)}`;
                                window.open(waUrl, '_blank', 'noopener,noreferrer');
                              }}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="שלח הודעת אישור משלוח בוואטסאפ"
                              data-testid={`send-shipping-msg-${order.id}`}
                            >
                              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            </button>
                            <button
                              onClick={() => duplicateShipment(order)}
                              className={`p-1.5 rounded-lg transition-colors ${duplicateArmed === order.id ? 'bg-indigo-600 text-white animate-pulse' : 'text-indigo-600 hover:bg-indigo-50'}`}
                              title={duplicateArmed === order.id ? 'לחץ שוב לאישור' : 'שיכפול משלוח'}
                              data-testid={`duplicate-order-${order.id}`}
                            >
                              <Package className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => printLabel(order)}
                              className="p-1.5 text-[#D97706] hover:bg-[#D97706]/10 rounded-lg transition-colors"
                              title="הדפס מדבקה פנימית"
                              data-testid={`print-label-${order.id}`}
                            >
                              <Printer className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setSelectedOrder(order)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="צפה בפרטים"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteOrder(order.id)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="מחק הזמנה"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* Filtered totals strip — sums the currently-displayed list
                  so the admin sees at a glance how much the active filter
                  represents in NIS (e.g. "סה״כ אשראי שסולק: 12,340 ₪").
                  Counts only paid/completed orders for accuracy. */}
              {displayedOrders.length > 0 && activeFilter !== 'all' && (() => {
                // Only count rows ACTUALLY visible on screen (respects the
                // "טען עוד 5" pagination) so sum matches what the admin sees.
                const visible = displayedOrders.slice(0, orderLimit);
                const settled = visible.filter(o => o.status === 'completed' || o.cardcom_verified);
                const settledSum = settled.reduce((s, o) => s + (o.total_amount || 0), 0);
                const allSum = visible.reduce((s, o) => s + (o.total_amount || 0), 0);
                const labelMap = {
                  credit_card: 'אשראי שסולק',
                  bit: 'ביט שסולק',
                  bank_transfer: 'העברה בנקאית',
                  cash: 'מזומן שנגבה',
                  pending: 'בטיפול',
                  completed: 'הושלמו',
                  not_purchased: 'לא רכשו',
                  sent_bari: 'נשלח ע״י ברי',
                  sent_yaniv: 'נשלח ע״י יניב',
                  sent_liran: 'נשלח ע״י לירן',
                  sent_daniel: 'נשלח ע״י דניאל',
                };
                const lbl = labelMap[activeFilter] || activeFilter;
                return (
                  <div className="mt-3 bg-gradient-to-l from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-3 mx-2" data-testid="filter-total-strip">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="text-xs font-bold text-stone-700">
                        סינון פעיל: <span className="text-orange-700">{lbl}</span>
                        <span className="text-stone-400 font-normal mr-1">({displayedOrders.length} הזמנות)</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <div>
                          <span className="text-stone-500">סה״כ {lbl}:</span>
                          <strong className="text-green-700 text-base ml-1" data-testid="filter-total-settled">{settledSum.toLocaleString()} ₪</strong>
                          <span className="text-stone-400 text-[10px] mr-1">({settled.length})</span>
                        </div>
                        {allSum !== settledSum && (
                          <div className="border-r border-stone-300 pr-3">
                            <span className="text-stone-500">סה״כ כולל ממתינים:</span>
                            <strong className="text-stone-800 text-base ml-1" data-testid="filter-total-all">{allSum.toLocaleString()} ₪</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
              {/* Pagination — chunk of 5 more on each click. We slice client
                  side because the orders array is already in memory; the
                  performance win comes from rendering far fewer rows
                  (each row has dozens of buttons + status pills). */}
              {displayedOrders.length > orderLimit && (
                <div className="flex flex-col items-center gap-2 py-4">
                  <button
                    onClick={() => setOrderLimit(n => n + 5)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#351C15] hover:bg-[#4a2a1f] text-[#FFB500] rounded-xl text-sm font-bold transition-all shadow-md hover:shadow-lg"
                    data-testid="load-more-orders"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                    טען עוד {Math.min(5, displayedOrders.length - orderLimit)} משלוחים
                  </button>
                  <span className="text-[11px] text-stone-500" data-testid="orders-shown-label">
                    מציג {orderLimit} מתוך {displayedOrders.length} משלוחים
                  </span>
                  {displayedOrders.length > orderLimit + 20 && (
                    <button
                      onClick={() => setOrderLimit(displayedOrders.length)}
                      className="text-[11px] text-stone-500 hover:text-[#D97706] underline transition"
                      data-testid="load-all-orders"
                    >
                      הצג את כולם ({displayedOrders.length})
                    </button>
                  )}
                </div>
              )}
              {displayedOrders.length > 0 && displayedOrders.length <= orderLimit && (
                <div className="text-center py-3 text-[11px] text-stone-400" data-testid="orders-shown-label">
                  מציג {displayedOrders.length} משלוחים
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#2C3E2D]" style={{ fontFamily: 'Heebo, sans-serif' }}>
                פרטי הזמנה #{selectedOrder.id.slice(0, 8)}
              </h3>
              <button
                onClick={() => setSelectedOrder(null)}
                className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <XCircle className="w-6 h-6 text-stone-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Customer Info */}
              <div>
                <h4 className="font-semibold text-[#2C3E2D] mb-3">פרטי לקוח</h4>
                <div className="bg-stone-50 rounded-xl p-4 space-y-2">
                  <p><span className="text-stone-500">שם:</span> {selectedOrder.customer_name}</p>
                  <p><span className="text-stone-500">טלפון:</span> <span dir="ltr">{selectedOrder.customer_phone}</span></p>
                  {selectedOrder.customer_email && (
                    <p><span className="text-stone-500">אימייל:</span> {selectedOrder.customer_email}</p>
                  )}
                  <p><span className="text-stone-500">כתובת:</span> {selectedOrder.customer_address}</p>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h4 className="font-semibold text-[#2C3E2D] mb-3">פריטים</h4>
                <div className="bg-stone-50 rounded-xl p-4">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-stone-200">
                        <th className="text-right pb-2 text-stone-500">מוצר</th>
                        <th className="text-right pb-2 text-stone-500">כמות</th>
                        <th className="text-right pb-2 text-stone-500">מחיר</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items.map((item, index) => (
                        <tr key={index} className="border-b border-stone-100 last:border-0">
                          <td className="py-2">{item.product_name}</td>
                          <td className="py-2">{item.quantity}</td>
                          <td className="py-2">{item.price} ש"ח</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Order Summary */}
              <div className="flex items-center justify-between bg-[#D97706]/10 rounded-xl p-4">
                <span className="font-semibold text-[#2C3E2D]">סה"כ לתשלום:</span>
                <span className="text-2xl font-bold text-[#D97706]">{selectedOrder.total_amount} ש"ח</span>
              </div>

              {/* Notes */}
              {selectedOrder.notes && (
                <div>
                  <h4 className="font-semibold text-[#2C3E2D] mb-3">הערות</h4>
                  <div className="bg-stone-50 rounded-xl p-4">
                    <p className="text-stone-600">{selectedOrder.notes}</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  onClick={() => deleteOrder(selectedOrder.id)}
                  className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-5 h-5" />
                  מחק הזמנה
                </button>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="flex-1 py-3 bg-stone-200 text-[#2C3E2D] font-bold rounded-xl hover:bg-stone-300 transition-colors"
                >
                  סגור
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FB Budget Schedule Modal */}
      {scheduleModal && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => setScheduleModal(null)} data-testid="budget-schedule-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} dir="rtl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-black text-lg text-[#2C3E2D]">תזמן שינוי תקציב</h3>
                <p className="text-xs text-stone-500 truncate max-w-[260px]">{scheduleModal.name}</p>
              </div>
            </div>
            <div className="bg-stone-50 rounded-lg p-3 mb-4 text-sm">
              <div className="flex justify-between"><span className="text-stone-500">תקציב נוכחי:</span><span className="font-bold">₪{scheduleModal.currentBudget} / יום</span></div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs font-bold text-stone-700 block mb-1">תאריך</label>
                <input type="date" value={schedDate} onChange={e => setSchedDate(e.target.value)} min={new Date().toISOString().slice(0,10)} className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm font-bold" data-testid="budget-sched-date" />
              </div>
              <div>
                <label className="text-xs font-bold text-stone-700 block mb-1">שעה</label>
                <input type="time" value={schedTime} onChange={e => setSchedTime(e.target.value)} className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm font-bold" data-testid="budget-sched-time" />
              </div>
            </div>
            <label className="text-xs font-bold text-stone-700 block mb-1">תקציב חדש (₪ ליום)</label>
            <input type="number" value={schedAmount} onChange={e => setSchedAmount(e.target.value)} min="1" placeholder="לדוגמה: 200" className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm font-bold mb-4" autoFocus data-testid="budget-sched-amount" />
            <div className="flex gap-2">
              <button onClick={submitBudgetSchedule} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2" data-testid="budget-sched-submit">
                <Clock className="w-4 h-4" /> תזמן שינוי
              </button>
              <button onClick={() => setScheduleModal(null)} className="px-4 bg-stone-100 hover:bg-stone-200 rounded-xl font-bold">ביטול</button>
            </div>
            <p className="text-[10px] text-stone-400 mt-3 text-center">בזמן הנקבע ה-API של פייסבוק יקבל את הפקודה אוטומטית. אם תיצור תזמון חדש לאותו קמפיין - הוא יחליף את הקודם.</p>
          </div>
        </div>
      )}

      {/* ── eHouse send-with-carrier modal ────────────────────────────
          Opens when the admin clicks "זיגזג" or "קרגו" on an order. Shows
          customer name + address + phone, then 3 mode buttons (pickup /
          home / home-cash). On submit, the order is pushed to eHouse and
          the shipping_method is auto-set on the local DB so the dashboard
          column updates without any extra clicks. */}
      {ehouseModal.open && ehouseModal.order && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEhouseModal({ open: false, order: null, carrier: null })} data-testid="ehouse-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${ehouseModal.carrier === 'zigzag' ? 'bg-yellow-400' : 'bg-cyan-400'}`}>
                <Truck className="w-5 h-5 text-stone-900" />
              </div>
              <div>
                <h3 className="font-black text-lg text-[#2C3E2D]">
                  שליחת משלוח עם {ehouseModal.carrier === 'zigzag' ? 'זיגזג' : 'קרגו'}
                </h3>
                <p className="text-xs text-stone-500">בחר אופן משלוח להעברה לאי-האוס</p>
              </div>
            </div>

            {/* Customer card */}
            <div className="bg-stone-50 rounded-lg p-3 mb-4 text-sm space-y-1" data-testid="ehouse-modal-customer">
              <div className="flex justify-between">
                <span className="text-stone-500">שם לקוח:</span>
                <span className="font-bold text-stone-800">{ehouseModal.order.customer_name || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-500">טלפון:</span>
                <span className="font-bold text-stone-800" dir="ltr">{ehouseModal.order.customer_phone || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-500">כתובת:</span>
                <span className="font-bold text-stone-800 text-left max-w-[260px] truncate">
                  {ehouseModal.order.customer_address || '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-500">סכום:</span>
                <span className="font-bold text-amber-700">{(ehouseModal.order.total_amount || 0).toLocaleString()} ₪</span>
              </div>
            </div>

            {/* 4 mode buttons — mapping to eHouse delivery types:
                 home      → HOME      (Home Delivery)
                 drop_off  → DROP_OFF  (Drop Off / נקודת איסוף)
                 pickup    → PICKUP    (Pickup / איסוף עצמי)
                 home_cash → HOME + tag GOVAINA (גוביינא / מזומן) */}
            <p className="text-xs font-bold text-stone-700 mb-2">בחר אופן משלוח:</p>
            <div className="space-y-2 mb-4">
              <button
                onClick={() => createEhouseShipment(ehouseModal.order, { carrier: ehouseModal.carrier, mode: 'home' })}
                disabled={shippingLoading === ehouseModal.order.id}
                className="w-full px-4 py-3 bg-stone-100 hover:bg-stone-200 border-2 border-stone-300 rounded-xl font-bold text-stone-800 text-sm flex items-center justify-between transition-colors disabled:opacity-50"
                data-testid="ehouse-mode-home"
              >
                <span className="text-right">🏠 עד הבית <span className="text-[10px] text-stone-400 font-normal">— Home Delivery</span></span>
                <span className="text-[10px] text-stone-500">העבר לאי-האוס →</span>
              </button>
              <button
                onClick={() => createEhouseShipment(ehouseModal.order, { carrier: ehouseModal.carrier, mode: 'drop_off' })}
                disabled={shippingLoading === ehouseModal.order.id}
                className="w-full px-4 py-3 bg-stone-100 hover:bg-stone-200 border-2 border-stone-300 rounded-xl font-bold text-stone-800 text-sm flex items-center justify-between transition-colors disabled:opacity-50"
                data-testid="ehouse-mode-drop-off"
              >
                <span className="text-right">📦 נקודת איסוף <span className="text-[10px] text-stone-400 font-normal">— Drop Off</span></span>
                <span className="text-[10px] text-stone-500">העבר לאי-האוס →</span>
              </button>
              <button
                onClick={() => createEhouseShipment(ehouseModal.order, { carrier: ehouseModal.carrier, mode: 'pickup' })}
                disabled={shippingLoading === ehouseModal.order.id}
                className="w-full px-4 py-3 bg-stone-100 hover:bg-stone-200 border-2 border-stone-300 rounded-xl font-bold text-stone-800 text-sm flex items-center justify-between transition-colors disabled:opacity-50"
                data-testid="ehouse-mode-pickup"
              >
                <span className="text-right">🚚 איסוף עצמי <span className="text-[10px] text-stone-400 font-normal">— Pickup</span></span>
                <span className="text-[10px] text-stone-500">העבר לאי-האוס →</span>
              </button>
              <button
                onClick={() => createEhouseShipment(ehouseModal.order, { carrier: ehouseModal.carrier, mode: 'home_cash' })}
                disabled={shippingLoading === ehouseModal.order.id}
                className="w-full px-4 py-3 bg-stone-100 hover:bg-stone-200 border-2 border-stone-300 rounded-xl font-bold text-stone-800 text-sm flex items-center justify-between transition-colors disabled:opacity-50"
                data-testid="ehouse-mode-home-cash"
              >
                <span className="text-right">💵 מזומן עד הבית <span className="text-[10px] text-stone-400 font-normal">— GOVAINA</span></span>
                <span className="text-[10px] text-stone-500">העבר לאי-האוס →</span>
              </button>
            </div>

            <button
              onClick={() => setEhouseModal({ open: false, order: null, carrier: null })}
              disabled={shippingLoading === ehouseModal.order.id}
              className="w-full px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-xl font-bold text-stone-600 text-sm"
              data-testid="ehouse-modal-close"
            >
              ביטול
            </button>
          </div>
        </div>
      )}

      {/* CardCom Reconciliation Modal ── shows after a bulk verify so the
          admin can see the two-way diff: dashboard orders missing from
          CardCom AND CardCom transactions missing from the dashboard. */}
      {cardcomReport && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="cardcom-report-modal" onClick={() => setCardcomReport(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} dir="rtl">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Heebo, sans-serif' }}>דוח התאמת CardCom</h2>
                <p className="text-xs opacity-80 mt-0.5">
                  סונכרנו {cardcomReport.verified || 0} · קיימים {cardcomReport.already || 0} · לא נמצאו {cardcomReport.failed || 0} (מתוך {cardcomReport.total})
                </p>
              </div>
              <button onClick={() => setCardcomReport(null)} className="text-white/80 hover:text-white text-2xl leading-none p-1" data-testid="cardcom-report-close">×</button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-stone-50 border-b border-stone-200">
              <div className="bg-white rounded-lg p-3 border border-emerald-300">
                <div className="text-xs text-stone-500">סונכרנו בהצלחה</div>
                <div className="text-lg font-bold text-emerald-700">₪{(cardcomReport.summary?.verified_orders_amount || 0).toLocaleString()}</div>
                <div className="text-[10px] text-stone-400 mt-0.5">{(cardcomReport.verified_orders || []).length} לקוחות</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-blue-300">
                <div className="text-xs text-stone-500">סה״כ בקארדקום (חלון)</div>
                <div className="text-lg font-bold text-blue-700">₪{(cardcomReport.summary?.cardcom_total_amount || 0).toLocaleString()}</div>
                <div className="text-[10px] text-stone-400 mt-0.5">{cardcomReport.summary?.cardcom_tx_count || 0} עסקאות</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-amber-300">
                <div className="text-xs text-stone-500">לא נמצאו בקארדקום</div>
                <div className="text-lg font-bold text-amber-700">₪{(cardcomReport.summary?.unmatched_orders_amount || 0).toLocaleString()}</div>
                <div className="text-[10px] text-stone-400 mt-0.5">{(cardcomReport.unmatched_orders || []).length} הזמנות</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-red-300">
                <div className="text-xs text-stone-500">בקארדקום ולא בדשבורד</div>
                <div className="text-lg font-bold text-red-700">₪{(cardcomReport.summary?.unmatched_cardcom_amount || 0).toLocaleString()}</div>
                <div className="text-[10px] text-stone-400 mt-0.5">{(cardcomReport.unmatched_cardcom || []).length} עסקאות</div>
              </div>
            </div>

            {/* Body: 3 sections stacked (verified / unmatched-dashboard / unmatched-cardcom).
                Each row includes a green WhatsApp quick-launch button next
                to the phone so Bari can call/message the customer directly
                from the reconciliation report. */}
            {(() => {
              const waHref = (rawPhone, name) => {
                const digits = String(rawPhone || '').replace(/[-\s]/g, '').replace(/^0/, '');
                if (!digits) return null;
                const firstName = String(name || '').split(' ')[0] || '';
                const msg = firstName ? `שלום ${firstName},` : 'שלום,';
                return `https://wa.me/972${digits}?text=${encodeURIComponent(msg)}`;
              };
              const PhoneCell = ({ phone, name }) => {
                const href = waHref(phone, name);
                if (!phone) return <span className="text-stone-400">—</span>;
                return (
                  <div className="flex items-center gap-1.5">
                    <span className="text-stone-700 font-mono text-[11px]">{phone}</span>
                    {href && (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500 hover:bg-green-600 text-white text-[10px] shadow-sm transition"
                        title={`שלח וואטסאפ ל-${phone}`}
                        data-testid={`wa-launch-${phone}`}
                        onClick={e => e.stopPropagation()}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      </a>
                    )}
                  </div>
                );
              };
              return (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Section 1: successfully verified (this run + already) */}
                <div className="border border-emerald-200 rounded-lg overflow-hidden" data-testid="verified-orders-table">
                  <div className="bg-emerald-50 px-3 py-2 border-b border-emerald-200 flex items-center justify-between">
                    <h3 className="font-bold text-emerald-800 text-sm">✓ סונכרנו בהצלחה ({(cardcomReport.verified_orders || []).length})</h3>
                    <span className="text-xs text-emerald-700">סך {(cardcomReport.summary?.verified_orders_amount || 0).toLocaleString()} ₪</span>
                  </div>
                  <div className="overflow-y-auto max-h-[30vh]">
                    {(cardcomReport.verified_orders || []).length === 0 ? (
                      <div className="p-4 text-center text-sm text-stone-500">אף הזמנה לא סונכרנה בהצלחה בסבב זה</div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead className="bg-stone-50 text-stone-600 text-[11px]">
                          <tr>
                            <th className="px-2 py-1.5 text-right">שם</th>
                            <th className="px-2 py-1.5 text-right">טלפון</th>
                            <th className="px-2 py-1.5 text-right">סכום</th>
                            <th className="px-2 py-1.5 text-right">מס׳ עסקה</th>
                            <th className="px-2 py-1.5 text-right">חשבונית</th>
                            <th className="px-2 py-1.5 text-right">סטטוס</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(cardcomReport.verified_orders || []).map(o => (
                            <tr key={o.order_id} className="border-t border-stone-100 hover:bg-emerald-50/50">
                              <td className="px-2 py-1.5 font-medium">{o.customer_name || '—'}</td>
                              <td className="px-2 py-1.5"><PhoneCell phone={o.customer_phone} name={o.customer_name} /></td>
                              <td className="px-2 py-1.5 font-bold">₪{Number(o.amount).toLocaleString()}</td>
                              <td className="px-2 py-1.5 text-stone-600 font-mono text-[11px]">{o.transaction_id || '—'}</td>
                              <td className="px-2 py-1.5 text-stone-600">#{o.document_number || '—'}</td>
                              <td className="px-2 py-1.5 text-[10px]">
                                {o.status === 'verified_now'
                                  ? <span className="bg-emerald-600 text-white px-1.5 py-0.5 rounded-full">אומת עכשיו</span>
                                  : <span className="bg-stone-400 text-white px-1.5 py-0.5 rounded-full">כבר היה מאומת</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* Section 2: dashboard orders NOT found on CardCom */}
                <div className="border border-amber-200 rounded-lg overflow-hidden" data-testid="unmatched-orders-table">
                  <div className="bg-amber-50 px-3 py-2 border-b border-amber-200 flex items-center justify-between">
                    <h3 className="font-bold text-amber-800 text-sm">⚠ לא נמצאו בקארדקום ({(cardcomReport.unmatched_orders || []).length})</h3>
                    <span className="text-xs text-amber-700">סך {(cardcomReport.summary?.unmatched_orders_amount || 0).toLocaleString()} ₪</span>
                  </div>
                  <div className="overflow-y-auto max-h-[30vh]">
                    {(cardcomReport.unmatched_orders || []).length === 0 ? (
                      <div className="p-4 text-center text-sm text-stone-500">כל ההזמנות הנבחרות נמצאו ✓</div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead className="bg-stone-50 text-stone-600 text-[11px]">
                          <tr>
                            <th className="px-2 py-1.5 text-right">שם</th>
                            <th className="px-2 py-1.5 text-right">טלפון</th>
                            <th className="px-2 py-1.5 text-right">סכום</th>
                            <th className="px-2 py-1.5 text-right">סיבה</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(cardcomReport.unmatched_orders || []).map(o => (
                            <tr key={o.order_id} className="border-t border-stone-100 hover:bg-amber-50/50">
                              <td className="px-2 py-1.5 font-medium">{o.customer_name || '—'}</td>
                              <td className="px-2 py-1.5"><PhoneCell phone={o.customer_phone} name={o.customer_name} /></td>
                              <td className="px-2 py-1.5 font-bold">₪{Number(o.amount).toLocaleString()}</td>
                              <td className="px-2 py-1.5 text-[10px] text-stone-500">{o.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* Section 3: CardCom transactions NOT in dashboard */}
                <div className="border border-red-200 rounded-lg overflow-hidden" data-testid="unmatched-cardcom-table">
                  <div className="bg-red-50 px-3 py-2 border-b border-red-200 flex items-center justify-between">
                    <h3 className="font-bold text-red-800 text-sm">🔴 בקארדקום ולא בדשבורד ({(cardcomReport.unmatched_cardcom || []).length})</h3>
                    <span className="text-xs text-red-700">סך {(cardcomReport.summary?.unmatched_cardcom_amount || 0).toLocaleString()} ₪</span>
                  </div>
                  <div className="overflow-y-auto max-h-[30vh]">
                    {(cardcomReport.unmatched_cardcom || []).length === 0 ? (
                      <div className="p-4 text-center text-sm text-stone-500">כל העסקאות מקושרות להזמנה ✓</div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead className="bg-stone-50 text-stone-600 text-[11px]">
                          <tr>
                            <th className="px-2 py-1.5 text-right">שם</th>
                            <th className="px-2 py-1.5 text-right">טלפון</th>
                            <th className="px-2 py-1.5 text-right">סכום</th>
                            <th className="px-2 py-1.5 text-right">מס׳ עסקה</th>
                            <th className="px-2 py-1.5 text-right">חשבונית</th>
                            <th className="px-2 py-1.5 text-right">תאריך</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(cardcomReport.unmatched_cardcom || []).map(t => (
                            <tr key={t.transaction_id} className="border-t border-stone-100 hover:bg-red-50/50">
                              <td className="px-2 py-1.5 font-medium">{t.customer_name || '—'}</td>
                              <td className="px-2 py-1.5"><PhoneCell phone={t.customer_phone} name={t.customer_name} /></td>
                              <td className="px-2 py-1.5 font-bold">₪{Number(t.amount).toLocaleString()}</td>
                              <td className="px-2 py-1.5 text-stone-600 font-mono text-[11px]">{t.transaction_id || '—'}</td>
                              <td className="px-2 py-1.5 text-stone-600">#{t.document_number || '—'}</td>
                              <td className="px-2 py-1.5 text-[10px] text-stone-500">{t.date ? new Date(t.date).toLocaleDateString('he-IL') : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
              );
            })()}

            {/* Footer */}
            <div className="px-6 py-3 bg-stone-50 border-t border-stone-200 flex items-center justify-between">
              <div className="text-xs text-stone-500">
                חלון חיפוש: {cardcomReport.summary?.window_from ? new Date(cardcomReport.summary.window_from).toLocaleDateString('he-IL') : '—'}
                {' '}עד{' '}
                {cardcomReport.summary?.window_to ? new Date(cardcomReport.summary.window_to).toLocaleDateString('he-IL') : '—'}
              </div>
              <button onClick={() => setCardcomReport(null)} className="px-4 py-1.5 bg-stone-700 hover:bg-stone-800 text-white text-sm rounded-lg font-bold" data-testid="cardcom-report-done">
                סגור
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
        )}
      </div>
    </div>
  );
};

// Inline component: clock button + active schedule indicator next to budget
const BudgetScheduleControl = ({ targetId, level, name, currentBudget, schedules, onOpen, onCancel }) => {
  const active = (schedules || []).find(s => s.target_id === targetId && s.status === 'pending');
  if (active) {
    const dt = new Date(active.scheduled_at);
    const label = dt.toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    return (
      <div className="flex items-center gap-1 bg-indigo-500/30 border border-indigo-300/40 rounded-lg px-2 py-1 text-xs text-indigo-100" data-testid={`budget-sched-active-${targetId}`}>
        <Clock className="w-3 h-3" />
        <span className="font-bold">{label} → ₪{active.new_budget}</span>
        <button onClick={() => onCancel(active.id)} className="text-red-300 hover:text-red-100 mr-1" title="בטל תזמון" data-testid={`budget-sched-cancel-${targetId}`}>
          <XCircle className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  return (
    <button
      onClick={() => onOpen(targetId, level, name, currentBudget)}
      className="bg-indigo-500/20 hover:bg-indigo-500/30 border border-indigo-300/30 rounded-lg p-1.5 text-indigo-200 hover:text-indigo-100 transition-colors"
      title="תזמן שינוי תקציב"
      data-testid={`budget-sched-open-${targetId}`}
    >
      <Clock className="w-3.5 h-3.5" />
    </button>
  );
};

export default Admin;

// ── CashReportUploader ─────────────────────────────────────────────────────
// Updated May 2026: now opens a picker (Upload / Paste) first; the parse
// response is shown in a modal where each matched order is "בטיפול".
// The DB commit only happens when the user clicks "השלם את כולם".
// Global Ctrl+V paste still works on the dashboard as a shortcut.
const CashReportUploader = ({ onMatched }) => {
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [showSidebar, setShowSidebar] = React.useState(false);
  const [history, setHistory] = React.useState([]);
  const [pasteHint, setPasteHint] = React.useState(false);
  const [showPicker, setShowPicker] = React.useState(false);
  const [pasteMode, setPasteMode] = React.useState(false); // waiting-for-paste UI
  const [duplicatePrompt, setDuplicatePrompt] = React.useState(null); // {existing, file, filename}

  // Fetch history on mount + after every successful upload so the sidebar
  // counter (Documents tab) shows a live total.
  const fetchHistory = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/cash-reports`);
      setHistory(Array.isArray(r.data) ? r.data : []);
    } catch (_e) { /* graceful */ }
  }, []);
  React.useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const openPicker = () => setShowPicker(true);
  const triggerFile = () => { setShowPicker(false); fileRef.current?.click(); };
  const triggerPaste = () => { setShowPicker(false); setPasteMode(true); };

  const sendFile = async (file, filename, force = false) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('הקובץ גדול מדי (מקסימום 10MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      // Filename matters for the history label — for pastes we synthesise
      // "הדבקה DD/MM HH:MM" so the user can tell two clipboards apart.
      const fname = filename || `הדבקה ${new Date().toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}.png`;
      fd.append('file', file, fname);
      const r = await axios.post(`${API}/admin/cash-report/parse-match${force ? '?force=1' : ''}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      // Duplicate detected — ask the user whether to re-scan.
      if (r.data?.duplicate) {
        setDuplicatePrompt({ existing: r.data.existing_report, file, filename: fname });
        return;
      }
      setResult(r.data);
      await fetchHistory();
      // No DB change yet — onMatched only fires after user confirms commit.
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`שגיאה בעיבוד: ${msg}`);
    } finally {
      setUploading(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) await sendFile(f, f.name);
  };

  // Global paste handler — listens on the whole window so the user can
  // paste an image (Ctrl+V / Cmd+V) anywhere on the dashboard without
  // first focusing the upload button. We only intercept paste events that
  // actually contain an image (typing text into an input still works).
  React.useEffect(() => {
    const onPaste = async (ev) => {
      if (uploading) return;
      const items = ev.clipboardData?.items;
      if (!items) return;
      for (const it of items) {
        if (it.kind === 'file' && it.type.startsWith('image/')) {
          const blob = it.getAsFile();
          if (!blob) continue;
          ev.preventDefault();
          setPasteMode(false);
          setPasteHint(true);
          setTimeout(() => setPasteHint(false), 1500);
          await sendFile(blob);
          return;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading]);

  return (
    <>
      <button
        onClick={openPicker}
        disabled={uploading}
        className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg text-xs font-bold transition-colors mr-2 whitespace-nowrap"
        data-testid="cash-report-upload-btn"
        title="פתח תפריט: העלאה או הדבקה"
      >
        {uploading ? (
          <>
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" /></svg>
            מפענח...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 13h6m-3-3v6M5 21h14a2 2 0 002-2V7l-5-5H5a2 2 0 00-2 2v15a2 2 0 002 2z" /></svg>
            מזומן שהתקבל
          </>
        )}
      </button>
      <button
        onClick={() => setShowSidebar(true)}
        className="flex items-center gap-1.5 px-3 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-lg text-xs font-bold transition-colors mr-2 whitespace-nowrap"
        data-testid="cash-report-history-btn"
        title="היסטוריית מסמכים שהועלו"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
        מסמכים
        {history.length > 0 && (
          <span className="bg-emerald-500 text-white rounded-full text-[10px] px-1.5 py-0.5 mr-0.5">{history.length}</span>
        )}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={onFile}
        className="hidden"
        data-testid="cash-report-file-input"
      />
      {pasteHint && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[210] bg-emerald-600 text-white px-4 py-2 rounded-xl shadow-lg text-sm font-bold animate-pulse" data-testid="paste-hint-toast">
          📋 הודבק - מפענח...
        </div>
      )}
      {showPicker && (
        <div className="fixed inset-0 z-[205] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowPicker(false)} data-testid="cash-report-picker">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()} style={{ direction: 'rtl' }}>
            <header className="bg-gradient-to-l from-emerald-600 to-emerald-700 text-white px-5 py-4 flex items-center justify-between">
              <h2 className="text-lg font-black">דוח גוביינות</h2>
              <button onClick={() => setShowPicker(false)} className="text-white hover:bg-white/20 rounded-lg p-1.5" data-testid="cash-report-picker-close">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </header>
            <div className="p-5 space-y-3">
              <p className="text-sm text-stone-600 mb-2">בחר איך להעלות את הדוח:</p>
              <button
                type="button"
                onClick={triggerFile}
                className="w-full flex items-center gap-3 p-4 border-2 border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 rounded-xl transition-colors text-right"
                data-testid="cash-report-picker-upload"
              >
                <div className="bg-emerald-100 rounded-xl p-2.5">
                  <svg className="w-6 h-6 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                </div>
                <div className="flex-1">
                  <p className="font-black text-stone-800">העלאת קובץ</p>
                  <p className="text-[11px] text-stone-500">JPG / PNG / PDF — מהמחשב</p>
                </div>
              </button>
              <button
                type="button"
                onClick={triggerPaste}
                className="w-full flex items-center gap-3 p-4 border-2 border-stone-200 hover:border-indigo-500 hover:bg-indigo-50 rounded-xl transition-colors text-right"
                data-testid="cash-report-picker-paste"
              >
                <div className="bg-indigo-100 rounded-xl p-2.5">
                  <svg className="w-6 h-6 text-indigo-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                </div>
                <div className="flex-1">
                  <p className="font-black text-stone-800">הדבקה מהלוח</p>
                  <p className="text-[11px] text-stone-500">Ctrl+V — מתמונה שצולמה / צילום מסך</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
      {pasteMode && (
        <div className="fixed inset-0 z-[207] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPasteMode(false)} data-testid="cash-report-paste-overlay">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden text-center p-8" onClick={(e) => e.stopPropagation()} style={{ direction: 'rtl' }}>
            <div className="text-5xl mb-3">📋</div>
            <h3 className="text-xl font-black text-stone-800 mb-2">ממתין להדבקה</h3>
            <p className="text-sm text-stone-500 mb-4">לחץ עכשיו <kbd className="px-2 py-1 bg-stone-100 border border-stone-300 rounded text-xs font-mono">Ctrl + V</kbd> כדי להדביק תמונה</p>
            <button onClick={() => setPasteMode(false)} className="px-4 py-2 bg-stone-200 hover:bg-stone-300 rounded-xl text-sm font-bold text-stone-700" data-testid="cash-report-paste-cancel">
              ביטול
            </button>
          </div>
        </div>
      )}
      {result && (
        <CashReportModal
          result={result}
          onClose={() => setResult(null)}
          onCommitted={(updated) => {
            setResult(updated);
            fetchHistory();
            if (onMatched) onMatched();
          }}
        />
      )}
      {duplicatePrompt && (
        <div className="fixed inset-0 z-[208] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDuplicatePrompt(null)} data-testid="cash-report-duplicate-prompt">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()} style={{ direction: 'rtl' }}>
            <header className="bg-gradient-to-l from-amber-500 to-amber-600 text-white px-5 py-4 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              <h2 className="text-base font-black">מסמך זה כבר עלה בעבר</h2>
            </header>
            <div className="p-5 space-y-3">
              <p className="text-sm text-stone-700">
                המסמך הזה כבר נסרק וכל הנתונים שלו עודכנו במערכת.
              </p>
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-stone-500">שם:</span><strong className="text-stone-800">{duplicatePrompt.existing.filename || '—'}</strong></div>
                <div className="flex justify-between"><span className="text-stone-500">תאריך העלאה:</span><strong className="text-stone-800" dir="ltr">{new Date(duplicatePrompt.existing.uploaded_at).toLocaleString('he-IL')}</strong></div>
                <div className="flex justify-between"><span className="text-stone-500">התאמות:</span><strong className="text-emerald-700">{duplicatePrompt.existing.matched_count}/{duplicatePrompt.existing.rows_in_sheet}</strong></div>
                <div className="flex justify-between"><span className="text-stone-500">סה״כ נספר:</span><strong className="text-emerald-700">{Number(duplicatePrompt.existing.total_collected || 0).toLocaleString()} ₪</strong></div>
                {duplicatePrompt.existing.committed && (
                  <div className="text-[10px] text-emerald-700 font-bold pt-1 border-t border-stone-200">✓ הזמנות מהדוח כבר הושלמו</div>
                )}
              </div>
              <p className="text-sm text-stone-600">תרצה לסרוק שוב את אותו מסמך?</p>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={async () => {
                    const { file, filename } = duplicatePrompt;
                    setDuplicatePrompt(null);
                    await sendFile(file, filename, true);
                  }}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white font-bold py-2.5 rounded-xl text-sm"
                  data-testid="cash-report-duplicate-rescan"
                >
                  כן, סרוק שוב
                </button>
                <button
                  onClick={async () => {
                    try {
                      const r = await axios.get(`${API}/admin/cash-reports/${duplicatePrompt.existing.id}`);
                      setDuplicatePrompt(null);
                      setResult(r.data);
                    } catch (_e) {
                      toast.error('שגיאה בטעינת הדוח הקיים');
                      setDuplicatePrompt(null);
                    }
                  }}
                  className="flex-1 bg-stone-700 hover:bg-stone-800 text-white font-bold py-2.5 rounded-xl text-sm"
                  data-testid="cash-report-duplicate-open"
                >
                  פתח את הקיים
                </button>
                <button
                  onClick={() => setDuplicatePrompt(null)}
                  className="px-3 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-600 font-bold rounded-xl text-sm"
                  data-testid="cash-report-duplicate-cancel"
                >
                  ביטול
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showSidebar && (
        <CashReportHistorySidebar
          history={history}
          onClose={() => setShowSidebar(false)}
          onOpen={(reportId) => {
            axios.get(`${API}/admin/cash-reports/${reportId}`).then(r => {
              setShowSidebar(false);
              setResult(r.data);
            }).catch(() => toast.error('שגיאה בטעינה'));
          }}
          onDelete={async (reportId) => {
            if (!window.confirm('למחוק את הדוח? פעולה זו לא תבטל את ההזמנות שכבר עודכנו.')) return;
            try {
              await axios.delete(`${API}/admin/cash-reports/${reportId}`);
              await fetchHistory();
              toast.success('הדוח נמחק');
            } catch (_e) { toast.error('שגיאה במחיקה'); }
          }}
        />
      )}
    </>
  );
};

const CashReportHistorySidebar = ({ history, onClose, onOpen, onDelete }) => {
  return (
    <div className="fixed inset-0 z-[180] bg-black/50 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose}>
      <aside
        className="bg-white w-full max-w-md h-full overflow-hidden flex flex-col shadow-2xl animate-in slide-in-from-left"
        onClick={(e) => e.stopPropagation()}
        data-testid="cash-report-history-sidebar"
      >
        <header className="bg-gradient-to-l from-slate-800 to-slate-900 text-white px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-black">מסמכי מזומן שהתקבל</h2>
            <p className="text-stone-300 text-xs mt-0.5">{history.length} מסמכים נשמרו</p>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 rounded-lg p-1.5" data-testid="cash-history-close">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </header>
        {/* Revenue summary banner — sums "total_collected" across every
            saved cash report. These are cash payments received from the
            shipping companies (HFD/Zigzag/Cargo) and are also rolled into
            the main dashboard's "סה״כ הכנסות" via the cashReportRevenue
            state. */}
        {history.length > 0 && (
          <div className="bg-emerald-50 border-b border-emerald-200 px-5 py-3" data-testid="cash-reports-revenue-summary">
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-bold text-emerald-700">סך הכנסות מחברות שילוח</span>
              <span className="text-xl font-black text-emerald-800" data-testid="cash-reports-total">
                ₪{history.reduce((s, h) => s + Number(h.total_collected || 0), 0).toLocaleString()}
              </span>
            </div>
            <p className="text-[10px] text-emerald-600 mt-1">הסכום מצטרף אוטומטית לסה״כ ההכנסות במסך הראשי</p>
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ direction: 'rtl' }}>
          {history.length === 0 ? (
            <p className="text-center text-stone-400 py-10 text-sm">אין מסמכים עדיין. העלה או הדבק (Ctrl+V) דוח גוביינות.</p>
          ) : history.map(h => (
            <div
              key={h.id}
              className="border-2 border-stone-200 hover:border-emerald-400 rounded-xl p-3 cursor-pointer transition-colors group"
              onClick={() => onOpen(h.id)}
              data-testid={`cash-report-history-item-${h.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <p className="font-bold text-stone-800 text-sm truncate flex-1">{h.filename || 'דוח'}</p>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(h.id); }}
                  className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-600 transition-opacity"
                  title="מחק"
                  data-testid={`cash-history-delete-${h.id}`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
                </button>
              </div>
              <p className="text-[10px] text-stone-500 mb-2" dir="ltr">{new Date(h.uploaded_at).toLocaleString('he-IL')}</p>
              <div className="grid grid-cols-3 gap-1 text-center text-[10px]">
                <div className="bg-emerald-50 rounded p-1">
                  <p className="font-black text-emerald-700">{h.matched_count}/{h.rows_in_sheet}</p>
                  <p className="text-emerald-700">התאמות</p>
                </div>
                <div className="bg-amber-50 rounded p-1">
                  <p className="font-black text-amber-700">{Number(h.total_collected || 0).toLocaleString()}</p>
                  <p className="text-amber-700">נספר ₪</p>
                </div>
                <div className="bg-stone-50 rounded p-1">
                  <p className="font-black text-stone-700">{Number(h.total_amount_in_sheet || 0).toLocaleString()}</p>
                  <p className="text-stone-600">סה״כ ₪</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
};

const CashReportModal = ({ result, onClose, onCommitted }) => {
  const { matched = [], unmatched = [], already_settled = [], rows_in_sheet = 0, matched_count = 0,
    already_settled_count = (result.already_settled || []).length,
    total_amount_in_sheet = 0, total_collected = 0, image_base64, mime,
    report_id, committed: alreadyCommitted = false } = result;

  // Track which orders are committed locally (for instant UI update after
  // the user clicks "השלם את כולם"). Initial state mirrors what the
  // server returned — re-opening a committed report keeps the checkmarks.
  const initialCommitted = React.useMemo(() => {
    const s = new Set();
    for (const m of matched) {
      if (m.committed || m.new_status === 'completed') s.add(m.order_id);
    }
    return s;
  }, [matched]);
  const [committedSet, setCommittedSet] = React.useState(initialCommitted);
  const [committing, setCommitting] = React.useState(false);

  const pendingIds = matched.filter(m => !committedSet.has(m.order_id)).map(m => m.order_id);
  const allCommitted = matched.length > 0 && pendingIds.length === 0;

  const commitAll = async () => {
    if (pendingIds.length === 0 || committing) return;
    setCommitting(true);
    try {
      const r = await axios.post(`${API}/admin/cash-report/commit`, {
        report_id: report_id || null,
        order_ids: pendingIds,
      });
      const newSet = new Set(committedSet);
      (r.data.committed_ids || []).forEach(id => newSet.add(id));
      setCommittedSet(newSet);
      toast.success(`עודכנו ${r.data.committed_count || 0} הזמנות כ-הושלמו`);
      // Bubble up the updated result so the dashboard re-fetches orders
      // and the modal stays open showing the green checkmarks.
      const updatedMatched = matched.map(m => newSet.has(m.order_id)
        ? { ...m, new_status: 'completed', committed: true }
        : m);
      if (onCommitted) onCommitted({ ...result, matched: updatedMatched, committed: newSet.size === matched.length });
    } catch (err) {
      toast.error('שגיאה בעדכון: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="cash-report-modal">
        <header className="bg-gradient-to-l from-emerald-600 to-emerald-700 text-white px-5 py-4 rounded-t-2xl flex items-center justify-between sticky top-0">
          <div>
            <h2 className="text-lg font-black">תוצאת זיהוי דוח גוביינות</h2>
            <p className="text-emerald-100 text-xs mt-0.5">
              נמצאו {matched_count} משלוחים חדשים מתוך {rows_in_sheet}
              {already_settled_count > 0 && ` · ${already_settled_count} כבר שולמו`}
              {allCommitted ? ' · הושלמו' : (matched_count > 0 ? ' · ממתינים לאישור' : '')}
            </p>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 rounded-lg p-1.5" data-testid="cash-report-modal-close">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </header>
        <div className="p-5 space-y-4" style={{ direction: 'rtl' }}>
          {image_base64 && (
            <details className="border border-stone-200 rounded-xl bg-stone-50">
              <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-stone-700 hover:bg-stone-100 rounded-xl">📎 הצג מסמך מקור</summary>
              <div className="p-3 pt-0">
                <img
                  src={`data:${mime || 'image/jpeg'};base64,${image_base64}`}
                  alt="מסמך מקור"
                  className="w-full max-h-[400px] object-contain rounded-lg border border-stone-200"
                  data-testid="cash-report-source-image"
                />
              </div>
            </details>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
              <p className="text-2xl font-black text-emerald-700">{matched_count}/{rows_in_sheet}</p>
              <p className="text-[11px] font-bold text-emerald-800 mt-0.5">משלוחים חדשים</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
              <p className="text-2xl font-black text-amber-700">{total_collected.toLocaleString()} ₪</p>
              <p className="text-[11px] font-bold text-amber-800 mt-0.5">סה״כ לעדכון</p>
              {already_settled_count > 0 && (
                <p className="text-[9px] text-blue-700 mt-0.5">לא כולל {already_settled_count} ששולמו</p>
              )}
            </div>
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 text-center">
              <p className="text-2xl font-black text-stone-700">{total_amount_in_sheet.toLocaleString()} ₪</p>
              <p className="text-[11px] font-bold text-stone-600 mt-0.5">סה״כ במסמך</p>
            </div>
          </div>
          {matched.length > 0 && (
            <div className={`rounded-xl border-2 overflow-hidden ${allCommitted ? 'border-emerald-300' : 'border-amber-300'}`}>
              <div className={`px-3 py-2 text-sm font-black flex items-center justify-between ${allCommitted ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                <span>
                  {allCommitted ? '✅ הושלמו' : '⏳ בטיפול'} ({matched.length})
                </span>
                {!allCommitted && (
                  <button
                    type="button"
                    onClick={commitAll}
                    disabled={committing || pendingIds.length === 0}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg text-xs font-bold flex items-center gap-1.5"
                    data-testid="cash-report-commit-all"
                  >
                    {committing ? (
                      <>
                        <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" /></svg>
                        מעדכן...
                      </>
                    ) : (
                      <>✓ השלם את כולם ({pendingIds.length})</>
                    )}
                  </button>
                )}
              </div>
              <div className="divide-y divide-stone-100 max-h-[260px] overflow-y-auto">
                {matched.map((m) => {
                  const isDone = committedSet.has(m.order_id);
                  return (
                    <div key={m.order_id} className={`px-3 py-2 flex items-center justify-between text-sm ${isDone ? 'bg-emerald-50' : 'bg-amber-50/40'}`} data-testid={`cash-match-row-${m.order_id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-stone-800 truncate">{m.customer_name || '—'}</p>
                        <p className="text-[11px] text-stone-500" dir="ltr">{m.customer_phone}{m.shipment_number ? ` · HFD #${m.shipment_number}` : ''}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${isDone ? 'bg-emerald-200 text-emerald-800' : 'bg-amber-200 text-amber-800'}`} data-testid={`cash-match-status-${m.order_id}`}>
                          {isDone ? 'הושלם' : 'בטיפול'}
                        </span>
                        <p className={`font-black mr-1 whitespace-nowrap ${isDone ? 'text-emerald-700' : 'text-amber-700'}`}>{Number(m.amount || 0).toLocaleString()} ₪</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {already_settled.length > 0 && (
            <div className="rounded-xl border-2 border-blue-200 overflow-hidden">
              <div className="bg-blue-100 px-3 py-2 text-sm font-black text-blue-800">
                ✓ כבר שולם — לא נכלל בסה"כ ({already_settled.length})
              </div>
              <div className="divide-y divide-blue-100 max-h-[200px] overflow-y-auto">
                {already_settled.map((m) => (
                  <div key={`as-${m.order_id}`} className="px-3 py-2 flex items-center justify-between text-sm bg-blue-50/40" data-testid={`cash-already-settled-row-${m.order_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-stone-800 truncate">{m.customer_name || '—'}</p>
                      <p className="text-[11px] text-stone-500" dir="ltr">{m.customer_phone}{m.shipment_number ? ` · HFD #${m.shipment_number}` : ''}</p>
                      <p className="text-[10px] text-blue-600 mt-0.5">ההזמנה כבר עודכנה לתשלום במערכת</p>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-blue-200 text-blue-800">הושלם</span>
                      <p className="font-black text-blue-700 mr-1 whitespace-nowrap">{Number(m.amount || 0).toLocaleString()} ₪</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {unmatched.length > 0 && (
            <div className="rounded-xl border-2 border-amber-200 overflow-hidden">
              <div className="bg-amber-100 px-3 py-2 text-sm font-black text-amber-800">⚠️ לא נמצאו התאמה ({unmatched.length})</div>
              <div className="divide-y divide-amber-100 max-h-[200px] overflow-y-auto">
                {unmatched.map((m, idx) => (
                  <div key={`um-${idx}`} className="px-3 py-2 text-sm">
                    <div className="flex items-center justify-between">
                      <p className="font-bold text-stone-800 truncate">{m.customer_name || '—'}</p>
                      <p className="font-black text-amber-700 mr-2 whitespace-nowrap">{Number(m.amount || 0).toLocaleString()} ₪</p>
                    </div>
                    <p className="text-[11px] text-stone-500" dir="ltr">{m.phone_normalized || m.phone}</p>
                    <p className="text-[10px] text-amber-700 mt-0.5">{m.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          <button
            onClick={onClose}
            className="w-full bg-stone-800 hover:bg-stone-900 text-white font-bold py-2.5 rounded-xl transition-colors"
            data-testid="cash-report-modal-ok"
          >
            סגור
          </button>
        </div>
      </div>
    </div>
  );
};
